import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useSharedReportDateFilter } from '../../redux/reportFilters/reportFiltersHooks';
import { fetchSnapshotDetailsRequest, fetchLivePaymentsRequest, fetchLiveRefundSummaryRequest, fetchHourlySalesRequest, fetchLiveByChannelRequest, fetchThirdPartyByChannelRequest, fetchLiveStaffPerformanceRequest, fetchOrderLogRequest, exportOrderLogRequest, fetchOrderDetailRequest, clearOrderDetail, fetchLiveCategorySalesRequest, fetchLiveStaffRefundsRequest, fetchSnapshotComparisonRequest, fetchChannelNamesRequest, fetchLiveItemSalesV2Request, fetchLiveOutstandingValueRequest, fetchLiveVoidSummaryRequest, fetchLiveCompOrderLogRequest } from '../../redux/reports/clickHouse/clickHouseActions';
import { getLiveCategorySalesApi, getSnapshotDetails, getLiveByChannelApi, getLivePaymentsApi, getLiveRefundSummaryApi, getLiveOutstandingValueApi, getOrderLogExportApi, getLiveStaffPerformanceApi } from '../../redux/reports/clickHouse/clickHouseApi';
import type { LiveByChannelItem, LiveCategorySalesItem, LiveHourlySalesItem, HourlySalesResponse, LiveItemSalesV2Item, LiveOutstandingValueResponse, LivePaymentsItem, LiveRefundSummaryItem, LiveStaffPerformanceItem, LiveStaffRefundsItem, OrderChannelGroup, OrderDetailResponse, OrderLogItem, SnapshotDetailsResponse, SnapshotComparisonResponse } from '../../interface/clickHouseInterface';
import { channelGroupLabel } from '../../interface/clickHouseInterface';
import HourlyBarChart from './components/charts/reusable/HourlyBarChart';
import { TipsGratuityDrill, buildTipsGratuityData, TG_CARD_LABEL, TG_DRILL_TITLE } from './components/TipsGratuity';
import DoughnutChart, { computeDisplayPcts } from './components/charts/reusable/DoughnutChart';
import type { DoughnutSlice } from './components/charts/reusable/DoughnutChart';
import SalesWaterfall, { type WaterfallSource, buildWaterfallData, getTotalPaidValue, getTotalPaidLabel, getTotalPaidSub } from './components/SalesWaterfall';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { getCurrencySymbol, formatGroupedNumber, formatMoneyForExport } from '../../utils/currency';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';
import DesktopLayout from '../layout/DesktopLayout';
import TopBar from '../layout/TopBar';
import Phone from '../Phone';
import BottomNav from '../BottomNav';
import KPI from '../KPI';
import KpiTooltip from '../KpiTooltip';
import StatKPI from '../StatKPI';
import Arrow from '../Arrow';
import Icon from '../Icon';
import { C } from '../theme';
import { getInitialReportDateFilter, isHistoricalReportDateFilter } from '../../utils/reportDateFilter';
import './reports.css';
import { T, useIsDesktop, Bone } from './reportTokens';
import SectionError from './SectionError';
import SectionEmpty from './SectionEmpty';
import { ReportExportMenu, ReportMobileExportBtn, ReportSectionExportButton } from './components/ReportExportButton';
import { ExportFormat, formatMonetaryExport, type ExportCell } from '../../utils/reportExport';
import DiscountsDrilldown, { DiscountBreakdownAndLog } from './components/DiscountsDrilldown';
import { getDiscountSourceBreakdown, DISCOUNT_SOURCES, type DiscountSourceKey } from './loyaltyRedemption';
import { orderLogToExportTable } from '../../utils/exportUtils';
import SalesByChannelBreakdown, { type ChannelRow as SbcChannelRow } from './SalesByChannelBreakdown';
import { useSalesByChannelBreakdown } from '../../hooks/useSalesByChannelBreakdown';
import { OrderDetailSlider } from './components/OrderDetailSlider';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { useDrillHistoryBack } from '../../hooks/useDrillHistoryBack';
import {
    getDiscountSummary,
    getStaffSales,
    getStaffTipsPerformance,
    getPaymentDetails,
    getLiveBilledOrders,
    getLiveNetSales,
    getLiveItemSales,
    getOrderLog,
    getOrderDetail,
    todayStr,
    yesterdayStr,
    isTodayRange,
} from '../../services/reportsApi';
import { useStoreTimeZone, getStoreTimeZone } from '../../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken, comparisonTooltip, comparisonRangeFor, hasComparison, exportPeriodFor } from '../../utils/reportPeriod';

// =========================
// Desktop implementation
// =========================

// Desktop shimmer — real labels visible, only values shimmer
const DesktopShimmer = ({ isToday = false }: { isToday?: boolean }) => {
    const kpiRow1 = ['Total Collected', 'Net Sales', 'Total Orders', 'Avg Order Value'];
    const kpiRow2 = ['Total Tax', 'Total Discounts', 'Refunds', 'Tips & Grat'];
    const kpiRow3 = ['Other', 'Outstanding Amount'];
    return (
        <div className="rpt-page">
            {/* KPI row 1 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                {kpiRow1.map(label => (
                    <div key={label} className="rpt-tile rpt-card" style={{ flex: 1 }}>
                        <div className="rpt-tile__label" style={{ marginBottom: 10 }}>{label}</div>
                        <Bone w={90} h={28} mb={8} />
                        <Bone w={50} h={10} />
                    </div>
                ))}
            </div>
            {/* KPI row 2 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                {kpiRow2.map(label => (
                    <div key={label} className="rpt-card" style={{ flex: 1, padding: '16px 18px' }}>
                        <div style={{ fontSize: 11, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
                        <Bone w={65} h={22} mb={6} />
                        <Bone w={40} h={10} />
                    </div>
                ))}
            </div>
            {/* KPI row 3 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                {kpiRow3.map(label => (
                    <div key={label} className="rpt-card" style={{ flex: 1, padding: '16px 18px' }}>
                        <div style={{ fontSize: 11, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
                        <Bone w={65} h={22} mb={6} />
                        <Bone w={40} h={10} />
                    </div>
                ))}
            </div>
            {/* Charts / category / third-party rows — hidden for Today */}
            {!isToday && (
                <>
                    {/* Charts row */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                        <div className="rpt-card rpt-card--r16" style={{ flex: 2 }}>
                            <div className="rpt-section-title">Sales by Hour</div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <Bone key={i} w={null} h={20 + ((i * 37 + 17) % 90)} r={4} style={{ flex: 1 }} />
                                ))}
                            </div>
                        </div>
                        <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                            <div className="rpt-section-title">Sales by Channel</div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                                <Bone w={120} h={120} r={60} />
                            </div>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <Bone w={80} h={12} />
                                    <Bone w={55} h={12} />
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Category + Refund row */}
                    <div className="rpt-flex-row rpt-gap-16" style={{ marginBottom: 24 }}>
                        {['Sales by Category', 'Refund Analysis'].map(title => (
                            <div key={title} className="rpt-card" style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, marginBottom: 14 }}>{title}</div>
                                {[1, 2, 3, 4, 5].map(j => (
                                    <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <Bone w={90} h={11} />
                                        <Bone w={55} h={11} />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                    {/* Third-Party Marketplace */}
                    <div className="rpt-card">
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, marginBottom: 14 }}>Third-Party Marketplace Performance</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ padding: '16px 18px', borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.bg }}>
                                    <Bone w={80} h={11} mb={8} />
                                    <Bone w={90} h={22} mb={10} />
                                    <Bone w={50} h={10} />
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Mobile shimmer — real labels visible, only values shimmer
const MobileShimmer = ({ isToday = false }: { isToday?: boolean }) => {
    const kpiRows = [
        ['Net Sales', 'Orders', 'Avg Order'],
        ['Tax', 'Discounts', 'Refunds'],
        ['Tips & Grat', 'Other', 'Outstanding'],
    ];
    return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
            {/* Total Collected hero */}
            <div style={{
                background: `linear-gradient(135deg, ${C.accent}0D, ${C.purple ?? '#7c3aed'}0A)`,
                border: `1px solid ${C.accent}22`,
                borderRadius: 16, padding: '16px 18px', marginBottom: 10,
                position: 'relative', overflow: 'hidden', boxShadow: C.shadow,
            }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Total Collected</div>
                <Bone w={180} h={30} mb={6} />
                <Bone w={110} h={10} />
            </div>
            {/* KPI grid rows */}
            {kpiRows.map((row, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: ri === kpiRows.length - 1 ? 12 : 8 }}>
                    {row.map(label => (
                        <div key={label} style={{ background: C.card, borderRadius: 12, padding: '12px 10px', border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }}>
                            <div style={{ fontSize: 8, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                            <Bone w={55} h={20} />
                        </div>
                    ))}
                </div>
            ))}
            {/* Hourly Sales / Channel Split / Third-Party — hidden for Today */}
            {!isToday && (
                <>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Hourly Sales</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 74 }}>
                            {Array.from({ length: 14 }).map((_, i) => (
                                <Bone key={i} w={null} h={10 + ((i * 23 + 11) % 58)} r={3} style={{ flex: 1 }} />
                            ))}
                        </div>
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Channel Split</div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                <Bone w={70} h={11} />
                                <Bone w={55} h={11} />
                            </div>
                        ))}
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Third-Party Marketplace</div>
                        {[1, 2, 3].map(i => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                                <Bone w={80} h={12} />
                                <Bone w={60} h={12} />
                            </div>
                        ))}
                    </div>
                </>
            )}
            {/* Staff Sales Leaderboard — always shown */}
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Staff Sales Leaderboard</div>
                {[1, 2, 3].map(i => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 18, height: 18, borderRadius: 5, background: i === 1 ? C.accentSoft : C.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: i === 1 ? C.accent : C.t3 }}>{i}</span>
                            <Bone w={80} h={11} />
                        </div>
                        <Bone w={55} h={11} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const findToken = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'bearerToken', 'authToken', 'userToken', 'sessionToken', 'Authorization', 'authorization', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrapRes = (res: any) => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const pick = (res: any, ...keys: any[]) => {
    if (res == null) return null;
    for (const key of keys) {
        const v = res?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const r = unwrapRes(res);
    for (const key of keys) {
        const v = r?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const raw = Array.isArray(r) ? r : (r?.content ?? r?.data ?? r);
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    for (const key of keys) {
        const v = obj[key];
        if (v !== undefined && v !== null) return v;
    }
    for (const [k, v] of Object.entries(obj)) {
        if (keys.some((key) => String(k).toLowerCase().includes(String(key).toLowerCase())) && (typeof v === 'number' || (typeof v === 'string' && /^[\d.,]+$/.test(v)))) return v;
    }
    return null;
};

const listFrom = (res: any) => {
    const r = unwrapRes(res);
    let raw = r?.content ?? r?.data ?? r?.items ?? r?.records ?? r?.categories ?? r?.channelList ?? r?.employees ?? r?.staff ?? r?.staffList ?? r?.staffTips ?? r?.tipDetails ?? r?.discounts ?? r?.discountList ?? r?.offers ?? r?.refunds ?? r?.refundList ?? r?.orders ?? r?.orderList ?? (Array.isArray(r) ? r : null);
    if (!Array.isArray(raw) && r && typeof r === 'object') {
        for (const val of Object.values(r)) {
            if (Array.isArray(val)) {
                raw = val;
                break;
            }
        }
    }
    return Array.isArray(raw) ? raw : [];
};

const sumFromList = (res: any, key: any) => listFrom(res).reduce((sum: any, item: any) => sum + (Number(item?.[key] ?? item?.amount ?? item?.value ?? item?.total ?? 0) || 0), 0);

const num = (v: any) => Number(v ?? 0) || 0;
const numPct = (v: any) => v == null ? null : Number(v);
// Sales by Category — "All" page-size sends one large size so the backend returns
// every category in a single page (no client-side paging). Also used by the
// fetch-all that drives the running total + export.
const CAT_ALL_SIZE = 100000;
// Comp-order-log page size for the Net Sales drill waterfall's Complimentary row
// (mirrors Snapshot's COMP_PAGE_SIZE so the value matches the Complimentary stamp KPI).
const WF_COMP_PAGE_SIZE = 10;
// Records-per-page options for the Sales by Category card (ZAR-1223).
const CAT_PAGE_SIZE_OPTIONS: (number | 'All')[] = [5, 10, 20, 'All'];
// SSE-19: quantity cells in the Sales by Category export — integers stay whole,
// fractional values show 2–3 decimals (42 → "42", 42.2 → "42.20", 42.201 → "42.201").
// Returned as a string so the CSV escaper's force-text guard keeps the trailing zero.
const fmtExportQty = (n: number | null | undefined): string => {
    const v = Number(n) || 0;
    if (Number.isInteger(v)) return String(v);
    const decimals = (String(v).split('.')[1] || '').length;
    return v.toFixed(Math.min(Math.max(decimals, 2), 3));
};
const fmtCurrency = (v: any) => `${getCurrencySymbol()}${num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Table-cell amount: whole numbers drop decimals ($27), fractional keep 2 ($27.50).
// Matches the export formatting rule for monetary table columns.
const fmtCurrencyCell = (v: any) => {
    const n = num(v);
    return Number.isInteger(n)
        ? `${getCurrencySymbol()}${n.toLocaleString('en-US')}`
        : `${getCurrencySymbol()}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Stamp-KPI amount: abbreviates at ≥100K (same thresholds as fmtCurrencyMobile) so long
// values can't overflow the fixed-width KPI cards (SSE-11). Exact amounts remain
// available in the drill-downs.
const fmtCurrencyKpi = (v: any) => {
    const n = num(v);
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${getCurrencySymbol()}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${getCurrencySymbol()}${(n / 1_000).toFixed(1)}K`;
    return fmtCurrency(n);
};
const fmtCompact = (v: any) => {
    const n = num(v);
    const abs = Math.abs(n);
    const sym = getCurrencySymbol();
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtNum = (v: any) => num(v).toLocaleString('en-US');

/**
 * Hourly bar labels are stored compact ("5a" / "5p") so the chart's x-axis can stay
 * legible when 24 bars are cramped. KPI cards have room for the full meridiem, so
 * expand "5a" → "5 AM" and "5p" → "5 PM" before displaying them there.
 */
const expandHourLabel = (lbl: string) =>
    (lbl ?? '').replace(/(\d+)a$/, '$1 AM').replace(/(\d+)p$/, '$1 PM');

/** Distribute integer percentages so they always sum to exactly 100. */
const distribute100 = (values: number[]): number[] => {
    const total = values.reduce((s, v) => s + v, 0);
    if (total === 0) return values.map(() => 0);
    const rawPcts = values.map((v) => (v / total) * 100);
    const floored = rawPcts.map((p) => Math.floor(p));
    let remainder = 100 - floored.reduce((s, v) => s + v, 0);
    const indices = rawPcts
        .map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (const { i } of indices) {
        if (remainder <= 0) break;
        floored[i] += 1;
        remainder -= 1;
    }
    return floored;
};

/** Distribute 2-decimal percentages so they always sum to exactly 100.00.
 *  Uses the largest-remainder method on hundredths of a percent. */
const distribute100At2dp = (values: number[]): string[] => {
    const total = values.reduce((s, v) => s + (Number(v) || 0), 0);
    if (total === 0) return values.map(() => '0.00');
    const scaled = values.map((v) => ((Number(v) || 0) / total) * 10000); // hundredths of a percent
    const floored = scaled.map((p) => Math.floor(p));
    let remainder = 10000 - floored.reduce((s, v) => s + v, 0);
    const indices = scaled
        .map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (const { i } of indices) {
        if (remainder <= 0) break;
        floored[i] += 1;
        remainder -= 1;
    }
    return floored.map((n) => (n / 100).toFixed(2));
};

const pctChange = (curr: any, prev: any) => {
    const c = Number(curr);
    const p = Number(prev);
    if (p === 0 || !Number.isFinite(c) || !Number.isFinite(p)) return null;
    return ((c - p) / p) * 100;
};

const fmtPct = (v: any) => (v == null ? '--' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`);

// Delta badge: arrow + signed % only. For exactly-zero deltas, no +/- sign and no up/down
// arrow are shown (neutral color).
// invert: for "lower is better" metrics (e.g. Refunds) — color/arrow flip (increase = red ↓),
// but the displayed sign always matches the actual value.
//
// GLOBAL-DP-4 (ZAR-1663): the trailing "vs Yesterday" text is gone. The comparison basis is
// stated once in the unified date control instead of being repeated on every card. `comparedTo`
// carries the resolved comparison period into the hover tooltip so the basis is still
// recoverable per card.
const DeltaBadge = ({ pct, theme, invert, comparedTo }: { pct: number; theme: any; invert?: boolean; comparedTo?: string }) => {
    const isZero = pct === 0;
    const isGood = invert ? pct < 0 : pct >= 0;
    return (
        <div title={comparedTo} style={{ fontSize: 12, fontWeight: 600, color: isZero ? theme.t2 : isGood ? theme.green : theme.red, display: 'flex', alignItems: 'center', gap: 3 }}>
            {!isZero && <span>{isGood ? '↑' : '↓'}</span>}{isZero ? `${pct.toFixed(2)}%` : fmtPct(pct)}
        </div>
    );
};

// Stamp-KPI export filename: encodes the applied date range (or single selected
// date, DD-MM-YYYY) and channel filter so downloaded files are distinguishable, e.g.
// "sales-kpis-08-07-2026-overall" or "sales-kpis-01-07-2026_to_08-07-2026-direct".
const fmtFilenameDate = (isoDate: string) => {
    const [y, m, d] = isoDate.split('-');
    return `${d}-${m}-${y}`;
};
const kpiExportFilename = (base: string, startDate: string, endDate: string, orderSource?: string) => {
    const dateSeg = startDate === endDate ? fmtFilenameDate(startDate) : `${fmtFilenameDate(startDate)}_to_${fmtFilenameDate(endDate)}`;
    return `${base}-${dateSeg}-${orderSource ?? 'overall'}`;
};

// Stamp-KPI export content: From/To/Channel Filter shown as the first rows of the
// exported table so the applied date range and channel are visible inside the
// document itself, e.g. "8/7/2026".
const fmtExportDate = (iso?: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return `${d}/${m}/${y}`;
};

// The Stamp-KPI export header's date range comes from `exportPeriodFor` (DPFIX-05): the
// resolver's own `selectionLabel`, so the file and the date control word the period the same
// way. Sales' `getFilterLabel` below is NOT this path — it survives for four on-screen
// subtitle lines and already understood `custom:` tokens; see the note on Snapshot's twin,
// which did not and leaked the raw token into the export header.
const getFilterLabel = (filter: any, startDate: any, endDate: any) => {
    if (!filter) return 'Today';
    if (filter === 'Today') return 'Today';
    if (filter === 'Yesterday') return 'Yesterday';
    if (filter === '7D') return 'Last 7 Days';
    if (filter === '30D') return 'Last 30 Days';
    if (filter === 'This Month') return 'This Month';
    const fmt = (d: string) => {
        const dt = new Date(`${d}T12:00:00`);
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    if (filter.startsWith('custom:')) {
        const p = filter.split(':');
        if (p.length === 3) return p[1] === p[2] ? fmt(p[1]) : `${fmt(p[1])} to ${fmt(p[2])}`;
    }
    if (filter === 'Custom' && startDate && endDate) {
        return startDate === endDate ? fmt(startDate) : `${fmt(startDate)} to ${fmt(endDate)}`;
    }
    return filter;
};

// Delegates to the single GLOBAL-DP resolver (utils/reportPeriod) — see the note on the
// twin in SnapshotScreen. Historical presets stay anchored on the store-timezone anchor
// date (5 AM cutoff); see getAnchorDate / DateProdIssue.md.
const getStartEndFromFilter = (filter: any, today: any, yesterday: any, timeZoneCd: string = getStoreTimeZone()) => {
    if (normalizeToken(filter) === 'today') return { startDate: today, endDate: today };
    return resolveTokenRange(filter, timeZoneCd);
};

const getInitials = (name: any) => {
    if (!name || typeof name !== 'string') return '—';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return name.slice(0, 2).toUpperCase();
};

const Chev = ({ s = 10, c = T.t3, r = 0 }: any) => (
    <svg width={s} height={s} viewBox="0 0 10 10" style={{ transform: `rotate(${r}deg)`, transition: 'transform 0.2s', flexShrink: 0 }}>
        <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
);

const DrillArrow = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: 'absolute', top: 6, right: 6, opacity: 0.45 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CHANNEL_COLORS = [T.accent, '#E86B6B', T.orange, T.green, T.t3, T.blue, T.purple];
const CATEGORY_COLORS = [T.accent, T.green, T.orange, T.blue, T.purple, T.teal, '#E86B6B', T.t3];

// Stable color per channel for the live (Today) Sales by Channel doughnut.
// Keeps Dine-In red, Pickup orange, Delivery green, Instore accent — same convention as Snapshot.
const getChannelColor = (channel: string): string => {
    const key = (channel ?? '').toLowerCase().replace(/[\s\-_]+/g, '');
    if (/instore|inhouse/.test(key)) return T.accent;
    if (/dinein/.test(key)) return '#E86B6B';
    if (/pickup/.test(key)) return T.orange;
    if (/delivery/.test(key)) return T.green;
    return T.t3;
};
const OL_PAGE_SIZE_D = 10; // desktop
const OL_PAGE_SIZE_M = 5;  // mobile

const SALES_REFRESH_TS_KEY = 'reportsSalesScreenRefreshedTime';
const formatSalesRefreshedTime = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

// Global sales-channel filter — single control governing the whole report.
// Maps the UI labels to the `orderSource` query param; "Overall sales" sends nothing.
const CHANNEL_OPTIONS = ['Overall sales', 'Direct sales', '3rd party sales'];
const channelToOrderSource = (c: string): 'direct' | 'thirdparty' | undefined =>
    c === 'Direct sales' ? 'direct' : c === '3rd party sales' ? 'thirdparty' : undefined;

const DesktopSalesScreen = () => {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);
    const dispatch = useAppDispatch();
    const snapshotDetails = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess) as SnapshotDetailsResponse | null;
    console.log('[SalesScreen][Desktop] /sales/live/v2/snapshot-details', snapshotDetails);
    const livePayments = useAppSelector((s) => s.clickHouse.livePaymentsSuccess) as LivePaymentsItem[];
    const livePaymentsLoading = useAppSelector((s) => s.clickHouse.livePaymentsLoading);
    const livePaymentsFailure = useAppSelector((s) => s.clickHouse.livePaymentsFailure);
    const liveRefundSummary = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess) as LiveRefundSummaryItem[];
    const liveRefundSummaryLoading = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const liveRefundSummaryFailure = useAppSelector((s) => s.clickHouse.liveRefundSummaryFailure);
    // Void + Comp — feed the Net Sales drill waterfall (ZAR-1462), same source as the Snapshot stamp KPIs.
    const liveVoidSummary = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess) as { cancelledAmt: number }[];
    const liveVoidSummaryLoading = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
    const liveCompOrderLog = useAppSelector((s) => s.clickHouse.liveCompOrderLog) as { amount: number }[];
    const liveCompOrderLogLoading = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
    const liveHourlySales = useAppSelector((s) => s.clickHouse.liveHourlySalesSuccess) as LiveHourlySalesItem[];
    const liveHourlySalesLoading = useAppSelector((s) => s.clickHouse.liveHourlySalesLoading);
    const liveHourlySalesFailure = useAppSelector((s) => s.clickHouse.liveHourlySalesFailure);
    const hourlySalesData = useAppSelector((s) => s.clickHouse.hourlySalesData) as HourlySalesResponse | null;
    const hourlySalesLoading = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailure = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const liveByChannel = useAppSelector((s) => s.clickHouse.liveByChannelSuccess) as LiveByChannelItem[];
    const liveByChannelLoading = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailure = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    const liveCategorySales = useAppSelector((s) => s.clickHouse.liveCategorySalesSuccess) as LiveCategorySalesItem[];
    const liveCategorySalesLoading = useAppSelector((s) => s.clickHouse.liveCategorySalesLoading);
    const liveCategorySalesFailure = useAppSelector((s) => s.clickHouse.liveCategorySalesFailure);
    const liveCategorySalesTotalElements = useAppSelector((s) => s.clickHouse.liveCategorySalesTotalElements);
    const liveCategorySalesTotalPages = useAppSelector((s) => s.clickHouse.liveCategorySalesTotalPages);
    const liveStaffPerformance = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess) as LiveStaffPerformanceItem[];
    const liveStaffPerformanceLoading = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailure = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveStaffRefunds = useAppSelector((s) => s.clickHouse.liveStaffRefundsSuccess) as LiveStaffRefundsItem[];
    const liveStaffRefundsLoading = useAppSelector((s) => s.clickHouse.liveStaffRefundsLoading);
    const liveStaffRefundsFailure = useAppSelector((s) => s.clickHouse.liveStaffRefundsFailure);
    const snapshotComparison = useAppSelector((s) => s.clickHouse.snapshotComparisonSuccess) as SnapshotComparisonResponse | null;
    const snapshotDetailsLoading = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    const snapshotComparisonLoading = useAppSelector((s) => s.clickHouse.snapshotComparisonLoading);
    const liveOutstandingValue = useAppSelector((s) => s.clickHouse.liveOutstandingValue) as LiveOutstandingValueResponse | null;
    const liveOutstandingValueLoading = useAppSelector((s) => s.clickHouse.liveOutstandingValueLoading);
    const liveItemSalesV2 = useAppSelector((s) => s.clickHouse.liveItemSalesV2Success) as LiveItemSalesV2Item[];

    // Order Log (Today / ClickHouse live)
    const orderLogOrders = useAppSelector((s) => s.clickHouse.orderLogOrders) as OrderLogItem[];
    const orderLogLoading = useAppSelector((s) => s.clickHouse.orderLogLoading);
    const orderLogFailure = useAppSelector((s) => s.clickHouse.orderLogFailure);
    const orderLogTotalElements = useAppSelector((s) => s.clickHouse.orderLogTotalElements);
    const orderLogTotalPages = useAppSelector((s) => s.clickHouse.orderLogTotalPages);

    // Export
    const exportOrderLogLoading = useAppSelector((s) => s.clickHouse.exportOrderLogLoading);

    // Channel Names for Order Type dropdown
    const channelNames = useAppSelector((s) => s.clickHouse.channelNames);

    // Order Detail slider
    const orderDetailData = useAppSelector((s) => s.clickHouse.orderDetailData) as OrderDetailResponse | null;
    const orderDetailLoading = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailError = useAppSelector((s) => s.clickHouse.orderDetailError);

    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [activeDate, setActiveDate] = useSharedReportDateFilter('Today');
    // Global sales-channel filter — per-report, defaults to Overall.
    const [channel, setChannel] = useState('Overall sales');
    const orderSourceVal = channelToOrderSource(channel);
    const orderSourceParam: { orderSource?: 'direct' | 'thirdparty' } = orderSourceVal ? { orderSource: orderSourceVal } : {};
    const isThirdParty = orderSourceVal === 'thirdparty';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // SW3 — deep-link into a specific drill via ?drill=<key> (currently only used to reopen
    // 'net-sales' when the user backs out of a cross-navigated Snapshot drill they opened from
    // here). Same mount-cascade-safe pattern as Snapshot — see its drillFromUrlRef comment.
    const drillFromUrlRef = useRef(false);
    const [drillView, setDrillView] = useState<string | null>(() => {
        const raw = searchParams.get('drill');
        if (!raw) return null;
        drillFromUrlRef.current = true;
        return raw;
    });
    const [tenderPage, setTenderPage] = useState(0);
    const [staffPage, setStaffPage] = useState(0);
    const [discountsStaffPage, setDiscountsStaffPage] = useState(0);
    const [refundsStaffPage, setRefundsStaffPage] = useState(0);
    const goBack = useCallback(() => setDrillView(null), []);
    useEffect(() => {
        if (!drillFromUrlRef.current) return;
        const t = setTimeout(() => { drillFromUrlRef.current = false; }, 0);
        return () => clearTimeout(t);
    }, []);
    // SW3 — landing back on the Net Sales drill via the ?drill=net-sales return link should
    // show its KPI row (Net Sales/Total Collected/Peak Hour/Avg per hour), not whatever scroll
    // offset the page happens to carry over from before the user drilled into Voids/Complimentary.
    const netSalesKpiRowRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (drillView !== 'net-sales' || !drillFromUrlRef.current) return;
        requestAnimationFrame(() => netSalesKpiRowRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }, [drillView]);
    const fetchIdRef = useRef(0);
    const snapshotFetchKeyRef = useRef<string | null>(null);
    const thirdPartyFetchKeyRef = useRef<string | null>(null);
    const channelNamesKeyRef = useRef<string | null>(null);
    const orderLogKeyRef = useRef<string | null>(null);
    const retryFunctionsRef = useRef<Record<string, () => void>>({});
    const kpiExportRef = useRef<Record<string, any>>({});
    const [sectionErrors, setSectionErrors] = useState<Record<string, boolean>>({});
    const markSectionError = useCallback((key: string, hasError: boolean) => {
        setSectionErrors((prev) => (prev[key] === hasError ? prev : { ...prev, [key]: hasError }));
    }, []);
    const retrySection = useCallback((key: string) => {
        retryFunctionsRef.current[key]?.();
    }, []);
    const [salesRefreshedTime, setSalesRefreshedTime] = useState<string | null>(() => localStorage.getItem(SALES_REFRESH_TS_KEY));
    const [salesRefreshNonce, setSalesRefreshNonce] = useState(0);
    const recordSalesRefreshedTime = useCallback(() => {
        const ts = new Date().toISOString();
        localStorage.setItem(SALES_REFRESH_TS_KEY, ts);
        setSalesRefreshedTime(ts);
    }, []);
    const [isSalesSpinningD, setIsSalesSpinningD] = useState(false);
    const handleSalesManualRefresh = useCallback(() => {
        setSalesRefreshNonce(n => n + 1);
        recordSalesRefreshedTime();
        setIsSalesSpinningD(true);
    }, [recordSalesRefreshedTime]);
    // Historical discount summary loading flag — error is tracked via sectionErrors.discounts.
    const [discountsLoading, setDiscountsLoading] = useState(false);
    // Historical refunds-by-staff: own /sales/cancelSummary call, fetched lazily when the refunds drill opens.
    const [cancelReasonsItems, setCancelReasonsItems] = useState<any[]>([]);
    const [cancelReasonsLoading, setCancelReasonsLoading] = useState(false);
    const [cancelReasonsFailure, setCancelReasonsFailure] = useState(false);
    const [cancelReasonsReload, setCancelReasonsReload] = useState(0);
    const cancelReasonsFetchKeyRef = useRef<string | null>(null);

    // ── Order Log state (SALES-7/8/9) ──
    const [olPage, setOlPage] = useState(0);
    const [olSort, setOlSort] = useState('orderTime');
    const [olSortDir, setOlSortDir] = useState<'desc' | 'asc'>('desc');
    const [olSearch, setOlSearch] = useState('');
    const [olDebouncedSearch, setOlDebouncedSearch] = useState('');
    const [olOrderTypes, setOlOrderTypes] = useState<string[]>([]);
    // Set by the Sales by Channel — Financial Breakdown drill-down; sent as the
    // `channelGroup` query param (NOT `channelName`, which filters order types).
    const [olChannelGroup, setOlChannelGroup] = useState<OrderChannelGroup | null>(null);
    const [olMinAmt, setOlMinAmt] = useState('');
    const [olMaxAmt, setOlMaxAmt] = useState('');
    const [olMinGuests, setOlMinGuests] = useState('');
    const [olPageSize, setOlPageSize] = useState(10);
    const [olOrders, setOlOrders] = useState<any[]>([]);
    const [olTotal, setOlTotal] = useState(0);
    const [olLoading, setOlLoading] = useState(false);
    const [olTypeMenuOpen, setOlTypeMenuOpen] = useState(false);
    const [olPhoneVisible, setOlPhoneVisible] = useState(false);
    // Order detail slide-over
    const [olSelectedOrder, setOlSelectedOrder] = useState<any>(null);
    const [olOrderDetail, setOlOrderDetail] = useState<any>(null);
    const [olDetailLoading, setOlDetailLoading] = useState(false);

    // ── Sales by Category pagination (ZAR-1223) ──
    // catPage is 0-indexed in the UI and sent to the backend as +1 (1-indexed).
    // Default page size is 5 (show the 5 highest-revenue categories).
    const [catPage, setCatPage] = useState(0);
    const [catPageSize, setCatPageSize] = useState<number | 'All'>(5);
    const catReqSize = catPageSize === 'All' ? CAT_ALL_SIZE : catPageSize;
    // Full category set (all pages) — drives the running total + CSV/PDF export.
    const [catAllItems, setCatAllItems] = useState<LiveCategorySalesItem[]>([]);
    const catAllKeyRef = useRef<string | null>(null);
    const catGrandTotal = useMemo(() => catAllItems.reduce((s, it) => s + (it.price || 0), 0), [catAllItems]);

    const [data, setData] = useState<any>(() => ({
        summary: null,
        channel: null,
        hourly: null,
        category: null,
        discounts: null,
        staffSales: null,
        staffTips: null,
        topItems: null,
        revenueClass: null,
        payments: null,
        voidedSummary: null,
        // Live today-only fields
        liveBilledOrders: null,
        liveNetSales: null,
        liveItemSales: null,
        isToday: getInitialReportDateFilter('Today') === 'Today',
    }));

    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDate, today, yesterday, storeTz);
        if (range) return range;
        return { startDate: selectedDate, endDate: selectedDate };
    }, [activeDate, selectedDate, today, yesterday, storeTz]);

    // Human-readable active date range for the "All outlets" export file header.
    const exportDateRangeLabel = exportPeriodFor(activeDate, dateRange)?.label ?? activeDate;

    // ClickHouse date params for drill retries — omitted for Today (live path), passed for historical.
    const chDatesView: { startDate?: string; endDate?: string } = data.isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };

    // Sales by Channel — Financial Breakdown (ZAR-1555)
    const salesByChannelBreakdown = useSalesByChannelBreakdown({
        locationId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        isToday: dateRange.startDate === today,
        orderSource: orderSourceVal,
        refreshNonce: salesRefreshNonce,
    });

    const DRILL_TITLES: Record<string, string> = {
        'net-sales': 'Net Sales',
        discounts: 'Discounts',
        refunds: 'Refunds',
        // Card face reads "Tips & Grat"; the breadcrumb/title keeps the full name (ZAR-1762 / TG-1).
        tips: TG_DRILL_TITLE,
    };

    // Close drill when date filter changes
    useEffect(() => { if (!drillFromUrlRef.current) setDrillView(null); }, [activeDate]);

    const buildSalesKpiExport = useCallback(async (_format?: ExportFormat, opts?: { locationId?: string }) => {
        let k: Record<string, any> = kpiExportRef.current;
        if (opts?.locationId) {
            const loc = opts.locationId;
            const { chDates, orderSourceVal: os, token: tok } = kpiExportRef.current;
            // ZAR-1762 — Tips & Grat now comes straight off snapshot-details' tipAndGratuity,
            // so this refetch no longer needs the staff-performance call it only summed tips from.
            const [snap, refundRes, outstandingRes]: any[] = await Promise.all([
                getSnapshotDetails({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveRefundSummaryApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveOutstandingValueApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
            ]);
            const refundItems: any[] = Array.isArray(refundRes) ? refundRes : (refundRes?.data ?? []);
            const netSalesOther = num(snap?.netSales);
            const totalOrdersOther = num(snap?.orderCount);
            k = {
                ...kpiExportRef.current,
                grossSales: num(snap?.grandTotal),
                netSales: netSalesOther,
                totalOrders: totalOrdersOther,
                avgOrderValue: totalOrdersOther > 0 ? netSalesOther / totalOrdersOther : 0,
                spendPerGuest: num(snap?.guestCount) > 0 ? num(snap?.spendPerGuest) : null,
                totalTax: num(snap?.tax),
                totalDiscounts: num(snap?.discount),
                refunds: parseFloat(refundItems.reduce((s: number, item: any) => s + (item.cancelledAmt || 0), 0).toFixed(2)),
                // ZAR-1762 / TG-1 — export mirrors the on-screen card: the backend's own combined figure.
                tipsAndGratuity: num(snap?.tipAndGratuity),
                otherCharges: num(snap?.otherCharges),
                outstandingAmount: num(outstandingRes?.outStandingAmt),
            };
        }
        const sym = getCurrencySymbol();
        const fmtMoney = (v: number) => `${sym}${formatMonetaryExport(v)}`;
        const rows: ExportCell[][] = [
            ['From', fmtExportDate(k.dateRange?.startDate)],
            ['To', fmtExportDate(k.dateRange?.endDate)],
            // GLOBAL-DP-4: the export is read away from the date control that states the
            // basis, so the comparison period travels with the file.
            ['Comparison From', fmtExportDate(comparisonRangeFor(k.dateRange?.startDate, k.dateRange?.endDate)?.comparisonStart)],
            ['Comparison To', fmtExportDate(comparisonRangeFor(k.dateRange?.startDate, k.dateRange?.endDate)?.comparisonEnd)],
            ['Channel Filter', k.channel],
            ['Total Collected', fmtMoney(num(k.grossSales))],
            ['Net Sales', fmtMoney(num(k.netSales))],
            ['Total Orders', fmtNum(k.totalOrders)],
            ['Avg Order Value', fmtMoney(num(k.avgOrderValue))],
            ['Total Tax', fmtMoney(num(k.totalTax))],
            ['Total Discounts', fmtMoney(num(k.totalDiscounts))],
            ['Refunds', fmtMoney(num(k.refunds))],
            ['Tips & Grat', fmtMoney(num(k.tipsAndGratuity))],
        ];
        rows.push(['Other', fmtMoney(num(k.otherCharges))]);
        rows.push(['Outstanding Amount', fmtMoney(num(k.outstandingAmount))]);
        // SPG-1: Spend/Guest — last stamp KPI on screen, so last in the export too.
        rows.push(['Spend / Guest', k.spendPerGuest == null ? '—' : fmtMoney(num(k.spendPerGuest))]);
        return { headers: ['KPI', 'Value'], rows };
    }, []);

    // Push title + date filters to the shared TopBar via context
    useEffect(() => {
        setTopBarProps({
            breadcrumbs: drillView ? 'Reports › Sales' : 'Reports',
            title: drillView ? DRILL_TITLES[drillView] ?? 'Sales' : 'Sales Report',
            dateFilters: drillView ? undefined : {
                value: activeDate,
                onChange: setActiveDate,
            },
            sourceFilters: {
                options: CHANNEL_OPTIONS,
                value: channel,
                onChange: setChannel,
            },
            leftAddon: drillView ? (
                <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
            extraContent: (!drillView) ? (
                <>
                    {(false && activeDate === 'Today') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--t2)', fontSize: 12, flexShrink: 0 }}>
                            {salesRefreshedTime && <span>As of {formatSalesRefreshedTime(salesRefreshedTime)}</span>}
                            <button type="button" onClick={handleSalesManualRefresh} aria-label="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                                <span className="material-symbols-rounded" onAnimationEnd={() => setIsSalesSpinningD(false)} style={{ fontSize: 18, color: isSalesSpinningD ? 'var(--accent)' : 'var(--t2)', display: 'inline-block', animation: isSalesSpinningD ? 'spin 0.5s linear' : 'none' }}>refresh</span>
                            </button>
                        </div>
                    ) : null}
                    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                        title="Sales KPIs"
                        filename={kpiExportFilename('sales-kpis', dateRange.startDate, dateRange.endDate, orderSourceVal)}
                        buildExport={buildSalesKpiExport}
                        variant="desktop"
                        iconOnly
                    />
                </>
            ) : undefined,
        });
    }, [setTopBarProps, activeDate, channel, drillView, goBack, salesRefreshedTime, handleSalesManualRefresh, isSalesSpinningD, buildSalesKpiExport, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    useEffect(() => {
        if (!locationId || !token) {
            setLoading(false);
            return;
        }

        const id = ++fetchIdRef.current;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        console.log('[SalesScreen][Desktop] fetch opts =>', { startDate: opts.startDate, endDate: opts.endDate, isToday });

        setLoading(true);
        setError('');

        // Per-section runner: fetches the API, tracks errors, patches just that field,
        // and stashes a retry closure that re-runs this same call with current params.
        // Returns a settled Promise (resolved on both success and failure) so callers
        // can await it to drive the primary-section loading flag.
        const runSection = (key: string, factory: () => Promise<any>, field: string, fallback: any = null): Promise<void> => {
            const run = (): Promise<void> => {
                markSectionError(key, false);
                return factory()
                    .then((r) => {
                        if (id !== fetchIdRef.current) return;
                        setData((prev: any) => ({ ...prev, [field]: r }));
                        markSectionError(key, false);
                    })
                    .catch(() => {
                        if (id !== fetchIdRef.current) return;
                        setData((prev: any) => ({ ...prev, [field]: fallback }));
                        markSectionError(key, true);
                    });
            };
            retryFunctionsRef.current[key] = run;
            return run();
        };

        // Third-Party Marketplace (unfiltered by-channel) — data is channel-independent,
        // so only refetch on location/date/refresh changes, not when the global filter changes
        const tpFetchKey = `${locationId}-${dateRange.startDate}-${dateRange.endDate}-${salesRefreshNonce}`;
        if (thirdPartyFetchKeyRef.current !== tpFetchKey) {
            thirdPartyFetchKeyRef.current = tpFetchKey;
            dispatch(fetchThirdPartyByChannelRequest({ locationId, ...chDates }));
        }

        if (isToday) {
            const fetchKey = `${locationId}-${dateRange.startDate}-${salesRefreshNonce}-${channel}`;
            if (snapshotFetchKeyRef.current !== fetchKey) {
                snapshotFetchKeyRef.current = fetchKey;
                recordSalesRefreshedTime();
                dispatch(fetchSnapshotDetailsRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchSnapshotComparisonRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveRefundSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                // Void + Comp — back the Net Sales drill waterfall (ZAR-1462).
                dispatch(fetchLiveVoidSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: WF_COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
                dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
                dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCategorySalesRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveItemSalesV2Request(locationId, undefined, undefined, undefined, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveOutstandingValueRequest(locationId, undefined, undefined, orderSourceVal));
            }
            setData((prev: any) => ({
                ...prev,
                summary: null, channel: null, category: null, topItems: null,
                revenueClass: null, payments: null, hourly: null,
                discounts: null, staffSales: null, voidedSummary: null,
                liveBilledOrders: null, liveNetSales: null, liveItemSales: null,
                isToday: true,
            }));
            setLoading(false);
        } else {
            // Historical: SAME ClickHouse endpoints as Today, just with startDate/endDate.
            // Reset so switching back to Today re-triggers snapshot details fetch
            snapshotFetchKeyRef.current = null;
            setData((prev: any) => ({
                ...prev,
                summary: null, channel: null, category: null, topItems: null,
                revenueClass: null, payments: null, hourly: null,
                discounts: null, staffSales: null, voidedSummary: null,
                liveBilledOrders: null, liveNetSales: null, liveItemSales: null,
                isToday: false,
            }));

            dispatch(fetchSnapshotDetailsRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchSnapshotComparisonRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveRefundSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            // Void + Comp — back the Net Sales drill waterfall (ZAR-1462).
            dispatch(fetchLiveVoidSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: WF_COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCategorySalesRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveItemSalesV2Request(locationId, 0, 10, undefined, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveOutstandingValueRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            // Staff Sales Leaderboard — historical-only section (no ClickHouse equivalent yet)
            runSection('staffSales', () => getStaffSales(opts), 'staffSales');
            setLoading(false);
        }
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, markSectionError, salesRefreshNonce, recordSalesRefreshedTime]);

    // ── Sales by Category — server-paginated view (ZAR-1223) ──
    // Reset to the first page whenever the period, channel, or page size changes.
    useEffect(() => { setCatPage(0); }, [locationId, dateRange.startDate, dateRange.endDate, orderSourceVal, catPageSize, salesRefreshNonce]);
    // Fetch the current page (page/size passed to the backend).
    useEffect(() => {
        if (!locationId || !token) return;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const start = isToday ? undefined : dateRange.startDate;
        const end = isToday ? undefined : dateRange.endDate;
        dispatch(fetchLiveCategorySalesRequest(locationId, start, end, orderSourceVal, catPage + 1, catReqSize));
    }, [dispatch, locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, catPage, catReqSize, salesRefreshNonce]);
    // Fetch ALL categories once per period/channel — drives the running total + export.
    useEffect(() => {
        if (!locationId || !token) return;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const dates = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const key = `${locationId}|${dateRange.startDate}|${dateRange.endDate}|${orderSourceVal}|${salesRefreshNonce}`;
        if (catAllKeyRef.current === key) return;
        catAllKeyRef.current = key;
        let cancelled = false;
        getLiveCategorySalesApi({ locationId, page: 1, size: CAT_ALL_SIZE, ...dates, orderSource: orderSourceVal, token })
            .then((res: any) => { if (!cancelled) setCatAllItems(Array.isArray(res) ? res : (res?.categories ?? res?.data ?? [])); })
            .catch(() => { if (!cancelled) setCatAllItems([]); });
        return () => { cancelled = true; };
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, salesRefreshNonce]);

    // ── Derive KPI values (unified: snapshotDetails primary, billedSrc fallback) ──
    const billedSrc = unwrapRes(data.liveBilledOrders) ?? unwrapRes(data.liveNetSales) ?? data.summary;

    const grossSales = num(snapshotDetails?.grandTotal) || num(pick(billedSrc, 'grossSales', 'totalGrossSales', 'gross_sales', 'totalSales', 'totalGrossSalesIncludingThirdparty', 'magilSales', 'maghilSales') || pick(data.summary, 'grossSales', 'totalGrossSales', 'totalGrossSalesIncludingThirdparty', 'magilSales'));
    const grossSalesPct = numPct(snapshotComparison?.grandTotal) ?? numPct(pick(billedSrc, 'totalSalesPercentage', 'magilSalesPercentage', 'grossSalesChangePercent') ?? pick(data.summary, 'totalSalesPercentage', 'magilSalesPercentage', 'grossSalesChangePercent'));

    const netSales = num(snapshotDetails?.netSales) || num(pick(billedSrc, 'totalNetSales', 'netSales', 'totalSales', 'net_sales', 'revenue') ?? pick(data.summary, 'totalNetSales', 'netSales', 'totalSales'));
    const netSalesPct = numPct(snapshotComparison?.netSales) ?? numPct(pick(billedSrc, 'netSalesPercentage', 'magilNetSalesPercentage', 'netSalesChangePercent') ?? pick(data.summary, 'netSalesPercentage', 'magilNetSalesPercentage', 'netSalesChangePercent'));

    const totalOrders = num(snapshotDetails?.orderCount) || num(pick(billedSrc, 'totalTransactions', 'totalOrders', 'orders', 'orderCount') ?? pick(data.summary, 'totalTransactions', 'totalOrders', 'orders', 'orderCount'));
    const orderPct = numPct(snapshotComparison?.orderCount) ?? numPct(pick(billedSrc, 'transactionPercentage', 'magilTransactionPercentage', 'transactionsChangePercent') ?? pick(data.summary, 'transactionPercentage', 'magilTransactionPercentage', 'transactionsChangePercent'));

    const avgOrderValue = snapshotDetails?.orderCount != null
        ? (totalOrders > 0 ? netSales / totalOrders : 0)
        : num(pick(billedSrc, 'avgOrderValue') ?? pick(data.summary, 'avgOrderValue') ?? (totalOrders > 0 ? netSales / totalOrders : 0));
    const avgOrderPct = numPct(snapshotComparison?.avgOrderValue) ?? numPct(pick(billedSrc, 'avgOrderChangePercent') ?? pick(data.summary, 'avgOrderChangePercent'));

    // SPG-1: Spend per Guest — value from snapshot-details spendPerGuest, scoped to guest-bearing
    // orders. "—" when there are no guests in the active period/source (incalculable), never $0.00.
    const spgGuestCount = num(snapshotDetails?.guestCount);
    const spendPerGuest = spgGuestCount > 0 ? num(snapshotDetails?.spendPerGuest) : null;
    const spendPerGuestPct = numPct(snapshotComparison?.spendPerGuest);

    const totalTax = num(snapshotDetails?.tax ?? (snapshotDetails ? 0 : undefined)) || num(pick(billedSrc, 'tax', 'totalTax', 'taxAmount') ?? pick(data.summary, 'tax', 'totalTax', 'taxAmount'));
    const taxPct = numPct(snapshotComparison?.tax) ?? numPct(pick(billedSrc, 'totalTaxPercentage', 'magilTaxPercentage', 'taxChangePercent') ?? pick(data.summary, 'totalTaxPercentage', 'magilTaxPercentage', 'taxChangePercent'));

    const totalDiscounts = num(snapshotDetails?.discount) || num(pick(billedSrc, 'discounts', 'totalDiscount', 'totalDiscounts') ?? pick(data.summary, 'discounts', 'totalDiscount', 'totalDiscounts'));
    const discountsPct = numPct(pick(billedSrc, 'discountPercentage', 'magilDiscountPercentage', 'discountsChangePercent') ?? pick(data.summary, 'discountPercentage', 'magilDiscountPercentage', 'discountsChangePercent'));

    const liveRefundOrderCount = parseFloat(liveRefundSummary.reduce((sum, item) => sum + item.orderCount, 0).toFixed(2));
    const liveCancelledAmt = parseFloat(liveRefundSummary.reduce((sum, item) => sum + (item.cancelledAmt || 0), 0).toFixed(2));
    const refundsPct = numPct(snapshotComparison?.refund) ?? numPct(pick(billedSrc, 'cancelledAmtPercentage', 'magilCancelledAmtPercentage', 'refundsChangePercent', 'refundChangePercent') ?? pick(data.summary, 'cancelledAmtPercentage', 'magilCancelledAmtPercentage', 'refundsChangePercent', 'refundChangePercent'));

    const tips = num(snapshotDetails?.tip);

    // ZAR-1762 / TG-1 — the standalone Gratuity card is gone; gratuity now only exists as part
    // of the unified Tips & Gratuity total. The card face reads the backend's own combined
    // total and combined delta (`tipAndGratuity` on snapshot-details and on its /comparison
    // twin), so the FE never re-derives either figure.
    const gratuity = num(snapshotDetails?.gratuity);
    const tipsAndGratuity = num(snapshotDetails?.tipAndGratuity);
    const tipsGratuityPct = numPct(snapshotComparison?.tipAndGratuity) ?? null;
    // ZAR-1762 / TG-2 — both drill-in sections, rebuilt whenever the source toggle or date
    // range changes so no value from a previous period/source survives. Totals, order counts
    // and Net Sales come from snapshot-details; staff-performance and by-channel supply only
    // the two rankings and the two channel panels.
    const tipsGratuityDrillData = useMemo(() => buildTipsGratuityData({
        staff: liveStaffPerformance.map((s) => ({
            name: s.staff_name,
            tip: s.tip ?? 0,
            gratuity: s.gratuity ?? 0,
            orderCount: s.orderCount ?? 0,
            netSales: s.netSales ?? 0,
        })),
        channels: liveByChannel.map((c) => ({ channel: c.channel, tip: c.tip ?? 0, gratuity: c.gratuity ?? 0 })),
        // Tip Rate / Gratuity Rate share the screen's Net Sales denominator.
        netSales,
        totalOrders: num(snapshotDetails?.orderCount),
        tipsTotal: num(snapshotDetails?.tip),
        gratuityTotal: num(snapshotDetails?.gratuity),
        ordersWithTips: num(snapshotDetails?.ordersWithTips),
        ordersWithGratuity: num(snapshotDetails?.ordersWithGratuity),
    }), [liveStaffPerformance, liveByChannel, netSales, snapshotDetails]);

    const otherCharges = num(snapshotDetails?.otherCharges) || num(pick(billedSrc, 'otherCharges', 'totalOtherCharges', 'otherCharge') ?? pick(data.summary, 'otherCharges', 'totalOtherCharges', 'otherCharge'));
    const otherChargesPct = num(pick(billedSrc, 'otherChargesChangePercent') ?? pick(data.summary, 'otherChargesChangePercent'));

    const outstandingAmount = num(liveOutstandingValue?.outStandingAmt) || num(pick(billedSrc, 'outstandingAmount', 'totalOutstandingAmount', 'outstanding') ?? pick(data.summary, 'outstandingAmount', 'totalOutstandingAmount', 'outstanding'));
    const outstandingPct = numPct(snapshotComparison?.outStandingAmt) ?? num(pick(billedSrc, 'outstandingAmountChangePercent', 'outstandingChangePercent') ?? pick(data.summary, 'outstandingAmountChangePercent', 'outstandingChangePercent'));

    // Sales report-level "Export KPIs" — mirrors exactly what each stamp KPI tile shows on screen.
    kpiExportRef.current = {
        activeDate,
        dateRange,
        chDates: chDatesView,
        orderSourceVal,
        token,
        channel,
        isThirdParty,
        grossSales, netSales, totalOrders, avgOrderValue, spendPerGuest, totalTax, totalDiscounts,
        refunds: liveRefundSummaryFailure ? 0 : liveCancelledAmt,
        tipsAndGratuity, otherCharges, outstandingAmount,
    };

    // ZAR-1661: delta visibility follows the SHAPE of the selected period, not a literal
    // token list. "This month", "This year", "Last 30 days" and every stepped/hand-picked
    // range that matches a registered shape now carry a comparison — and a stepper emits
    // `custom:from:to`, which no hardcoded list could have covered.
    const showDelta = hasComparison(activeDate, storeTz);

    // ── Channel data ──
    const channelArr = listFrom(data.channel);

    // Same as Snapshot: primary channels shown individually, everything else collapses into "3rd Party"
    const PRIMARY_CHANNELS_SALES = /^(dinein|dine-in|dine in|instore|in-store|in store|pickup|pick-up|pick up|delivery)$/i;
    const normalizedChannelArr = (() => {
        let othersValue = 0;
        let othersCount = 0;
        const primary: any[] = [];
        channelArr.forEach((item: any) => {
            const name = (item.channelName ?? item.channel ?? item.name ?? '').trim();
            const value = Math.round((Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0) * 100) / 100;
            const count = Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0;
            if (PRIMARY_CHANNELS_SALES.test(name)) {
                primary.push(item);
            } else if (value > 0) {
                othersValue += value;
                othersCount += count;
            }
        });
        if (othersValue > 0) {
            primary.push({ channelName: '3rd Party', totalSales: Math.round(othersValue * 100) / 100, orderCount: othersCount });
        }
        return primary;
    })();

    const channelData = normalizedChannelArr.map((item: any, index: number) => ({
        name: item.channelName ?? item.channel ?? item.name ?? `Channel ${index + 1}`,
        value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
        count: Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0,
        color: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
    })).filter((item: any) => item.value > 0).sort((a: any, b: any) => b.value - a.value);
    const channelTotal = channelData.reduce((sum: number, item: any) => sum + item.value, 0);
    const channelPcts = distribute100(channelData.map((d: any) => d.value));
    const channelPctsExact = distribute100At2dp(channelData.map((d: any) => d.value));
    const channelBreakdown = channelData.map((item: any, i: number) => ({
        ...item,
        pct: channelPcts[i],
        pctExact: channelPctsExact[i],
    }));

    // ── Today Sales by Channel (ClickHouse Redux only — no fallbacks) ──
    // Source: GET /sales/live/v2/by-channel via fetchLiveByChannelRequest.
    // grandTotal drives the slice value; percentage is calculated on the frontend.
    const liveChFiltered = liveByChannel.filter((item) => item.grandTotal > 0);
    const liveChTotal = liveChFiltered.reduce((s, item) => s + item.grandTotal, 0);
    const liveChDoughnutData: DoughnutSlice[] = (() => {
        if (!liveChFiltered.length) return [];
        const sorted = [...liveChFiltered].sort((a, b) => b.grandTotal - a.grandTotal);
        const pcts = distribute100(sorted.map((item) => item.grandTotal));
        return sorted.map((item, i) => ({
            name: item.channel,
            value: item.grandTotal,
            color: getChannelColor(item.channel),
            pct: pcts[i],
        }));
    })();

    // ── Hourly data ── (unified: both Today and Historical use the new /sales/live/v2/hourly-sales API via Redux)
    const hourlyHours = hourlySalesData?.hours ?? [];
    const opStartHour = hourlySalesData?.operatingStartHour ?? 6;
    const opEndHour = hourlySalesData?.operatingEndHour ?? 23;
    const opHourCount = hourlySalesData?.operatingHourCount ?? 0;
    const hourlyData: { label: string; value: number }[] = hourlyHours.map(h => {
        const hv = h.hourValue % 24;
        const disp = hv % 12 || 12;
        const suffix = hv >= 12 ? 'p' : 'a';
        return { label: `${disp}${suffix}`, value: h.itemTotal };
    });
    const hourlyAvg = opHourCount > 0
        ? hourlyHours
            .filter(h => h.hourValue >= opStartHour && h.hourValue <= opEndHour)
            .reduce((s, h) => s + h.itemTotal, 0) / opHourCount
        : 0;

    // ── Category data ──
    const categoryRaw = listFrom(data.category);
    const categoryData = categoryRaw.map((item: any) => ({
        name: item.categoryName ?? item.name ?? item.label ?? '—',
        revenue: Number(item.totalPrice ?? item.totalSales ?? item.revenue ?? item.grossSales ?? item.amount ?? 0) || 0,
        qty: Number(item.totalQuantity ?? item.salesQty ?? item.qty ?? item.quantity ?? 0) || 0,
    })).filter((item: any) => item.revenue > 0).sort((a: any, b: any) => b.revenue - a.revenue);
    const maxCatRevenue = categoryData[0]?.revenue || 1;

    // ── Top Items ── (unified: ClickHouse item-sales for both Today and historical)
    const topItemsSource = liveItemSalesV2.length > 0 ? liveItemSalesV2 : listFrom(data.topItems);
    const topItems = topItemsSource
        .map((item: any) => ({
            name: item.itemName ?? item.name ?? item.menuItemName ?? item.label ?? '—',
            qty: Number(item.salesQty ?? item.sales_qty ?? item.itemCount ?? item.quantitySold ?? item.qty ?? item.quantity ?? item.count ?? 0) || 0,
            rev: Number(item.totalItemPrice ?? item.price ?? item.grossSales ?? item.gross_sales ?? item.itemTotal ?? item.totalSales ?? item.amount ?? item.revenue ?? 0) || 0,
        }))
        .filter((item: any) => item.name !== '—' || item.rev > 0 || item.qty > 0)
        .sort((a: any, b: any) => b.rev - a.rev)
        .slice(0, 10);

    // ── Staff Sales ──
    console.log('[SalesScreen][Desktop] /sales/staff raw =>', data.staffSales);
    const staffSalesList = listFrom(data.staffSales);
    const staffLeaderboard = staffSalesList
        .map((item: any) => ({
            name: item.fullName ?? '—',
            role: item.role ?? '',
            sales: Number(item.total ?? 0) || 0,
            orders: Number(item.orders ?? 0) || 0,
        }))
        .filter((item: any) => item.name !== '—' || item.sales > 0)
        .sort((a: any, b: any) => b.sales - a.sales);

    // ── Staff Tips ──
    const staffTipsList = listFrom(data.staffTips);
    const staffTipsBoard = staffTipsList
        .map((s: any) => ({
            name: s.fullName ?? s.staffName ?? s.employeeName ?? s.name ?? '—',
            tips: Number(s.tip ?? s.tips ?? s.amount ?? s.totalTips ?? s.tipAmount ?? s.gratuity ?? 0) || 0,
            orders: Number(s.orders ?? s.totalOrders ?? s.orderCount ?? 0) || 0,
        }))
        .filter((s: any) => s.tips > 0)
        .sort((a: any, b: any) => b.tips - a.tips)
        .slice(0, 8);
    const maxStaffTip = staffTipsBoard[0]?.tips || 1;

    // ── Discount Details ──
    const discountList = listFrom(data.discounts);
    const discountOffers = discountList
        .map((d: any) => ({
            name: d.offerName ?? d.discountName ?? d.name ?? d.offer ?? '—',
            type: d.discountType ?? d.type ?? '—',
            rate: Number(d.discountRate ?? d.rate ?? d.percentage ?? 0) || 0,
            sales: Number(d.salesAmount ?? d.grossSales ?? d.sales ?? 0) || 0,
            amount: Number(d.discountAmount ?? d.amount ?? d.totalDiscount ?? d.value ?? 0) || 0,
            count: Number(d.orderCount ?? d.count ?? d.orders ?? 0) || 0,
        }))
        .filter((d: any) => d.amount > 0)
        .sort((a: any, b: any) => b.amount - a.amount)
        .slice(0, 10);
    const maxDiscountAmt = discountOffers[0]?.amount || 1;

    // ── Staff who gave discounts ──
    const staffDiscountMap: Record<string, { name: string; role: string; total: number; count: number }> = {};
    discountList.forEach((d: any) => {
        const staffName = d.staffName ?? d.employeeName ?? d.staff ?? '';
        if (staffName) {
            if (!staffDiscountMap[staffName]) staffDiscountMap[staffName] = { name: staffName, role: d.role ?? d.staffRole ?? '', total: 0, count: 0 };
            staffDiscountMap[staffName].total += Number(d.discountAmount ?? d.amount ?? 0) || 0;
            staffDiscountMap[staffName].count += Number(d.orderCount ?? d.count ?? 1) || 0;
        }
    });
    const staffDiscounts = Object.values(staffDiscountMap).sort((a, b) => b.total - a.total).slice(0, 6);

    // ── Tips by Channel (derived from channel breakdown) ──
    const tipsTotal = tips;
    const tipsByChannelRaw = channelTotal > 0 && channelBreakdown.length
        ? channelBreakdown.map((item: any) => ({ ...item, tipValue: (item.value / channelTotal) * tipsTotal }))
        : [];
    const tipPcts = distribute100(tipsByChannelRaw.map((d: any) => d.tipValue));
    const tipsByChannelData = tipsByChannelRaw.map((item: any, i: number) => ({ ...item, tipPct: tipPcts[i] }));

    // ── Payment Details ──
    const paymentList = listFrom(data.payments);
    const tenderDataRaw = (() => {
        const grouped: Record<string, { type: string; amount: number; count: number }> = {};
        paymentList.forEach((p: any) => {
            const type = p.paymentMode ?? p.paymentType ?? p.tenderType ?? p.type ?? p.name ?? '—';
            const amount = Number(p.totalSales ?? p.totalAmount ?? p.amount ?? p.total ?? p.netAmount ?? 0) || 0;
            const count = Number(p.totalOrders ?? p.transactionCount ?? p.count ?? p.orders ?? 0) || 0;
            if (!grouped[type]) grouped[type] = { type, amount: 0, count: 0 };
            grouped[type].amount += amount;
            grouped[type].count += count;
        });
        return Object.values(grouped)
            .filter((p) => p.amount > 0)
            .sort((a, b) => b.amount - a.amount);
    })();
    const tenderPcts = distribute100At2dp(tenderDataRaw.map((t: any) => t.amount));
    const tenderData = tenderDataRaw.map((t: any, i: number) => ({ ...t, pct: tenderPcts[i] }));
    const TENDER_PAGE_SIZE = 5;
    const tenderTotalPages = Math.ceil(tenderData.length / TENDER_PAGE_SIZE);
    const tenderPageData = tenderData.slice(tenderPage * TENDER_PAGE_SIZE, (tenderPage + 1) * TENDER_PAGE_SIZE);
    // Reset to page 0 whenever the tender dataset changes (date/location switch)
    useEffect(() => { setTenderPage(0); }, [data.payments]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Order Log: debounce search ──
    useEffect(() => {
        const t = setTimeout(() => setOlDebouncedSearch(olSearch), 300);
        return () => clearTimeout(t);
    }, [olSearch]);

    // ── Order Log: reset page on filter/date change ──
    useEffect(() => { setOlPage(0); }, [olDebouncedSearch, olOrderTypes, olChannelGroup, olMinAmt, olMaxAmt, olMinGuests, olPageSize, dateRange.startDate, dateRange.endDate, channel]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Channel Names: fetch once per location ──
    useEffect(() => {
        if (locationId && channelNamesKeyRef.current !== locationId) {
            channelNamesKeyRef.current = locationId;
            dispatch(fetchChannelNamesRequest(locationId));
        }
    }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Order Log: fetch ──
    // For Today → dispatch to Redux (ClickHouse live endpoint, no date params).
    // For other dates → call the legacy reportsApi endpoint with local state.
    useEffect(() => {
        if (!locationId) return;
        const olAmtError = olMinAmt && olMaxAmt && Number(olMinAmt) >= Number(olMaxAmt);
        if (olAmtError) return; // do not fetch while min/max are invalid
        const olIsToday = dateRange.startDate === today;
        const olDates = olIsToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const olKey = `${locationId}|${dateRange.startDate}|${dateRange.endDate}|${olIsToday}|${olPage}|${olPageSize}|${olSort}|${olSortDir}|${olDebouncedSearch}|${olOrderTypes.join(',')}|${olChannelGroup ?? ''}|${olMinAmt}|${olMaxAmt}|${olMinGuests}|${channel}|${salesRefreshNonce}`;
        if (orderLogKeyRef.current === olKey) return;
        orderLogKeyRef.current = olKey;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: olPage + 1,
            size: olPageSize,
            sortBy: olSort,
            sortDir: olSortDir,
            search: olDebouncedSearch || undefined,
            channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined,
            channelGroup: olChannelGroup ?? undefined,
            guestCount: olMinGuests ? Number(olMinGuests) : undefined,
            minAmount: olMinAmt ? Number(olMinAmt) : undefined,
            maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined,
            ...olDates,
            ...orderSourceParam,
        }));
    }, [locationId, token, dateRange.startDate, dateRange.endDate, olPage, olPageSize, olSort, olSortDir, olDebouncedSearch, olOrderTypes, olChannelGroup, olMinAmt, olMaxAmt, olMinGuests, today, channel, salesRefreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Order Detail: fetch when order selected ──
    // For Today → dispatch to Redux (ClickHouse live endpoint).
    // For other dates → keep legacy reportsApi call.
    useEffect(() => {
        if (!olSelectedOrder || !locationId) { dispatch(clearOrderDetail()); return; }
        // Always use ClickHouse order-detail (orderId-based, no date params needed) for Today + historical
        const orderId = (olSelectedOrder as OrderLogItem).orderId ?? '';
        if (orderId) dispatch(fetchOrderDetailRequest({ locationId, orderId, ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }));
    }, [olSelectedOrder, locationId, data.isToday, activeDate]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setStaffPage(0); }, [data.staffSales]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setDiscountsStaffPage(0); }, [liveStaffPerformance]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setRefundsStaffPage(0); }, [liveRefundSummary]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Live Tender Data (Today / ClickHouse) ──
    const liveTenderSorted = livePayments
        .filter(p => p.tenderAmount > 0)
        .sort((a, b) => b.tenderAmount - a.tenderAmount);
    const liveTenderPcts = distribute100At2dp(liveTenderSorted.map(p => p.tenderAmount));
    const liveTenderData = liveTenderSorted.map((p, i) => ({
        type: p.tenderType,
        amount: p.tenderAmount,
        count: p.transactionCount,
        pct: liveTenderPcts[i],
    }));

    const activeTenderData = liveTenderData;
    const activeTenderLoading = livePaymentsLoading;
    const activeTenderError = livePaymentsFailure;
    const activeTenderRetry = () => dispatch(fetchLivePaymentsRequest({ locationId, ...chDatesView, ...orderSourceParam }));

    // ── Voided / Refund Summary (shared between main Refund Analysis card and drill-down view) ──
    const voidedSummaryList = listFrom(data.voidedSummary);
    const refundReasonMap: Record<string, { reason: string; amount: number; count: number }> = {};
    voidedSummaryList.forEach((d: any) => {
        const reason = d.voidedReasons ?? d.reason ?? d.voidReason ?? d.refundReason ?? d.name ?? null;
        if (!reason) return;
        if (!refundReasonMap[reason]) refundReasonMap[reason] = { reason, amount: 0, count: 0 };
        refundReasonMap[reason].amount += Number(d.voidedAmount ?? d.totalAmount ?? d.amount ?? 0) || 0;
        refundReasonMap[reason].count += Number(d.orderCount ?? d.totalOrders ?? d.count ?? 1) || 0;
    });
    const refundReasons = Object.values(refundReasonMap).sort((a, b) => b.amount - a.amount);

    // ── Third-Party Marketplace — reads from raw channelArr (before combining) so individual platforms still appear ──
    const thirdPartyData = channelArr
        .map((item: any, index: number) => ({
            name: item.channelName ?? item.channel ?? item.name ?? `Channel ${index + 1}`,
            value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
            count: Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0,
            color: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
        }))
        .filter((ch: any) => ch.value > 0 && /doordash|uber\s?eats|grubhub|postmates|delivery.*maghil|third.?party|gloriafood|swiggy|zomato|seamless/i.test(ch.name));

    // ── Order Log helpers ──
    // Always use Redux state (ClickHouse endpoint with date params)
    const activeOlOrders: OrderLogItem[] = orderLogOrders;
    const activeOlLoading = orderLogLoading;
    const activeOlTotal = orderLogTotalElements;
    const activeOlTotalPages = orderLogTotalPages;
    const activeOlError = orderLogFailure;
    const olHasFilters = olDebouncedSearch || olOrderTypes.length || olChannelGroup || olMinAmt || olMaxAmt || olMinGuests;
    const olTotalPages = activeOlTotalPages;
    const olPageStart = activeOlOrders.length > 0 ? olPage * olPageSize + 1 : 0;
    const olPageEnd = Math.min(olPage * olPageSize + activeOlOrders.length, activeOlTotal);
    const olAmtError = olMinAmt && olMaxAmt && Number(olMinAmt) >= Number(olMaxAmt)
        ? 'Min must be less than Max'
        : null;

    const fmtOrderTime = (raw: any, isToday: boolean) => {
        if (!raw) return '—';
        if (isToday && /^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(String(raw).trim())) return String(raw).trim();
        try {
            const d = new Date(raw);
            if (isNaN(d.getTime())) return String(raw);
            const h = d.getHours() % 12 || 12;
            const m = String(d.getMinutes()).padStart(2, '0');
            const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
            if (isToday) return `${h}:${m} ${ampm}`;
            const mo = d.toLocaleString('en-US', { month: 'short' });
            const dy = d.getDate();
            return `${mo} ${dy} ${h}:${m} ${ampm}`;
        } catch { return String(raw); }
    };

    const fmtPhone = (raw: any) => {
        if (!raw) return '—';
        const digits = String(raw).replace(/\D/g, '');
        if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        return String(raw);
    };

    const handleOlColumnSort = (col: string) => {
        if (olSort !== col) {
            setOlSort(col);
            setOlSortDir('desc');
        } else {
            setOlSortDir((d) => d === 'desc' ? 'asc' : 'desc');
        }
    };

    const SortIcon = ({ col }: { col: string }) => {
        if (olSort !== col) return <span style={{ color: T.t3, fontSize: 10, marginLeft: 2 }}>↕</span>;
        return <span style={{ color: T.accent, fontSize: 10, marginLeft: 2 }}>{olSortDir === 'desc' ? '↓' : '↑'}</span>;
    };

    const olClearFilters = () => {
        setOlSearch(''); setOlOrderTypes([]); setOlChannelGroup(null); setOlMinAmt(''); setOlMaxAmt(''); setOlMinGuests('');
    };

    // ── Maghil vs 3rd Party split (mirrors the mobile drill computation) ──
    const isThirdPartyCh = (name: any) => /third.?party|3p|uber|ubereats|door.?dash|grub.?hub|postmates|swiggy|zomato|deliveroo|just.?eat|talabat|foodpanda/i.test(String(name || ''));
    let maghilSales = num(pick(billedSrc, 'magilNetSales', 'maghilNetSales', 'magilSales', 'maghilSales', 'directNetSales') ?? pick(data.summary, 'magilNetSales', 'maghilNetSales', 'magilSales', 'maghilSales', 'directNetSales') ?? 0);
    let thirdPartySales = num(pick(billedSrc, 'thirdPartyNetSales', 'thirdpartySales', 'thirdPartyTotalSales') ?? pick(data.summary, 'thirdPartyNetSales', 'thirdpartySales', 'thirdPartyTotalSales') ?? 0);
    if (maghilSales === 0 && thirdPartySales === 0 && channelArr.length > 0) {
        channelArr.forEach((item: any) => {
            const chName = item.channelName ?? item.channel ?? item.name ?? '';
            const total = Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0;
            const mRow = Number(item.maghilSales ?? item.magilSales ?? item.maghil ?? item.magil ?? item.directSales ?? 0) || 0;
            const tRow = Number(item.thirdPartySales ?? item.thirdpartySales ?? item.thirdParty ?? item.thirdparty ?? 0) || 0;
            if (mRow > 0 || tRow > 0) { maghilSales += mRow; thirdPartySales += tRow; }
            else if (total > 0) { if (isThirdPartyCh(chName)) thirdPartySales += total; else maghilSales += total; }
        });
    }
    const maghilThirdTotal = maghilSales + thirdPartySales;
    const maghilPct = maghilThirdTotal > 0 ? Math.round((maghilSales / maghilThirdTotal) * 100) : 0;
    const thirdPartyPct = maghilThirdTotal > 0 ? 100 - maghilPct : 0;

    // ── Mini KPI for drill panels ──
    const MiniK = ({ label, value, color, sub, loading: isLoading }: any) => (
        <div className="rpt-card rpt-card--r12" style={{ padding: '14px 16px', flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color || T.t1, letterSpacing: -0.5 }}>
                {isLoading ? <Bone w={80} h={22} r={4} /> : value}
            </div>
            {!isLoading && sub && <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>{sub}</div>}
        </div>
    );

    // ── PBarRow for drill panels ──
    const PBarRow = ({ label, val, pct, max = 100, color }: any) => (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: T.t1 }}>{label}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{val}</span>
            </div>
            <div style={{ height: 5, background: T.hover, borderRadius: 3 }}>
                <div style={{ height: 5, borderRadius: 3, width: `${Math.min((pct / max) * 100, 100)}%`, background: color, opacity: 0.7 }} />
            </div>
        </div>
    );

    // ══════════════════════════════════════
    // Drill-Down Renderers
    // ══════════════════════════════════════

    const renderDiscountsDrill = () => {
        // Discounts drill — KPI row + Discounts-by-Staff from ClickHouse
        // staff-performance; the by-source card and Discount Log come from the
        // loyalty discount-performance / discount-log APIs inside
        // DiscountBreakdownAndLog. Identical wiring to the Snapshot report.
        const totalDiscountsPf = liveStaffPerformance.reduce((s, item) => s + (item.discount || 0), 0);
        const discOrdersPf = liveStaffPerformance.reduce((s, item) => s + (item.orderCount || 0), 0);
        const totalGrandTotalPf = liveStaffPerformance.reduce((s, item) => s + (item.grandTotal || 0), 0);
        const sortedByDiscount = [...liveStaffPerformance].filter(s => s.discount > 0).sort((a, b) => b.discount - a.discount);
        const DISC_STAFF_PAGE_SIZE = 5;
        const discTotalPages = data.isToday ? 1 : Math.ceil(sortedByDiscount.length / DISC_STAFF_PAGE_SIZE);
        const discSafePage = data.isToday ? 0 : Math.min(discountsStaffPage, Math.max(0, discTotalPages - 1));
        const discPageSlice = data.isToday
            ? sortedByDiscount.slice(0, DISC_STAFF_PAGE_SIZE)
            : sortedByDiscount.slice(discSafePage * DISC_STAFF_PAGE_SIZE, discSafePage * DISC_STAFF_PAGE_SIZE + DISC_STAFF_PAGE_SIZE);
        return (
                <div style={{ padding: 4 }}>
                    <div style={{ fontSize: 11, color: T.t3, marginBottom: 16 }}>
                        {liveStaffPerformanceLoading ? <Bone w={200} h={11} r={3} style={{ display: 'inline-block' }} /> : `${fmtCurrency(totalDiscountsPf)} total discounts · ${fmtNum(discOrdersPf)} orders · ${getFilterLabel(activeDate, dateRange.startDate, dateRange.endDate)}`}
                    </div>
                    {liveStaffPerformanceLoading ? (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            {[1, 2].map(i => (
                                <div key={i} className="rpt-card rpt-card--r12" style={{ padding: '14px 16px', flex: 1, minWidth: 100 }}>
                                    <Bone w={60} h={10} mb={8} />
                                    <Bone w={90} h={22} mb={6} />
                                    <Bone w={50} h={10} />
                                </div>
                            ))}
                        </div>
                    ) : liveStaffPerformanceFailure ? (
                        <SectionError kpiName="discounts" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={80} />
                    ) : (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                            <MiniK label="Total Discounts" value={fmtCurrency(totalDiscountsPf)} sub={totalGrandTotalPf > 0 ? `${((totalDiscountsPf / totalGrandTotalPf) * 100).toFixed(1)}% of Gross` : undefined} color={T.orange} />
                            <MiniK label="Disc. Orders" value={fmtNum(discOrdersPf)} sub={totalOrders > 0 ? `${((discOrdersPf / totalOrders) * 100).toFixed(1)}% of all` : undefined} />
                        </div>
                    )}
                    <DiscountBreakdownAndLog
                        sym={getCurrencySymbol()}
                        startISO={data.isToday ? today : (dateRange.startDate || today)}
                        endISO={data.isToday ? today : (dateRange.endDate || today)}
                        outletId={null}
                        locationId={locationId}
                        isToday={data.isToday}
                        orderSource={orderSourceVal || undefined}
                        token={token}
                        onOpenOrder={(r) => setOlSelectedOrder({ orderId: r.orderId, orderNo: r.orderNo })}
                    >
                    <div className="rpt-card" style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discounts by Staff</div>
                        <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>{data.isToday ? 'Staff members who applied discounts today' : 'Staff discount breakdown'}</div>
                        {liveStaffPerformanceLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                                        <Bone w={20} h={20} r={6} />
                                        <div style={{ flex: 1 }}><Bone w={100} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                        <Bone w={55} h={16} />
                                    </div>
                                ))}
                            </div>
                        ) : liveStaffPerformanceFailure ? (
                            <SectionError kpiName="discounts by staff" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={160} />
                        ) : sortedByDiscount.length ? (
                            <>
                                {discPageSlice.map((s: LiveStaffPerformanceItem, i: number) => {
                                    const globalRank = discSafePage * DISC_STAFF_PAGE_SIZE + i;
                                    const pct = totalDiscountsPf > 0 ? ((s.discount / totalDiscountsPf) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < discPageSlice.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                            <div style={{ width: 22, height: 22, borderRadius: 6, background: globalRank === 0 ? T.orangeSoft : T.hover, color: globalRank === 0 ? T.orange : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{globalRank + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{s.orderCount} orders · {pct}%</div>
                                            </div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: T.orange, flexShrink: 0 }}>{fmtCurrency(s.discount)}</div>
                                        </div>
                                    );
                                })}
                                {!data.isToday && discTotalPages > 1 && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 4 }}>
                                        <button onClick={() => setDiscountsStaffPage((p) => Math.max(0, p - 1))} disabled={discSafePage === 0} style={{ fontSize: 11, color: discSafePage === 0 ? T.t3 : T.accent, background: 'none', border: `1px solid ${discSafePage === 0 ? T.divider : T.accent}`, borderRadius: 4, cursor: discSafePage === 0 ? 'default' : 'pointer', padding: '2px 8px' }}>← Prev</button>
                                        <span style={{ fontSize: 10, color: T.t3 }}>{discSafePage + 1} / {discTotalPages}</span>
                                        <button onClick={() => setDiscountsStaffPage((p) => Math.min(discTotalPages - 1, p + 1))} disabled={discSafePage === discTotalPages - 1} style={{ fontSize: 11, color: discSafePage === discTotalPages - 1 ? T.t3 : T.accent, background: 'none', border: `1px solid ${discSafePage === discTotalPages - 1 ? T.divider : T.accent}`, borderRadius: 4, cursor: discSafePage === discTotalPages - 1 ? 'default' : 'pointer', padding: '2px 8px' }}>Next →</button>
                                    </div>
                                )}
                                {sortedByDiscount.length > 0 && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                        <span>{sortedByDiscount.length} staff</span>
                                        <span>Total: <span style={{ color: T.orange, fontWeight: 600 }}>{fmtCurrency(totalDiscountsPf)}</span></span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <SectionEmpty kpiName="staff discounts" minHeight={160} />
                        )}
                    </div>
                    </DiscountBreakdownAndLog>
                </div>
        );
    };

    const renderRefundsDrill = () => {
        const maxRefund = refundReasons[0]?.amount || 1;
        const maxRefundCancelledAmt = liveRefundSummary.reduce((m, item) => Math.max(m, item.cancelledAmt || 0), 1);
        const liveAvgRefund = liveRefundOrderCount > 0 ? liveCancelledAmt / liveRefundOrderCount : 0;
        const liveTopRefundReason: LiveRefundSummaryItem | null = liveRefundSummary.reduce<LiveRefundSummaryItem | null>(
            (top, item) => ((item.cancelledAmt || 0) > (top?.cancelledAmt || 0) ? item : top),
            null,
        );
        const liveTopRefundPct = liveTopRefundReason && liveCancelledAmt > 0
            ? ((liveTopRefundReason.cancelledAmt || 0) / liveCancelledAmt) * 100
            : 0;

        const sortedStaffRefunds = [...liveStaffRefunds].sort((a, b) => b.refund - a.refund);
        const topRefunder: LiveStaffRefundsItem | null = sortedStaffRefunds[0] ?? null;
        const totalStaffRefundAmt = liveStaffRefunds.reduce((s, item) => s + (item.refund || 0), 0);
        const abbreviateName = (name: string) => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            return `${parts[0]} ${parts[parts.length - 1][0]}.`;
        };

        return (
            <div style={{ padding: 4 }}>
                <div style={{ fontSize: 11, color: T.t3, marginBottom: 16 }}>
                    {liveRefundSummaryLoading ? <Bone w={110} h={11} r={3} style={{ display: 'inline-block' }} /> : `${fmtNum(liveRefundOrderCount)} refund orders`}
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <MiniK label="Total Refunds" value={liveRefundSummaryFailure ? fmtCurrency(0) : fmtCurrency(liveCancelledAmt)} color={T.red} loading={liveRefundSummaryLoading} />
                    <MiniK label="Count" value={liveRefundSummaryFailure ? '0' : fmtNum(liveRefundOrderCount)} loading={liveRefundSummaryLoading} />
                    <MiniK label="Avg Refund" value={liveRefundSummaryFailure ? fmtCurrency(0) : liveRefundOrderCount > 0 ? fmtCurrency(liveAvgRefund) : fmtCurrency(0)} loading={liveRefundSummaryLoading} />
                    <MiniK
                        label="#1 Reason"
                        value={liveRefundSummaryFailure ? '--' : liveTopRefundReason ? liveTopRefundReason.cancelReason : '--'}
                        color={liveTopRefundReason ? T.red : undefined}
                        sub={liveTopRefundReason ? `${fmtCurrency(liveTopRefundReason.cancelledAmt)} · ${liveTopRefundPct.toFixed(1)}%` : undefined}
                        loading={liveRefundSummaryLoading}
                    />
                    <MiniK
                        label="Top Refunder"
                        value={liveStaffRefundsFailure ? '--' : topRefunder ? abbreviateName(topRefunder.staff_name) : '--'}
                        color={topRefunder ? T.red : undefined}
                        sub={topRefunder ? `${fmtCurrency(topRefunder.refund)} · ${topRefunder.orderCount} refunds` : undefined}
                        loading={liveStaffRefundsLoading}
                    />
                </div>
                <div className="rpt-flex-row rpt-gap-16">
                    <div className="rpt-card" style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Refunds by Reason</div>
                        <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>Click to see staff split</div>
                        {liveRefundSummaryLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[1, 2, 3].map(i => <div key={i}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><Bone w={110} h={12} /><Bone w={50} h={12} /></div><Bone h={4} r={2} /></div>)}
                            </div>
                        ) : liveRefundSummaryFailure ? (
                            <SectionError kpiName="refunds by reason" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={160} />
                        ) : liveRefundSummary.length ? (
                            liveRefundSummary.map((item: LiveRefundSummaryItem, i: number) => (
                                <PBarRow key={i} label={item.cancelReason} val={fmtCurrency(item.cancelledAmt)} pct={item.cancelledAmt} max={maxRefundCancelledAmt} color={T.red} />
                            ))
                        ) : (
                            <SectionEmpty kpiName="refunds by reason" minHeight={160} />
                        )}
                    </div>
                    <div className="rpt-card" style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Refunds by Staff</div>
                        <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>Staff members who processed refunds</div>
                        {liveStaffRefundsLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                                            <Bone w={20} h={20} r={6} />
                                            <div style={{ flex: 1 }}><Bone w={100} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                            <Bone w={40} h={16} />
                                        </div>
                                    ))}
                                </div>
                            ) : liveStaffRefundsFailure ? (
                                <SectionError kpiName="refunds by staff" onRetry={() => dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={160} />
                            ) : sortedStaffRefunds.length ? (
                                <>
                                    {sortedStaffRefunds.slice(0, 5).map((s: LiveStaffRefundsItem, i: number) => {
                                        const refundRate = totalStaffRefundAmt > 0 ? (s.refund / totalStaffRefundAmt) * 100 : 0;
                                        return (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < Math.min(sortedStaffRefunds.length, 5) - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                                <div style={{ width: 22, height: 22, borderRadius: 6, background: i === 0 ? `${T.red}20` : T.hover, color: i === 0 ? T.red : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                    <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{s.orderCount} refunds · {refundRate.toFixed(1)}% rate</div>
                                                </div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: T.red, flexShrink: 0 }}>{fmtCurrency(s.refund)}</div>
                                            </div>
                                        );
                                    })}
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                        <span>{liveStaffRefunds.length} staff</span>
                                        <span>Total: <span style={{ color: T.red, fontWeight: 600 }}>{fmtCurrency(totalStaffRefundAmt)}</span></span>
                                    </div>
                                </>
                            ) : (
                                <SectionEmpty kpiName="refunds by staff" minHeight={160} />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ZAR-1762 / TG-2 — "Tips & Gratuity" drill-in. Replaces the old Tips-only drill
    // (Tips by Channel + Tip Leaderboard) with the shared two-section layout, so Sales,
    // Snapshot and EOD present the same reconciliation view.
    const renderTipsDrill = () => (
        <div style={{ padding: 4 }}>
            <TipsGratuityDrill
                data={tipsGratuityDrillData}
                fmt={fmtCurrency}
                loading={snapshotDetailsLoading || liveStaffPerformanceLoading}
                staffFailure={liveStaffPerformanceFailure}
                onRetryStaff={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                channelLoading={liveByChannelLoading}
                channelFailure={liveByChannelFailure}
                onRetryChannel={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
            />
        </div>
    );

    // ── Net Sales drill (ZAR-1462) ── mini-KPI row + shared sales-breakdown waterfall.
    const renderNetSalesDrill = () => {
        const waterfallSource: WaterfallSource = orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall';
        // Gross Sales is summed inside buildWaterfallData; `grossSales` here is the
        // "Total Collected" stamp KPI value (grandTotal) and feeds the ladder's end row.
        const wfData = buildWaterfallData({
            voids: liveVoidSummary.reduce((s, i) => s + (Number(i.cancelledAmt) || 0), 0),
            discounts: totalDiscounts,
            complimentary: liveCompOrderLog.reduce((s, o) => s + (Number(o.amount) || 0), 0),
            refunds: liveCancelledAmt,
            netSales,
            tax: totalTax,
            tips,
            gratuity,
            otherCharges,
            totalCollected: grossSales,
        });
        const isTp = waterfallSource === 'thirdparty';
        const primaryLabel = isTp ? 'Item Total' : 'Net Sales';
        const primaryValue = isTp ? wfData.itemTotal : wfData.netSales;
        const peakIdx = hourlyData.length ? hourlyData.reduce((mi, d, i, arr) => d.value > arr[mi].value ? i : mi, 0) : -1;
        const hasPeak = peakIdx >= 0 && hourlyData[peakIdx].value > 0;
        const peakLbl = hasPeak ? expandHourLabel(hourlyData[peakIdx].label) : '--';
        // Revenue booked in the peak hour — the hour alone doesn't say how big it was.
        const peakSub = !hasPeak || hourlySalesFailure ? undefined : fmtCurrency(hourlyData[peakIdx].value);
        // SW 10: the waterfall's Voids/Complimentary/Refunds rows come from three separate
        // async fetches on top of snapshot-details — snapshotDetailsLoading alone drops before
        // they land, so the bars would render stale/zero values then jump once each resolves.
        const waterfallLoading = snapshotDetailsLoading || liveVoidSummaryLoading || liveCompOrderLogLoading || liveRefundSummaryLoading;
        return (
            <div style={{ padding: 4 }}>
                <div style={{ fontSize: 11, color: T.t3, marginBottom: 16 }}>{snapshotDetailsLoading ? <Bone w={200} h={11} r={3} style={{ display: 'inline-block' }} /> : `${fmtCurrency(primaryValue)} ${isTp ? 'item total' : 'total revenue'} · ${getFilterLabel(activeDate, dateRange.startDate, dateRange.endDate)}`}</div>
                <div ref={netSalesKpiRowRef} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <MiniK label={primaryLabel} value={fmtCurrency(primaryValue)} color={T.green} loading={snapshotDetailsLoading} />
                    <MiniK label={getTotalPaidLabel(waterfallSource)} value={fmtCurrency(getTotalPaidValue(waterfallSource, wfData))} sub={getTotalPaidSub(waterfallSource)} loading={snapshotDetailsLoading} />
                    <MiniK label="Peak Hour" value={hourlySalesFailure ? '--' : peakLbl} sub={peakSub} color={T.accent} loading={hourlySalesLoading} />
                    <MiniK label="Avg/Hour" value={fmtCurrency(hourlyAvg)} loading={hourlySalesLoading} />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <SalesWaterfall
                        source={waterfallSource}
                        data={wfData}
                        fmt={fmtCurrency}
                        loading={waterfallLoading}
                        // SW3 — Sales has its own Discounts/Refunds drills, but no Voids/Complimentary
                        // KPI of its own; those route to Snapshot's respective drilldown instead.
                        onDeductionClick={(key) => {
                            if (key === 'discounts' || key === 'refunds') setDrillView(key);
                            else navigate(`/reports/snapshot?drill=${key}`, { state: { returnTo: '/reports/sales?drill=net-sales' } });
                        }}
                    />
                </div>
                <div className="rpt-card">
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.t1, marginBottom: 2 }}>Hourly Revenue</div>
                    <div style={{ fontSize: 12, color: T.t3, marginBottom: 10 }}>Revenue distribution across service hours</div>
                    <HourlyBarChart
                        data={hourlyData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                        kpiName="hourly revenue"
                        barColor={T.accent}
                        chartHeight={120}
                        showCurrency={true}
                        avgOverride={hourlyAvg}
                    />
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════
    // Main Desktop Render
    // ══════════════════════════════════════

    const drillMap: Record<string, () => React.ReactNode> = {
        'net-sales': renderNetSalesDrill,
        discounts: renderDiscountsDrill,
        refunds: renderRefundsDrill,
        tips: renderTipsDrill,
    };
    if (drillView && drillMap[drillView]) {
        return (
            // Distinct key: prevents React reusing this drill DOM for the main page on back
            // (position-matched reuse left the Net Sales stamp KPI with drill geometry — SSE-6).
            <div className="rpt-page" key={`sales-drill-${drillView}`}>
                {error ? (
                    <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.16)', borderRadius: 12, padding: '12px 16px', color: T.red, fontSize: 12, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => setSalesRefreshNonce(n => n + 1)}
                            style={{ background: 'none', border: `1px solid ${T.red}4D`, borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: T.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Try again
                        </button>
                    </div>
                ) : null}
                {drillMap[drillView]()}
                <OrderDetailSlider
                    visible={!!olSelectedOrder}
                    onClose={() => { setOlSelectedOrder(null); setOlOrderDetail(null); dispatch(clearOrderDetail()); }}
                    orderNo={(olSelectedOrder as OrderLogItem)?.orderNo}
                    orderType={(olSelectedOrder as OrderLogItem)?.orderType}
                    loading={orderDetailLoading}
                    error={orderDetailError}
                    onRetry={() => dispatch(fetchOrderDetailRequest({ locationId, orderId: (olSelectedOrder as OrderLogItem).orderId, ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }))}
                    detail={orderDetailData}
                    legacyDetail={undefined}
                />
            </div>
        );
    }

    const kpiLoading = snapshotDetailsLoading;
    const deltaLoading = kpiLoading || snapshotComparisonLoading;
    const outstandingKpiLoading = liveOutstandingValueLoading;

    return (
        <div className="rpt-page" key="sales-main">
            {error ? (
                <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.16)', borderRadius: 12, padding: '12px 16px', color: T.red, fontSize: 12, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span>{error}</span>
                    <button
                        type="button"
                        onClick={() => setSalesRefreshNonce(n => n + 1)}
                        style={{ background: 'none', border: `1px solid ${T.red}4D`, borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: T.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                        Try again
                    </button>
                </div>
            ) : null}

            {/* KPI Row 1 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>Total Collected<KpiTooltip text="Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees" /></div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(grossSales)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : grossSalesPct != null && (
                        <DeltaBadge pct={grossSalesPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                <div style={{ background: T.card, borderRadius: 14, padding: '20px 22px', border: `1px solid ${T.accent}33`, boxShadow: T.cardShadow, flex: 1, minWidth: 160, cursor: 'pointer', position: 'relative', transition: 'all 0.15s' }} onClick={() => setDrillView('net-sales')}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>Net Sales<KpiTooltip text="From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions." /><span style={{ marginLeft: 'auto' }}><DrillArrow /></span></div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(netSales)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : netSalesPct != null && (
                    <DeltaBadge pct={netSalesPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Total Orders</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6 }}>{fmtNum(totalOrders)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : orderPct != null && (
                        <DeltaBadge pct={orderPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Avg Order Value</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(avgOrderValue)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : avgOrderPct != null && (
                        <DeltaBadge pct={avgOrderPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
            </div>

            {/* KPI Row 2 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Total Tax</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(totalTax)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : taxPct != null && (
                        <DeltaBadge pct={taxPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                <div style={{ background: T.card, borderRadius: 14, padding: '20px 22px', border: `1px solid ${T.accent}33`, boxShadow: T.cardShadow, flex: 1, minWidth: 160, cursor: 'pointer', position: 'relative', transition: 'all 0.15s' }} onClick={() => setDrillView('discounts')}>
                    <DrillArrow />
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Total Discounts</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(totalDiscounts)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : discountsPct != null && (
                    <DeltaBadge pct={discountsPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                <div style={{ background: T.card, borderRadius: 14, padding: '20px 22px', border: `1px solid ${T.accent}33`, boxShadow: T.cardShadow, flex: 1, minWidth: 160, cursor: 'pointer', position: 'relative', transition: 'all 0.15s' }} onClick={() => setDrillView('refunds')}>
                    <DrillArrow />
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Refunds</div>
                    {liveRefundSummaryLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>
                        {liveRefundSummaryFailure ? fmtCurrencyKpi(0) : fmtCurrencyKpi(liveCancelledAmt)}
                    </div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : refundsPct != null && (
                    <DeltaBadge pct={refundsPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} invert />
                    ))}
                </div>
                {/* ZAR-1762 / TG-1 — this single card replaces the pre-existing separate Tips and
                    Gratuity cards. Combined total + combined delta only; the split moved to the
                    drill-in (TG-2). */}
                <div style={{ background: T.card, borderRadius: 14, padding: '20px 22px', border: `1px solid ${T.accent}33`, boxShadow: T.cardShadow, flex: 1, minWidth: 160, cursor: 'pointer', position: 'relative', transition: 'all 0.15s' }} onClick={() => setDrillView('tips')}>
                    <DrillArrow />
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{TG_CARD_LABEL}</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(tipsAndGratuity)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : tipsGratuityPct != null && (
                        <DeltaBadge pct={tipsGratuityPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
            </div>

            {/* KPI Row 3 */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Other</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(otherCharges)}</div>}
                    {/* {otherChargesPct != null ? (
                        <div style={{ fontSize: 12, fontWeight: 600, color: otherChargesPct >= 0 ? T.green : T.red, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <span>{otherChargesPct >= 0 ? '↑' : '↓'}</span>{fmtPct(otherChargesPct)}
                        </div>
                    ) : <div style={{ fontSize: 12, fontWeight: 600, color: T.t3 }}>--</div>} */}
                </div>
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>Outstanding Amount<KpiTooltip text="Amount customers owes you (ie., Difference between order total and paid amount)" /></div>
                    {outstandingKpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{fmtCurrencyKpi(outstandingAmount)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : outstandingPct != null && (
                        <DeltaBadge pct={outstandingPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
                {/* SPG-1: Spend/Guest — last stamp KPI in the grid. Shows "—" (not $0.00) when
                    there are no guests in the active period/source. */}
                <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Spend / Guest</div>
                    {kpiLoading ? <Bone w={90} h={28} mb={6} /> : <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginBottom: 6, overflowWrap: 'anywhere' }}>{spendPerGuest == null ? '—' : fmtCurrencyKpi(spendPerGuest)}</div>}
                    {showDelta && (deltaLoading ? <Bone w={80} h={14} r={4} /> : spendPerGuestPct != null && (
                        <DeltaBadge pct={spendPerGuestPct} theme={T} comparedTo={comparisonTooltip(activeDate, storeTz)} />
                    ))}
                </div>
            </div>

            {/* Charts Row: Sales by Hour + Sales by Channel (always — Today: ClickHouse Redux; Historical: by-channel API) */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'flex-start' }}>
                <div className="rpt-card" style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Sales by Hour</div>
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Revenue distribution across service hours</div>
                    </div>
                    <HourlyBarChart
                        data={hourlyData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                        kpiName="sales by hour"
                        barColor={T.accent}
                        chartHeight={210}
                        formatValue={(v) => fmtCompact(v)}
                        showCurrency={true}
                        currencySymbol={getCurrencySymbol()}
                        minHeight={200}
                        avgOverride={hourlyAvg}
                    />
                </div>

                <div className="rpt-card" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Sales by Channel</div>
                            <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Revenue share across order channels</div>
                        </div>
                        {liveChFiltered.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="desktop"
                                title="Sales by Channel"
                                filename="sales-by-channel"
                                buildExport={async (format, opts) => {
                                    const cc = branch?.country ?? branch?.countryCode ?? branch?.countryCd;
                                    const money = (n: any) => formatMoneyForExport(n);
                                    const count = (n: any) => format === 'pdf' ? formatGroupedNumber(n, cc, { maximumFractionDigits: 0 }) : n;
                                    let chSource: LiveByChannelItem[] = liveChFiltered;
                                    if (opts?.locationId) {
                                        const res: any = await getLiveByChannelApi({ locationId: opts.locationId, ...chDatesView, orderSource: orderSourceVal, token });
                                        const arr: LiveByChannelItem[] = Array.isArray(res) ? res : (res?.data ?? []);
                                        chSource = arr.filter((item) => item.grandTotal > 0);
                                    }
                                    const sortedCh = [...chSource].sort((a, b) => b.grandTotal - a.grandTotal);
                                    const chTotal = sortedCh.reduce((s, item) => s + item.grandTotal, 0);
                                    const totalOrders = sortedCh.reduce((s, item) => s + (Number(item.orderCount) || 0), 0);
                                    const pcts = computeDisplayPcts(sortedCh.map((item) => item.grandTotal));
                                    return {
                                        totalRow: true,
                                        headers: ['Channel', 'Orders', 'Revenue', '%'],
                                        rows: [
                                            ...sortedCh.map((item: any, i: number) => [item.channel, count(item.orderCount ?? '—'), money(item.grandTotal), chTotal > 0 ? `${pcts[i]}%` : '—']),
                                            ['Total', count(totalOrders), money(chTotal), chTotal > 0 ? '100.00%' : '—'],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    <DoughnutChart
                        data={liveChDoughnutData}
                        loading={liveByChannelLoading}
                        error={liveByChannelFailure}
                        onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                        kpiName="sales by channel"
                        centerLabel="Total"
                        centerValue={fmtCompact(liveChTotal)}
                        formatValue={(v) => fmtCurrency(v)}
                        chartDiameter={160}
                        minHeight={240}
                    />
                </div>
            </div>

            {/* Row: Sales by Category + Sales by Tender Type */}
            {/* SSE-21: alignItems keeps each card's height independent (default flex stretch matched them) */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'flex-start' }}>
                <div className="rpt-card" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Sales by Category</div>
                            <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Top menu categories by revenue</div>
                        </div>
                        {liveCategorySalesTotalElements > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="desktop"
                                title="Sales by Category"
                                filename="sales-by-category"
                                buildExport={async (format, opts) => {
                                    // Fetch the whole set (all pages) at export time, like other sections.
                                    const loc = opts?.locationId ?? locationId;
                                    let size = liveCategorySalesTotalElements || 1;
                                    if (opts?.locationId) {
                                        // Current outlet's known total doesn't apply to the other outlet — probe first.
                                        const probe: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size: 1, startDate: chDatesView.startDate, endDate: chDatesView.endDate, orderSource: orderSourceVal, token });
                                        size = (Array.isArray(probe) ? probe.length : (probe?.totalElements ?? probe?.total ?? 1)) || 1;
                                    }
                                    const res: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size, startDate: chDatesView.startDate, endDate: chDatesView.endDate, orderSource: orderSourceVal, token });
                                    const cats: LiveCategorySalesItem[] = Array.isArray(res) ? res : (res?.categories ?? res?.data ?? []);
                                    // Every cell carries its own currency symbol now (formatMoneyForExport),
                                    // so this column is text in every format — no more raw-number CSV branch.
                                    const money = (n: number) => formatMoneyForExport(n);
                                    const totalQty = cats.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
                                    const totalValue = cats.reduce((s, c) => s + (Number(c.price) || 0), 0);
                                    return {
                                        totalRow: true,
                                        headers: ['Serial No.', 'Category Name', 'Total Sales Quantity', 'Total Sales Value'],
                                        rows: [
                                            ...cats.map((item, i) => [i + 1, item.categoryName, fmtExportQty(item.quantity), money(item.price)]),
                                            ['', 'Total', fmtExportQty(totalQty), money(totalValue)],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    {liveCategorySalesLoading ? (
                        <div style={{ paddingTop: 4 }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={120} h={12} />
                                        <Bone w={56} h={12} />
                                    </div>
                                    <Bone h={6} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : liveCategorySalesFailure ? (
                        <SectionError kpiName="sales by category" onRetry={() => dispatch(fetchLiveCategorySalesRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal, catPage + 1, catReqSize))} minHeight={200} />
                    ) : liveCategorySales.length ? (() => {
                        // Scale bars by the global max revenue (all pages) so a category's bar
                        // length stays consistent as you page through.
                        const maxCatPrice = Math.max(...(catAllItems.length ? catAllItems : liveCategorySales).map(item => item.price), 1);
                        return (
                            <>
                                {liveCategorySales.map((item: LiveCategorySalesItem, i: number) => (
                                    <div key={i} style={{ marginBottom: i < liveCategorySales.length - 1 ? 10 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{item.categoryName}</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 70, textAlign: 'right' }}>{fmtCurrency(item.price)}</span>
                                        </div>
                                        <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: 6, borderRadius: 3, width: `${(item.price / maxCatPrice) * 100}%`, background: '#db7172' }} />
                                        </div>
                                    </div>
                                ))}
                                {/* Running total reflects ALL categories, not just this page. */}
                                <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                    <span>{liveCategorySalesTotalElements} categories</span>
                                    <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{fmtCurrency(catGrandTotal)}</span></span>
                                </div>
                                {/* Records-per-page + pagination controls */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}`, gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 11, color: T.t3 }}>Records per page:</span>
                                        <select
                                            value={String(catPageSize)}
                                            onChange={e => setCatPageSize(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                            style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, cursor: 'pointer', fontFamily: T.font }}
                                        >
                                            {CAT_PAGE_SIZE_OPTIONS.map(n => <option key={String(n)} value={String(n)}>{n}</option>)}
                                        </select>
                                    </div>
                                    {catPageSize !== 'All' && liveCategorySalesTotalPages > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <button onClick={() => setCatPage(p => Math.max(0, p - 1))} disabled={catPage === 0} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catPage === 0 ? T.hover : T.surface, color: catPage === 0 ? T.t3 : T.t1, cursor: catPage === 0 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>‹ Prev</button>
                                            <span style={{ fontSize: 11, color: T.t3 }}>Page {catPage + 1} of {liveCategorySalesTotalPages}</span>
                                            <button onClick={() => setCatPage(p => Math.min(liveCategorySalesTotalPages - 1, p + 1))} disabled={catPage >= liveCategorySalesTotalPages - 1} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catPage >= liveCategorySalesTotalPages - 1 ? T.hover : T.surface, color: catPage >= liveCategorySalesTotalPages - 1 ? T.t3 : T.t1, cursor: catPage >= liveCategorySalesTotalPages - 1 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>Next ›</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })() : (
                        <SectionEmpty kpiName="sales by category" minHeight={200} />
                    )}
                </div>

                <div className="rpt-card" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Sales by Tender Type</div>
                            <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Payment method breakdown</div>
                        </div>
                        {activeTenderData.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="desktop"
                                title="Sales by Tender Type"
                                filename="sales-by-tender-type"
                                buildExport={async (_format, opts) => {
                                    let tenderRows = activeTenderData;
                                    if (opts?.locationId) {
                                        const res: any = await getLivePaymentsApi({ locationId: opts.locationId, ...chDatesView, orderSource: orderSourceVal, token });
                                        const items: LivePaymentsItem[] = Array.isArray(res) ? res : (res?.data ?? []);
                                        const sorted = items.filter(p => p.tenderAmount > 0).sort((a, b) => b.tenderAmount - a.tenderAmount);
                                        const pcts = distribute100At2dp(sorted.map(p => p.tenderAmount));
                                        tenderRows = sorted.map((p, i) => ({ type: p.tenderType, amount: p.tenderAmount, count: p.transactionCount, pct: pcts[i] }));
                                    }
                                    return {
                                        headers: ['Type', 'Transactions', 'Amount', 'Share'],
                                        rows: tenderRows.map((t: any) => [t.type, t.count, formatMoneyForExport(t.amount), `${t.pct}%`]),
                                    };
                                }}
                            />
                        )}
                    </div>
                    {activeTenderError ? (
                        <SectionError kpiName="sales by tender type" onRetry={activeTenderRetry} minHeight={200} />
                    ) : activeTenderLoading ? (
                        <div style={{ paddingTop: 8 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.6fr 1.2fr', padding: '10px 0', borderBottom: i < 3 ? `1px solid ${T.divider}` : 'none' }}>
                                    <Bone w={80} h={12} />
                                    <Bone w={24} h={12} />
                                    <Bone w={56} h={12} />
                                    <Bone w={32} h={12} />
                                </div>
                            ))}
                        </div>
                    ) : activeTenderData.length ? (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.6fr 1.2fr', padding: '0 0 8px', borderBottom: `1px solid ${T.divider}` }}>
                                {['Type', 'Transactions', 'Amount', 'Share'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: (h === 'Transactions' || h === 'Amount' || h === 'Share') ? 'right' : 'left' }}>{h}</div>)}
                            </div>
                            {activeTenderData.map((t: any, i: number, arr: any[]) => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.6fr 1.2fr', padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{t.type}</div>
                                    <div style={{ fontSize: 12, color: T.t2, textAlign: 'right' }}>{fmtNum(t.count)}</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t1, textAlign: 'right' }}>{fmtCurrency(t.amount)}</div>
                                    <div style={{ fontSize: 12, color: T.t3, textAlign: 'right' }}>{t.pct}%</div>
                                </div>
                            ))}
                        </>
                    ) : <SectionEmpty kpiName="sales by tender type" minHeight={200} />}
                </div>
            </div>

            {/* Row: Discount Breakdown + Refund Analysis */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                {/* Discount Breakdown — commented out
                <div className="rpt-card" style={{ flex: 1 }}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Discount Breakdown</div>
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Performance by offer</div>
                    </div>
                    {discountOffers.length ? (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 1.2fr', padding: '0 0 8px', borderBottom: `1px solid ${T.divider}` }}>
                                {['Offer', 'Orders', 'Discount'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>)}
                            </div>
                            {discountOffers.map((d: any, i: number) => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 1.2fr', padding: '10px 0', borderBottom: i < discountOffers.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{d.name}</div>
                                    <div style={{ fontSize: 12, color: T.t2 }}>{fmtNum(d.count || 0)}</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.orange }}>{fmtCurrency(d.amount)}</div>
                                </div>
                            ))}
                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, fontSize: 11, color: T.t3 }}>
                                Discount as % of Net: <span style={{ color: T.orange, fontWeight: 600 }}>{netSales > 0 ? `${((totalDiscounts / netSales) * 100).toFixed(1)}%` : '--'}</span>
                            </div>
                        </>
                    ) : <div style={{ fontSize: 12, color: T.t3 }}>No discount data</div>}
                </div>
                */}

                <div className="rpt-card" style={{ flex: 1 }}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Refund Analysis</div>
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Top reasons by amount</div>
                    </div>
                    {liveRefundSummaryLoading ? (
                        <div style={{ paddingTop: 8 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={120} h={12} />
                                        <Bone w={32} h={12} />
                                    </div>
                                    <Bone h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : liveRefundSummaryFailure ? (
                        <SectionError kpiName="refund analysis" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={160} />
                    ) : liveRefundSummary.length ? (() => {
                        const maxCancelledAmt = liveRefundSummary.reduce((m, item) => Math.max(m, item.cancelledAmt || 0), 1);
                        const totalRefundCount = liveRefundSummary.reduce((sum, item) => sum + item.orderCount, 0);
                        const totalCancelledAmt = liveRefundSummary.reduce((sum, item) => sum + (item.cancelledAmt || 0), 0);
                        const totalSales = Number(snapshotDetails?.grandTotal ?? 0);
                        const refundRate = totalSales > 0 ? (totalCancelledAmt / totalSales) * 100 : null;
                        return (
                            <>
                                {liveRefundSummary.map((item: LiveRefundSummaryItem, i: number) => (
                                    <PBarRow key={i} label={item.cancelReason} val={fmtCurrency(item.cancelledAmt)} pct={item.cancelledAmt} max={maxCancelledAmt} color={T.red} />
                                ))}
                                <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: T.t3 }}>
                                    <span>{fmtNum(totalRefundCount)} refunds</span>
                                    <span>Refund Rate: <span style={{ color: T.red, fontWeight: 600 }}>{refundRate != null ? `${refundRate.toFixed(1)}%` : '--'}</span></span>
                                </div>
                            </>
                        );
                    })() : (
                        <SectionEmpty kpiName="refund analysis" minHeight={160} />
                    )}
                </div>
            </div>

            {/* Sales by Channel — Financial Breakdown (ZAR-1555, replaces Third-Party Marketplace Performance) */}
            <SalesByChannelBreakdown
                data={salesByChannelBreakdown.groups}
                totals={salesByChannelBreakdown.totals}
                loading={salesByChannelBreakdown.loading}
                error={salesByChannelBreakdown.error}
                onRetry={salesByChannelBreakdown.retry}
                outletName={(branch as any)?.name}
                dateRange={{ startDate: dateRange.startDate, endDate: dateRange.endDate, isToday: dateRange.startDate === today, orderSource: orderSourceVal }}
                dateRangeLabel={exportDateRangeLabel}
                onChannelDrill={(ch: SbcChannelRow) => {
                    // Filter the Order Log by the row's sales channel (`channelGroup`).
                    // Any Order Type (`channelName`) selection is cleared so the two
                    // filters can never contradict each other.
                    setOlOrderTypes([]);
                    setOlChannelGroup(ch.channelGroup ?? null);
                    requestAnimationFrame(() => document.getElementById('sales-order-log')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                }}
            />

            {/* ─── Staff Sales Leaderboard ─── */}
            {staffLeaderboard.length > 0 && (() => {
                const PAGE_SIZE = 5;
                const totalPages = Math.ceil(staffLeaderboard.length / PAGE_SIZE);
                const safePage = Math.min(staffPage, totalPages - 1);
                const pageSlice = staffLeaderboard.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
                return (
                    <div className="rpt-card" style={{ marginBottom: 24 }}>
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Staff Sales Leaderboard</div>
                            <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Revenue by staff member</div>
                        </div>
                        {pageSlice.map((s: any, i: number) => {
                            const globalRank = safePage * PAGE_SIZE + i;
                            return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < pageSlice.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                    <div style={{ width: 24, height: 24, borderRadius: 6, background: globalRank === 0 ? T.accentSoft : T.hover, color: globalRank === 0 ? T.accent : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{globalRank + 1}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                                        {s.role && <div style={{ fontSize: 10, color: T.t3 }}>{s.role}</div>}
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{fmtCurrency(s.sales)}</div>
                                        <div style={{ fontSize: 10, color: T.t3 }}>{fmtNum(s.orders)} orders</div>
                                    </div>
                                </div>
                            );
                        })}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                                <button onClick={() => setStaffPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} style={{ fontSize: 12, color: safePage === 0 ? T.t3 : T.accent, background: 'none', border: `1px solid ${safePage === 0 ? T.divider : T.accent}`, borderRadius: 4, cursor: safePage === 0 ? 'default' : 'pointer', padding: '4px 10px' }}>← Prev</button>
                                <span style={{ fontSize: 11, color: T.t3 }}>{safePage + 1} / {totalPages}</span>
                                <button onClick={() => setStaffPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1} style={{ fontSize: 12, color: safePage === totalPages - 1 ? T.t3 : T.accent, background: 'none', border: `1px solid ${safePage === totalPages - 1 ? T.divider : T.accent}`, borderRadius: 4, cursor: safePage === totalPages - 1 ? 'default' : 'pointer', padding: '4px 10px' }}>Next →</button>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* ─── Order Log (SALES-7 / SALES-8) ─── */}
            <div id="sales-order-log" className="rpt-card" style={{ marginBottom: 24 }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Order Log (Paid Orders)</div>
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>All orders for the selected period.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {activeOlOrders.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                title="Order Log (Paid Orders)"
                                filename={`order-log-paid-orders-${dateRange.startDate}`}
                                buildExport={async (format, opts) => {
                                    const loc = opts?.locationId ?? locationId;
                                    const dateParams = dateRange.startDate !== today ? { startDate: dateRange.startDate, endDate: dateRange.endDate } : {};
                                    let size = orderLogTotalElements || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getOrderLogExportApi({ locationId: loc, page: 1, size: 1, sortBy: olSort, sortDir: olSortDir, search: olDebouncedSearch || undefined, channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined, channelGroup: olChannelGroup ?? undefined, guestCount: olMinGuests ? Number(olMinGuests) : undefined, minAmount: olMinAmt ? Number(olMinAmt) : undefined, maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined, ...dateParams, ...orderSourceParam });
                                        size = Number(probe?.totalElements) || 1;
                                    }
                                    const res: any = await getOrderLogExportApi({ locationId: loc, page: 1, size, sortBy: olSort, sortDir: olSortDir, search: olDebouncedSearch || undefined, channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined, channelGroup: olChannelGroup ?? undefined, guestCount: olMinGuests ? Number(olMinGuests) : undefined, minAmount: olMinAmt ? Number(olMinAmt) : undefined, maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined, ...dateParams, ...orderSourceParam });
                                    const orders = res?.orders ?? (Array.isArray(res) ? res : []);
                                    return orderLogToExportTable(orders, getCurrencySymbol(), format);
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Filter row */}
                <div style={{ display: 'flex', gap: 8, marginBottom: olAmtError ? 4 : 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Search */}
                    <div style={{ position: 'relative', flex: '1 1 240px' }}>
                        <input
                            value={olSearch}
                            onChange={e => setOlSearch(e.target.value)}
                            placeholder="Search by order ID, customer name, or phone"
                            style={{ width: '100%', height: 32, padding: olSearch ? '0 28px 0 10px' : '0 10px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font, boxSizing: 'border-box' }}
                        />
                        {olSearch && (
                            <button
                                onClick={() => setOlSearch('')}
                                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: T.t3 }}
                                aria-label="Clear search"
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                            </button>
                        )}
                    </div>
                    {/* Filters */}
                    <>
                        {/* Order Type multi-select — options from API */}
                        {olTypeMenuOpen && (
                            <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setOlTypeMenuOpen(false)} />
                        )}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setOlTypeMenuOpen(v => !v)}
                                style={{ height: 32, padding: '0 10px', border: `1px solid ${olOrderTypes.length ? T.accent : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: olOrderTypes.length ? T.accent : T.t2, background: olOrderTypes.length ? `${T.accent}10` : T.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontFamily: T.font }}
                            >
                                Order Type {olOrderTypes.length > 0 ? `(${olOrderTypes.length})` : ''}
                                <span style={{ fontSize: 10 }}>▾</span>
                            </button>
                            {olTypeMenuOpen && (
                                <div style={{ position: 'absolute', top: 36, left: 0, zIndex: 200, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow, padding: '6px 0', minWidth: 180 }}>
                                    {(channelNames.length > 0 ? channelNames : []).map(ot => (
                                        <div
                                            key={ot}
                                            onClick={() => setOlOrderTypes(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])}
                                            style={{ padding: '7px 14px', fontSize: 12, color: olOrderTypes.includes(ot) ? T.accent : T.t1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: olOrderTypes.includes(ot) ? `${T.accent}08` : 'transparent' }}
                                        >
                                            <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${olOrderTypes.includes(ot) ? T.accent : T.t3}`, background: olOrderTypes.includes(ot) ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {olOrderTypes.includes(ot) && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                                            </span>
                                            {ot}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Amount range — no negatives, min < max */}
                        <input
                            value={olMinAmt}
                            onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMinAmt(v); }}
                            placeholder="Min $"
                            type="number"
                            min="0"
                            style={{ width: 72, height: 32, padding: '0 8px', border: `1px solid ${olAmtError ? T.red : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }}
                        />
                        <input
                            value={olMaxAmt}
                            onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMaxAmt(v); }}
                            placeholder="Max $"
                            type="number"
                            min="0"
                            style={{ width: 72, height: 32, padding: '0 8px', border: `1px solid ${olAmtError ? T.red : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }}
                        />
                        {/* Guests — no negatives */}
                        <input
                            value={olMinGuests}
                            onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 1) setOlMinGuests(v); }}
                            placeholder="Guests ≥"
                            type="number"
                            min="1"
                            style={{ width: 76, height: 32, padding: '0 8px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }}
                        />
                        {/* Clear filters */}
                        {olHasFilters && (
                            <button onClick={olClearFilters} style={{ height: 32, padding: '0 10px', border: 'none', borderRadius: 6, fontSize: 12, color: T.accent, background: 'transparent', cursor: 'pointer', fontFamily: T.font, textDecoration: 'underline' }}>
                                Clear filters
                            </button>
                        )}
                    </>
                </div>
                {/* Amount validation error */}
                {olAmtError && (
                    <div style={{ fontSize: 11, color: T.red, marginBottom: 6 }}>{olAmtError}</div>
                )}

                {/* Showing + Rows per page */}
                {activeOlTotal > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 11, color: T.t3 }}>
                            {activeOlLoading ? <Bone w={140} h={11} r={3} style={{ display: 'inline-block' }} /> : `Showing ${olPageStart}–${olPageEnd} of ${activeOlTotal} orders`}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>Rows per page:</span>
                            <select
                                value={olPageSize}
                                onChange={e => setOlPageSize(Number(e.target.value))}
                                style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, cursor: 'pointer', fontFamily: T.font }}
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    </div>
                )}

                {/* Selected type / channel chips */}
                {(olOrderTypes.length > 0 || olChannelGroup) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {olChannelGroup && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${T.accent}12`, borderRadius: 12, fontSize: 11, color: T.accent, fontWeight: 500 }}>
                                Channel: {channelGroupLabel(olChannelGroup)}
                                <span onClick={() => setOlChannelGroup(null)} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                            </span>
                        )}
                        {olOrderTypes.map(ot => (
                            <span key={ot} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${T.accent}12`, borderRadius: 12, fontSize: 11, color: T.accent, fontWeight: 500 }}>
                                {ot}
                                <span onClick={() => setOlOrderTypes(prev => prev.filter(x => x !== ot))} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Table / loading / error / empty */}
                {activeOlLoading ? (
                    <div>
                        {/* Column header skeleton */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.4fr 1.2fr 1.3fr 1.2fr 0.6fr 2fr 1fr', gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                            {['Order No', 'Date', 'Time', 'Order Type', 'Customer', 'Phone', 'Guests', 'Items', 'Amount'].map(h => (
                                <div key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                            ))}
                        </div>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.4fr 1.2fr 1.3fr 1.2fr 0.6fr 2fr 1fr', gap: 8, padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
                                <Bone w={60} h={12} />
                                <Bone w={72} h={12} />
                                <Bone w={80} h={12} />
                                <Bone w={70} h={12} />
                                <Bone w={90} h={12} />
                                <Bone w={80} h={12} />
                                <Bone w={20} h={12} />
                                <Bone w={120} h={12} />
                                <Bone w={55} h={12} />
                            </div>
                        ))}
                    </div>
                ) : activeOlError ? (
                    <SectionError kpiName="Order Log" onRetry={() => dispatch(fetchOrderLogRequest({ locationId, page: olPage + 1, size: olPageSize, sortBy: olSort, sortDir: olSortDir, search: olDebouncedSearch || undefined, ...orderSourceParam }))} />
                ) : activeOlOrders.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 32, color: T.t3, display: 'block', marginBottom: 8 }}>receipt_long</span>
                        <div style={{ fontSize: 13, color: T.t3 }}>
                            {olHasFilters ? 'No orders found for the selected filters.' : 'No orders found for the selected period.'}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Column headers */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.4fr 1.2fr 1.3fr 1.2fr 0.6fr 2fr 1fr', gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 2 }}>
                            {[
                                { label: 'Order No', col: null },
                                { label: 'Date', col: null },
                                { label: 'Time', col: 'orderTime' },
                                { label: 'Order Type', col: 'orderType' },
                                { label: 'Customer', col: 'customerName' },
                                { label: 'Phone', col: null, isPhone: true },
                                { label: 'Guests', col: 'guests' },
                                { label: 'Items', col: null },
                                { label: 'Amount', col: 'amount', rightAlign: true },
                            ].map(({ label, col, isPhone, rightAlign }: any) => (
                                <div
                                    key={label}
                                    onClick={() => col && handleOlColumnSort(col)}
                                    style={{ fontSize: 10, fontWeight: 600, color: col && olSort === col ? T.accent : T.t3, textTransform: 'uppercase', letterSpacing: 0.4, cursor: col ? 'pointer' : 'default', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4, justifyContent: rightAlign ? 'flex-end' : undefined }}
                                >
                                    {label}{col && <SortIcon col={col} />}
                                    {isPhone && (
                                        <span
                                            className="material-symbols-rounded"
                                            onClick={(e) => { e.stopPropagation(); setOlPhoneVisible(v => !v); }}
                                            style={{ fontSize: 13, color: T.t3, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                                        >
                                            {olPhoneVisible ? 'visibility' : 'visibility_off'}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Rows */}
                        {activeOlOrders.map((order: OrderLogItem, i: number) => {
                            const orderId = order.orderNo ?? '—';
                            const orderDate = order.orderDate ?? '—';
                            const orderTime = fmtOrderTime(order.orderTime, data.isToday);
                            const orderType = order.orderType ?? '—';
                            const customerName = order.customerName || null;
                            const phone = fmtPhone(order.customerPhone ?? null);
                            const phoneDisplay = (() => {
                                if (phone === '—') return '—';
                                if (olPhoneVisible) return phone;
                                const last4 = phone.slice(-4);
                                return `(XXX) XXX-${last4}`;
                            })();
                            const isOffPremise = /delivery|doordash|uber|pickup|catering/i.test(String(orderType));
                            const guestsDisplay = order.guests ? String(order.guests) : '—';
                            const itemsDisplay = order.itemPreview || '—';
                            const amount = Number(order.amount ?? 0);

                            return (
                                <div
                                    key={`${orderId}-${i}`}
                                    onClick={() => setOlSelectedOrder(order)}
                                    style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.4fr 1.2fr 1.3fr 1.2fr 0.6fr 2fr 1fr', gap: 8, padding: '10px 0', borderBottom: i < activeOlOrders.length - 1 ? `1px solid ${T.divider}` : 'none', cursor: 'pointer', borderRadius: 4, transition: 'background 0.1s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = T.hover)}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <div title={orderId} style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{orderId}</div>
                                    <div style={{ fontSize: 12, color: T.t2 }}>{orderDate}</div>
                                    <div style={{ fontSize: 12, color: T.t2 }}>{orderTime}</div>
                                    <div style={{ fontSize: 12, color: T.t1 }}>{orderType}</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customerName ?? 'Walk-in'}</div>
                                    <div style={{ fontSize: 12, color: T.t2 }}>{phoneDisplay}</div>
                                    <div style={{ fontSize: 12, color: T.t2 }}>{guestsDisplay}</div>
                                    <div style={{ fontSize: 12, color: T.t2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={itemsDisplay}>{itemsDisplay}</div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, textAlign: 'right' }}>{fmtCurrencyCell(amount)}</div>
                                </div>
                            );
                        })}

                        {/* Pagination — always shown when there are orders */}
                        {activeOlOrders.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
                                <span style={{ fontSize: 11, color: T.t3 }}>
                                    Page {olPage + 1} of {olTotalPages || 1}
                                </span>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button onClick={() => setOlPage(p => Math.max(0, p - 1))} disabled={olPage === 0} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: olPage === 0 ? T.hover : T.surface, color: olPage === 0 ? T.t3 : T.t1, cursor: olPage === 0 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>‹ Prev</button>
                                    {Array.from({ length: Math.min(olTotalPages || 1, 7) }, (_, idx) => {
                                        const total = olTotalPages || 1;
                                        const pg = total <= 7 ? idx : (olPage < 4 ? idx : (olPage > total - 4 ? total - 7 + idx : olPage - 3 + idx));
                                        return (
                                            <button key={pg} onClick={() => setOlPage(pg)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${pg === olPage ? T.accent : T.cardBorder}`, background: pg === olPage ? T.accent : T.surface, color: pg === olPage ? '#fff' : T.t2, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: T.font }}>{pg + 1}</button>
                                        );
                                    })}
                                    <button onClick={() => setOlPage(p => Math.min((olTotalPages || 1) - 1, p + 1))} disabled={olPage >= (olTotalPages || 1) - 1} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: olPage >= (olTotalPages || 1) - 1 ? T.hover : T.surface, color: olPage >= (olTotalPages || 1) - 1 ? T.t3 : T.t1, cursor: olPage >= (olTotalPages || 1) - 1 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>Next ›</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ─── Order Detail Slide-Over (SALES-9) ─── */}
            <OrderDetailSlider
                visible={!!olSelectedOrder}
                onClose={() => { setOlSelectedOrder(null); setOlOrderDetail(null); dispatch(clearOrderDetail()); }}
                orderNo={(olSelectedOrder as OrderLogItem)?.orderNo}
                orderType={(olSelectedOrder as OrderLogItem)?.orderType}
                loading={orderDetailLoading}
                error={orderDetailError}
                onRetry={() => dispatch(fetchOrderDetailRequest({ locationId, orderId: (olSelectedOrder as OrderLogItem).orderId, ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }))}
                detail={orderDetailData}
                legacyDetail={undefined}
            />
        </div>
    );
};

// =========================
// Mobile implementation
// =========================

const findTokenMobile = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'bearerToken', 'authToken', 'userToken', 'sessionToken', 'Authorization', 'authorization', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findTokenMobile(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationIdMobile = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationIdMobile(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrapResMobile = (res: any) => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const pickMobile = (res: any, ...keys: any[]) => {
    if (res == null) return null;
    const r = unwrapResMobile(res);
    for (const key of keys) {
        const v = r?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const raw = Array.isArray(r) ? r : (r?.content ?? r?.data ?? r);
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    for (const key of keys) {
        const v = obj[key];
        if (v !== undefined && v !== null) return v;
    }
    for (const [k, v] of Object.entries(obj)) {
        if (keys.some((key) => String(k).toLowerCase().includes(String(key).toLowerCase())) && (typeof v === 'number' || (typeof v === 'string' && /^[\d.,]+$/.test(v)))) return v;
    }
    return null;
};

const listFromMobile = (res: any) => {
    const r = unwrapResMobile(res);
    let raw = r?.content ?? r?.data ?? r?.items ?? r?.records ?? r?.categories ?? r?.channelList ?? r?.employees ?? r?.staff ?? r?.staffList ?? r?.staffTips ?? r?.tipDetails ?? r?.discounts ?? r?.orders ?? (Array.isArray(r) ? r : null);
    if (!Array.isArray(raw) && r && typeof r === 'object') {
        for (const val of Object.values(r)) {
            if (Array.isArray(val)) {
                raw = val;
                break;
            }
        }
    }
    return Array.isArray(raw) ? raw : [];
};

const fmtCurrencyMobile = (v: any) => {
    const sym = getCurrencySymbol();
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Table-cell amount (mobile): whole numbers drop decimals, fractional keep 2.
// Preserves the K/M abbreviation behaviour of fmtCurrencyMobile for large values.
const fmtCurrencyCellMobile = (v: any) => {
    const sym = getCurrencySymbol();
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return Number.isInteger(n)
        ? `${sym}${n.toLocaleString('en-US')}`
        : `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtNumMobile = (v: any) => {
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 100_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString('en-US');
};

const pctChangeMobile = (curr: any, prev: any) => {
    const c = Number(curr);
    const p = Number(prev);
    if (p === 0 || !Number.isFinite(c) || !Number.isFinite(p)) return null;
    return ((c - p) / p) * 100;
};

const MobileSalesScreen = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationIdMobile(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findTokenMobile(auth) : null), [auth]);
    const dispatch = useAppDispatch();
    const snapshotDetails = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess) as SnapshotDetailsResponse | null;
    console.log('[SalesScreen][Mobile] /sales/live/v2/snapshot-details', snapshotDetails);
    const livePaymentsM = useAppSelector((s) => s.clickHouse.livePaymentsSuccess) as LivePaymentsItem[];
    const livePaymentsLoadingM = useAppSelector((s) => s.clickHouse.livePaymentsLoading);
    const livePaymentsFailureM = useAppSelector((s) => s.clickHouse.livePaymentsFailure);
    const liveRefundSummaryM = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess) as LiveRefundSummaryItem[];
    const liveRefundSummaryLoadingM = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const liveRefundSummaryFailureM = useAppSelector((s) => s.clickHouse.liveRefundSummaryFailure);
    // Void + Comp — feed the Net Sales drill waterfall (ZAR-1462), same source as the Snapshot stamp KPIs.
    const liveVoidSummaryM = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess) as { cancelledAmt: number }[];
    const liveVoidSummaryLoadingM = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
    const liveCompOrderLogM = useAppSelector((s) => s.clickHouse.liveCompOrderLog) as { amount: number }[];
    const liveCompOrderLogLoadingM = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
    const liveHourlySalesM = useAppSelector((s) => s.clickHouse.liveHourlySalesSuccess) as LiveHourlySalesItem[];
    const liveHourlySalesLoadingM = useAppSelector((s) => s.clickHouse.liveHourlySalesLoading);
    const liveHourlySalesFailureM = useAppSelector((s) => s.clickHouse.liveHourlySalesFailure);
    const hourlySalesDataM = useAppSelector((s) => s.clickHouse.hourlySalesData) as HourlySalesResponse | null;
    const hourlySalesLoadingM = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailureM = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const liveByChannelM = useAppSelector((s) => s.clickHouse.liveByChannelSuccess) as LiveByChannelItem[];
    const liveByChannelLoadingM = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailureM = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    const liveCategorySalesM = useAppSelector((s) => s.clickHouse.liveCategorySalesSuccess) as LiveCategorySalesItem[];
    const liveCategorySalesLoadingM = useAppSelector((s) => s.clickHouse.liveCategorySalesLoading);
    const liveCategorySalesFailureM = useAppSelector((s) => s.clickHouse.liveCategorySalesFailure);
    const liveCategorySalesTotalElementsM = useAppSelector((s) => s.clickHouse.liveCategorySalesTotalElements);
    const liveCategorySalesTotalPagesM = useAppSelector((s) => s.clickHouse.liveCategorySalesTotalPages);
    const liveStaffPerformanceM = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess) as LiveStaffPerformanceItem[];
    const liveStaffPerformanceLoadingM = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailureM = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveStaffRefundsM = useAppSelector((s) => s.clickHouse.liveStaffRefundsSuccess) as LiveStaffRefundsItem[];
    const liveStaffRefundsLoadingM = useAppSelector((s) => s.clickHouse.liveStaffRefundsLoading);
    const liveStaffRefundsFailureM = useAppSelector((s) => s.clickHouse.liveStaffRefundsFailure);
    const snapshotComparisonM = useAppSelector((s) => s.clickHouse.snapshotComparisonSuccess) as SnapshotComparisonResponse | null;
    const snapshotDetailsLoadingM = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    const snapshotComparisonLoadingM = useAppSelector((s) => s.clickHouse.snapshotComparisonLoading);
    const liveOutstandingValueM = useAppSelector((s) => s.clickHouse.liveOutstandingValue);
    const liveOutstandingValueLoadingM = useAppSelector((s) => s.clickHouse.liveOutstandingValueLoading);
    const liveItemSalesV2M = useAppSelector((s) => s.clickHouse.liveItemSalesV2Success) as LiveItemSalesV2Item[];

    // Order Log (Today / ClickHouse live)
    const orderLogOrdersM = useAppSelector((s) => s.clickHouse.orderLogOrders) as OrderLogItem[];
    const orderLogLoadingM = useAppSelector((s) => s.clickHouse.orderLogLoading);
    const orderLogFailureM = useAppSelector((s) => s.clickHouse.orderLogFailure);
    const orderLogTotalElementsM = useAppSelector((s) => s.clickHouse.orderLogTotalElements);
    const orderLogTotalPagesM = useAppSelector((s) => s.clickHouse.orderLogTotalPages);
    const exportOrderLogLoadingM = useAppSelector((s) => s.clickHouse.exportOrderLogLoading);

    // Channel Names for Order Type dropdown
    const channelNamesM = useAppSelector((s) => s.clickHouse.channelNames);

    // Order Detail slider (Today / ClickHouse live)
    const orderDetailDataM = useAppSelector((s) => s.clickHouse.orderDetailData) as OrderDetailResponse | null;
    const orderDetailLoadingM = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailErrorM = useAppSelector((s) => s.clickHouse.orderDetailError);

    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [activeDateFilter, setActiveDateFilter] = useSharedReportDateFilter('Today');
    // Global sales-channel filter — per-report, defaults to Overall.
    const [channel, setChannel] = useState('Overall sales');
    const orderSourceVal = channelToOrderSource(channel);
    const orderSourceParam: { orderSource?: 'direct' | 'thirdparty' } = orderSourceVal ? { orderSource: orderSourceVal } : {};
    const isThirdParty = orderSourceVal === 'thirdparty';

    const dateRange = useMemo(() => {
        if (activeDateFilter === 'Custom') {
            return { startDate: selectedDate, endDate: selectedDate };
        }
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday, storeTz);
        return range ?? { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday, storeTz]);

    // Human-readable active date range for the "All outlets" export file header.
    const exportDateRangeLabel = exportPeriodFor(activeDateFilter, dateRange)?.label ?? activeDateFilter;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>(null);
    const [voidedSummaryErrorM, setVoidedSummaryErrorM] = useState(false);
    // SW3 — deep-link into a specific drill via ?drill=<key> (currently only used to reopen
    // 'net-sales' when the user backs out of a cross-navigated Snapshot drill opened from here).
    const drillFromUrlRefM = useRef(false);
    const [drillView, setDrillView] = useState<string | null>(() => {
        const raw = searchParams.get('drill');
        if (raw) drillFromUrlRefM.current = true;
        return raw;
    });
    // Scroll the KPI row into view when landing here via the deep link, same as desktop — see
    // its netSalesKpiRowRef comment.
    const netSalesKpiRowRefM = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (drillView !== 'net-sales' || !drillFromUrlRefM.current) return;
        requestAnimationFrame(() => netSalesKpiRowRefM.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }, [drillView]);
    const [staffPageM, setStaffPageM] = useState(0);
    const [netSalesStaffPageM, setNetSalesStaffPageM] = useState(0);
    const [discountsStaffPageM, setDiscountsStaffPageM] = useState(0);
    const [refundsStaffPageM, setRefundsStaffPageM] = useState(0);
    const [cancelReasonsItemsM, setCancelReasonsItemsM] = useState<any[]>([]);
    const [cancelReasonsLoadingM, setCancelReasonsLoadingM] = useState(false);
    const [cancelReasonsFailureM, setCancelReasonsFailureM] = useState(false);
    const [cancelReasonsReloadM, setCancelReasonsReloadM] = useState(0);
    const cancelReasonsFetchKeyRefM = useRef<string | null>(null);
    const goBack = useCallback(() => setDrillView(null), []);
    useDrillHistoryBack(!!drillView, goBack);
    useSwipeBack();
    const fetchIdRefMobile = useRef(0);
    const snapshotFetchKeyRefM = useRef<string | null>(null);
    const thirdPartyFetchKeyRefM = useRef<string | null>(null);
    const kpiExportRefM = useRef<Record<string, any>>({});
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [salesRefreshedTimeM, setSalesRefreshedTimeM] = useState<string | null>(() => localStorage.getItem(SALES_REFRESH_TS_KEY));
    const recordSalesRefreshedTimeM = useCallback(() => {
        const ts = new Date().toISOString();
        localStorage.setItem(SALES_REFRESH_TS_KEY, ts);
        setSalesRefreshedTimeM(ts);
    }, []);
    const handlePtrRefresh = useCallback(() => {
        setRefreshNonce(n => n + 1);
        recordSalesRefreshedTimeM();
    }, [recordSalesRefreshedTimeM]);
    const [isSalesSpinningM, setIsSalesSpinningM] = useState(false);
    const handleSalesManualRefreshM = useCallback(() => {
        setRefreshNonce(n => n + 1);
        recordSalesRefreshedTimeM();
        setIsSalesSpinningM(true);
    }, [recordSalesRefreshedTimeM]);
    const channelNamesKeyRefM = useRef<string | null>(null);
    const orderLogKeyRefM = useRef<string | null>(null);
    // ── Mobile Order Log state ──
    const [molPage, setMolPage] = useState(0);
    const [molSort, setMolSort] = useState('Time (newest first)');
    const [molSearch, setMolSearch] = useState('');
    const [molDebouncedSearch, setMolDebouncedSearch] = useState('');
    const [molOrderTypes, setMolOrderTypes] = useState<string[]>([]);
    // Set by the Sales by Channel — Financial Breakdown drill-down; sent as the
    // `channelGroup` query param (NOT `channelName`, which filters order types).
    const [molChannelGroup, setMolChannelGroup] = useState<OrderChannelGroup | null>(null);
    const [molMinAmt, setMolMinAmt] = useState('');
    const [molMaxAmt, setMolMaxAmt] = useState('');
    const [molMinGuests, setMolMinGuests] = useState('');
    const [molOrders, setMolOrders] = useState<any[]>([]);
    const [molTotal, setMolTotal] = useState(0);
    const [molLoading, setMolLoading] = useState(false);
    const [molFiltersOpen, setMolFiltersOpen] = useState(false);
    const [molSelectedOrder, setMolSelectedOrder] = useState<any>(null);
    const [molOrderDetail, setMolOrderDetail] = useState<any>(null);
    const [expandedMolItems, setExpandedMolItems] = useState<Set<number>>(new Set());
    const [molDetailLoading, setMolDetailLoading] = useState(false);

    // ── Sales by Category pagination (ZAR-1223) — mirrors the desktop card ──
    const [catPageM, setCatPageM] = useState(0);
    const [catPageSizeM, setCatPageSizeM] = useState<number | 'All'>(5);
    const catReqSizeM = catPageSizeM === 'All' ? CAT_ALL_SIZE : catPageSizeM;
    const [catAllItemsM, setCatAllItemsM] = useState<LiveCategorySalesItem[]>([]);
    const catAllKeyRefM = useRef<string | null>(null);
    const catGrandTotalM = useMemo(() => catAllItemsM.reduce((s, it) => s + (it.price || 0), 0), [catAllItemsM]);

    // Hardware / swipe back closes the Order Log slider before navigating away
    const closeMolSlider = useCallback(() => {
        setMolSelectedOrder(null);
        setMolOrderDetail(null);
        dispatch(clearOrderDetail());
    }, [dispatch]);
    useDrillHistoryBack(!!molSelectedOrder, closeMolSlider);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh, disabled: !!molSelectedOrder });

    const [data, setData] = useState<any>(() => ({
        summary: null,
        channel: null,
        hourly: null,
        category: null,
        discounts: null,
        staffSales: null,
        staffTips: null,
        topItems: null,
        payments: null,
        voidedSummary: null,
        liveBilledOrders: null,
        liveNetSales: null,
        liveItemSales: null,
        isToday: getInitialReportDateFilter('Today') === 'Today',
    }));

    // ClickHouse date params for drill retries — omitted for Today (live path), passed for historical.
    const chDatesView: { startDate?: string; endDate?: string } = data.isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };

    // Sales by Channel — Financial Breakdown (ZAR-1555)
    const salesByChannelBreakdown = useSalesByChannelBreakdown({
        locationId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        isToday: dateRange.startDate === today,
        orderSource: orderSourceVal,
        refreshNonce,
    });

    const handleDateFilterChange = useCallback((opt: any) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    const buildSalesKpiExportM = useCallback(async (_format?: ExportFormat, opts?: { locationId?: string }) => {
        let k: Record<string, any> = kpiExportRefM.current;
        if (opts?.locationId) {
            const loc = opts.locationId;
            const { chDates, orderSourceVal: os, token: tok } = kpiExportRefM.current;
            // ZAR-1762 — Tips & Grat now comes straight off snapshot-details' tipAndGratuity,
            // so this refetch no longer needs the staff-performance call it only summed tips from.
            const [snap, refundRes, outstandingRes]: any[] = await Promise.all([
                getSnapshotDetails({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveRefundSummaryApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveOutstandingValueApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
            ]);
            const refundItems: any[] = Array.isArray(refundRes) ? refundRes : (refundRes?.data ?? []);
            const netSalesOther = num(snap?.netSales);
            const totalOrdersOther = num(snap?.orderCount);
            k = {
                ...kpiExportRefM.current,
                grossSales: num(snap?.grandTotal),
                netSales: netSalesOther,
                totalOrders: totalOrdersOther,
                avgOrderValue: totalOrdersOther > 0 ? netSalesOther / totalOrdersOther : 0,
                spendPerGuest: num(snap?.guestCount) > 0 ? num(snap?.spendPerGuest) : null,
                totalTax: num(snap?.tax),
                totalDiscounts: num(snap?.discount),
                refunds: parseFloat(refundItems.reduce((s: number, item: any) => s + (item.cancelledAmt || 0), 0).toFixed(2)),
                // ZAR-1762 / TG-1 — export mirrors the on-screen card: the backend's own combined figure.
                tipsAndGratuity: num(snap?.tipAndGratuity),
                otherCharges: num(snap?.otherCharges),
                outstandingAmount: num(outstandingRes?.outStandingAmt),
            };
        }
        const sym = getCurrencySymbol();
        const fmtMoney = (v: number) => `${sym}${formatMonetaryExport(v)}`;
        const rows: ExportCell[][] = [
            ['From', fmtExportDate(k.dateRange?.startDate)],
            ['To', fmtExportDate(k.dateRange?.endDate)],
            // GLOBAL-DP-4: the export is read away from the date control that states the
            // basis, so the comparison period travels with the file.
            ['Comparison From', fmtExportDate(comparisonRangeFor(k.dateRange?.startDate, k.dateRange?.endDate)?.comparisonStart)],
            ['Comparison To', fmtExportDate(comparisonRangeFor(k.dateRange?.startDate, k.dateRange?.endDate)?.comparisonEnd)],
            ['Channel Filter', k.channel],
            ['Total Collected', fmtMoney(num(k.grossSales))],
            ['Net Sales', fmtMoney(num(k.netSales))],
            ['Total Orders', fmtNumMobile(k.totalOrders)],
            ['Avg Order Value', fmtMoney(num(k.avgOrderValue))],
            ['Total Tax', fmtMoney(num(k.totalTax))],
            ['Total Discounts', fmtMoney(num(k.totalDiscounts))],
            ['Refunds', fmtMoney(num(k.refunds))],
            ['Tips & Grat', fmtMoney(num(k.tipsAndGratuity))],
        ];
        rows.push(['Other', fmtMoney(num(k.otherCharges))]);
        rows.push(['Outstanding Amount', fmtMoney(num(k.outstandingAmount))]);
        // SPG-1: Spend/Guest — last stamp KPI on screen, so last in the export too.
        rows.push(['Spend / Guest', k.spendPerGuest == null ? '—' : fmtMoney(num(k.spendPerGuest))]);
        return { headers: ['KPI', 'Value'], rows };
    }, []);

    useEffect(() => {
        const drillTitles: Record<string, string> = { 'net-sales': 'Net Sales', discounts: 'Discounts', refunds: 'Refunds', tips: TG_DRILL_TITLE };
        setTopBarProps({
            title: drillView ? (drillTitles[drillView] ?? 'Sales') : 'Sales',
            dateFilters: drillView ? undefined : { value: activeDateFilter, onChange: handleDateFilterChange },
            sourceFilters: { options: CHANNEL_OPTIONS, value: channel, onChange: setChannel },
            onBack: drillView ? goBack : undefined,
            outletRightAddon: !drillView ? (
                <>
                    {(false && activeDateFilter === 'Today') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.t2, fontSize: 10, flexShrink: 0 }}>
                            {salesRefreshedTimeM && <span>As of {formatSalesRefreshedTime(salesRefreshedTimeM)}</span>}
                            <button type="button" onClick={handleSalesManualRefreshM} aria-label="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                                <span className="material-symbols-rounded" onAnimationEnd={() => setIsSalesSpinningM(false)} style={{ fontSize: 15, color: isSalesSpinningM ? 'var(--accent)' : C.t2, display: 'inline-block', animation: isSalesSpinningM ? 'spin 0.5s linear' : 'none' }}>refresh</span>
                            </button>
                        </div>
                    ) : null}
                    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                        title="Sales KPIs"
                        filename={kpiExportFilename('sales-kpis', dateRange.startDate, dateRange.endDate, orderSourceVal)}
                        buildExport={buildSalesKpiExportM}
                        variant="mobile"
                        iconOnly
                    />
                </>
            ) : undefined,
        });
    }, [setTopBarProps, activeDateFilter, channel, handleDateFilterChange, drillView, goBack, salesRefreshedTimeM, isSalesSpinningM, handleSalesManualRefreshM, buildSalesKpiExportM, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    useEffect(() => {
        if (!locationId || !token) {
            setLoading(false);
            return;
        }

        const id = ++fetchIdRefMobile.current;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        console.log('[SalesScreen][Mobile] fetch opts =>', { startDate: opts.startDate, endDate: opts.endDate, isToday });

        setLoading(true);
        setError(null);
        setVoidedSummaryErrorM(false);

        // Third-Party Marketplace (unfiltered by-channel) — data is channel-independent,
        // so only refetch on location/date/refresh changes, not when the global filter changes
        const tpFetchKey = `${locationId}-${dateRange.startDate}-${dateRange.endDate}-${refreshNonce}`;
        if (thirdPartyFetchKeyRefM.current !== tpFetchKey) {
            thirdPartyFetchKeyRefM.current = tpFetchKey;
            dispatch(fetchThirdPartyByChannelRequest({ locationId, ...chDates }));
        }

        if (isToday) {
            const fetchKey = `${locationId}-${dateRange.startDate}-${refreshNonce}-${channel}`;
            if (snapshotFetchKeyRefM.current !== fetchKey) {
                snapshotFetchKeyRefM.current = fetchKey;
                recordSalesRefreshedTimeM();
                dispatch(fetchSnapshotDetailsRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchSnapshotComparisonRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveRefundSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                // Void + Comp — back the Net Sales drill waterfall (ZAR-1462).
                dispatch(fetchLiveVoidSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: WF_COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
                dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
                dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCategorySalesRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveItemSalesV2Request(locationId, undefined, undefined, undefined, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveOutstandingValueRequest(locationId, undefined, undefined, orderSourceVal));
            }
            setData((prev: any) => ({
                ...prev,
                summary: null,
                channel: null,
                hourly: null,
                category: null,
                discounts: null,
                staffSales: null,
                staffTips: null,
                topItems: null,
                payments: null,
                voidedSummary: null,
                liveBilledOrders: null,
                liveNetSales: null,
                liveItemSales: null,
                isToday: true,
            }));
            setLoading(false);
        } else {
            // Historical: SAME ClickHouse endpoints as Today, just with startDate/endDate.
            snapshotFetchKeyRefM.current = null;
            setData((prev: any) => ({
                ...prev,
                summary: null, channel: null, hourly: null, category: null,
                discounts: null, staffSales: null, staffTips: null, topItems: null,
                payments: null, voidedSummary: null,
                liveBilledOrders: null, liveNetSales: null, liveItemSales: null,
                isToday: false,
            }));

            dispatch(fetchSnapshotDetailsRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchSnapshotComparisonRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveRefundSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            // Void + Comp — back the Net Sales drill waterfall (ZAR-1462).
            dispatch(fetchLiveVoidSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: WF_COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCategorySalesRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveItemSalesV2Request(locationId, 0, 10, undefined, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveOutstandingValueRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            // Staff Sales Leaderboard — historical-only section (no ClickHouse equivalent yet)
            getStaffSales(opts)
                .then((r: any) => { if (id === fetchIdRefMobile.current) setData((prev: any) => ({ ...prev, staffSales: r })); })
                .catch(() => { if (id === fetchIdRefMobile.current) setData((prev: any) => ({ ...prev, staffSales: null })); });
            setLoading(false);
        }
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, refreshNonce]);

    // ── Sales by Category — server-paginated view (ZAR-1223), mobile ──
    useEffect(() => { setCatPageM(0); }, [locationId, dateRange.startDate, dateRange.endDate, orderSourceVal, catPageSizeM, refreshNonce]);
    useEffect(() => {
        if (!locationId || !token) return;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const start = isToday ? undefined : dateRange.startDate;
        const end = isToday ? undefined : dateRange.endDate;
        dispatch(fetchLiveCategorySalesRequest(locationId, start, end, orderSourceVal, catPageM + 1, catReqSizeM));
    }, [dispatch, locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, catPageM, catReqSizeM, refreshNonce]);
    useEffect(() => {
        if (!locationId || !token) return;
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
        const dates = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const key = `${locationId}|${dateRange.startDate}|${dateRange.endDate}|${orderSourceVal}|${refreshNonce}`;
        if (catAllKeyRefM.current === key) return;
        catAllKeyRefM.current = key;
        let cancelled = false;
        getLiveCategorySalesApi({ locationId, page: 1, size: CAT_ALL_SIZE, ...dates, orderSource: orderSourceVal, token })
            .then((res: any) => { if (!cancelled) setCatAllItemsM(Array.isArray(res) ? res : (res?.categories ?? res?.data ?? [])); })
            .catch(() => { if (!cancelled) setCatAllItemsM([]); });
        return () => { cancelled = true; };
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, orderSourceVal, refreshNonce]);

    // ── Derive mobile KPI values (unified: snapshotDetails primary, billedSrc fallback) ──
    const billedSrcM = unwrapResMobile(data.liveBilledOrders) ?? unwrapResMobile(data.liveNetSales) ?? data.summary;

    const grossSalesM = Number(snapshotDetails?.grandTotal ?? 0) || (Number(pickMobile(billedSrcM, 'grossSales', 'totalGrossSales', 'totalSales', 'totalGrossSalesIncludingThirdparty', 'magilSales', 'maghilSales') ?? pickMobile(data.summary, 'grossSales', 'totalGrossSales', 'totalGrossSalesIncludingThirdparty', 'magilSales') ?? 0) || 0);
    const grossSalesPctM = (snapshotComparisonM?.grandTotal != null ? Number(snapshotComparisonM.grandTotal) : null) ?? (Number(pickMobile(billedSrcM, 'totalSalesPercentage', 'magilSalesPercentage', 'grossSalesChangePercent') ?? pickMobile(data.summary, 'totalSalesPercentage', 'magilSalesPercentage', 'grossSalesChangePercent') ?? 0) || null);

    const netSalesM = Number(snapshotDetails?.netSales ?? 0) || (Number(pickMobile(billedSrcM, 'totalNetSales', 'netSales', 'totalSales', 'revenue') ?? pickMobile(data.summary, 'totalNetSales', 'netSales') ?? 0) || 0);
    const netSalesPctM = (snapshotComparisonM?.netSales != null ? Number(snapshotComparisonM.netSales) : null) ?? (Number(pickMobile(billedSrcM, 'netSalesPercentage', 'magilNetSalesPercentage', 'netSalesChangePercent') ?? pickMobile(data.summary, 'netSalesPercentage', 'magilNetSalesPercentage', 'netSalesChangePercent') ?? 0) || null);

    const totalOrdersM = Number(snapshotDetails?.orderCount ?? 0) || Number(pickMobile(billedSrcM, 'totalTransactions', 'totalOrders', 'orders', 'orderCount') ?? pickMobile(data.summary, 'totalTransactions', 'totalOrders') ?? 0) || 0;
    const orderPctM = (snapshotComparisonM?.orderCount != null ? Number(snapshotComparisonM.orderCount) : null) ?? (pickMobile(billedSrcM, 'transactionPercentage', 'magilTransactionPercentage', 'transactionsChangePercent') != null ? Number(pickMobile(billedSrcM, 'transactionPercentage', 'magilTransactionPercentage', 'transactionsChangePercent')) : null);

    const avgOrderM = totalOrdersM > 0 ? netSalesM / totalOrdersM : Number(pickMobile(billedSrcM, 'avgOrderValue') ?? 0) || 0;
    const avgOrderPctM = (snapshotComparisonM?.avgOrderValue != null ? Number(snapshotComparisonM.avgOrderValue) : null) ?? (pickMobile(billedSrcM, 'avgOrderChangePercent') != null ? Number(pickMobile(billedSrcM, 'avgOrderChangePercent')) : null);

    // SPG-1: Spend per Guest — value from snapshot-details spendPerGuest, scoped to guest-bearing
    // orders. "—" when there are no guests in the active period/source.
    const spgGuestCountM = Number(snapshotDetails?.guestCount ?? 0) || 0;
    const spendPerGuestM = spgGuestCountM > 0 ? (Number(snapshotDetails?.spendPerGuest ?? 0) || 0) : null;
    const spendPerGuestPctM = snapshotComparisonM?.spendPerGuest != null ? Number(snapshotComparisonM.spendPerGuest) : null;

    const totalTaxM = Number(snapshotDetails?.tax ?? (snapshotDetails ? 0 : undefined)) || (Number(pickMobile(billedSrcM, 'tax', 'totalTax', 'taxAmount') ?? pickMobile(data.summary, 'tax', 'totalTax') ?? 0) || 0);
    const taxPctM = (snapshotComparisonM?.tax != null ? Number(snapshotComparisonM.tax) : null) ?? (pickMobile(billedSrcM, 'taxPercentage', 'totalTaxPercentage', 'taxChangePercent') != null ? Number(pickMobile(billedSrcM, 'taxPercentage', 'totalTaxPercentage', 'taxChangePercent')) : null);
    const totalDiscountsM = Number(snapshotDetails?.discount ?? (snapshotDetails ? 0 : undefined)) || (Number(pickMobile(billedSrcM, 'discounts', 'totalDiscount', 'totalDiscounts') ?? pickMobile(data.summary, 'discounts', 'totalDiscount') ?? 0) || 0);
    const discountsPctM = (pickMobile(billedSrcM, 'discountPercentage', 'magilDiscountPercentage', 'discountsChangePercent') != null ? Number(pickMobile(billedSrcM, 'discountPercentage', 'magilDiscountPercentage', 'discountsChangePercent')) : null);
    const liveRefundOrderCountM = parseFloat(liveRefundSummaryM.reduce((sum, item) => sum + item.orderCount, 0).toFixed(2));
    const liveCancelledAmtM = parseFloat(liveRefundSummaryM.reduce((sum, item) => sum + (item.cancelledAmt || 0), 0).toFixed(2));
    const refundsPctM = (snapshotComparisonM?.refund != null ? Number(snapshotComparisonM.refund) : null) ?? (pickMobile(billedSrcM, 'cancelledAmtPercentage', 'magilCancelledAmtPercentage', 'refundsChangePercent') != null ? Number(pickMobile(billedSrcM, 'cancelledAmtPercentage', 'magilCancelledAmtPercentage', 'refundsChangePercent')) : null);
    const tipsM = Number(snapshotDetails?.tip ?? 0) || 0;
    // ZAR-1661 — see the desktop branch: delta visibility follows the shape of the period.
    const showDeltaM = hasComparison(activeDateFilter, storeTz);
    const refundCountM = data.isToday ? 0 : Number(pickMobile(billedSrcM, 'cancelledOrderCount', 'totalCancelledOrders', 'refundCount', 'refundOrders', 'voidedCount') ?? pickMobile(data.summary, 'cancelledOrderCount', 'totalCancelledOrders', 'refundCount') ?? 0) || 0;
    // ZAR-1762 / TG-1 — the standalone Gratuity card is gone; gratuity now only exists as part
    // of the unified Tips & Gratuity total. The card face reads the backend's own combined total
    // and combined delta (`tipAndGratuity` on snapshot-details and on its /comparison twin).
    const gratuityM = Number(snapshotDetails?.gratuity ?? 0) || 0;
    const tipsAndGratuityM = Number(snapshotDetails?.tipAndGratuity ?? 0) || 0;
    const tipsGratuityPctM = snapshotComparisonM?.tipAndGratuity != null && Number.isFinite(Number(snapshotComparisonM.tipAndGratuity))
        ? Number(snapshotComparisonM.tipAndGratuity)
        : null;
    // ZAR-1762 / TG-2 — both drill-in sections for the mobile Sales drill. Totals and order
    // counts from snapshot-details; staff-performance / by-channel only for the rankings and panels.
    const tipsGratuityDrillDataM = useMemo(() => buildTipsGratuityData({
        staff: liveStaffPerformanceM.map((s) => ({
            name: s.staff_name,
            tip: s.tip ?? 0,
            gratuity: s.gratuity ?? 0,
            orderCount: s.orderCount ?? 0,
            netSales: s.netSales ?? 0,
        })),
        channels: liveByChannelM.map((c) => ({ channel: c.channel, tip: c.tip ?? 0, gratuity: c.gratuity ?? 0 })),
        netSales: netSalesM,
        totalOrders: Number(snapshotDetails?.orderCount ?? 0) || 0,
        tipsTotal: Number(snapshotDetails?.tip ?? 0) || 0,
        gratuityTotal: Number(snapshotDetails?.gratuity ?? 0) || 0,
        ordersWithTips: Number(snapshotDetails?.ordersWithTips ?? 0) || 0,
        ordersWithGratuity: Number(snapshotDetails?.ordersWithGratuity ?? 0) || 0,
    }), [liveStaffPerformanceM, liveByChannelM, netSalesM, snapshotDetails]);

    const otherChargesM = Number(snapshotDetails?.otherCharges ?? (snapshotDetails ? 0 : undefined)) || Number(pickMobile(billedSrcM, 'otherCharges', 'totalOtherCharges', 'otherCharge') ?? pickMobile(data.summary, 'otherCharges', 'totalOtherCharges', 'otherCharge') ?? 0) || 0;
    const outstandingAmountM = Number(liveOutstandingValueM?.outStandingAmt ?? 0) || (Number(pickMobile(billedSrcM, 'outstandingAmount', 'totalOutstandingAmount', 'outstanding') ?? pickMobile(data.summary, 'outstandingAmount', 'totalOutstandingAmount', 'outstanding') ?? 0) || 0);
    const outstandingPctM = (snapshotComparisonM?.outStandingAmt != null ? Number(snapshotComparisonM.outStandingAmt) : null) ?? null;

    // Sales report-level "Export KPIs" — mirrors exactly what each stamp KPI card shows on screen.
    kpiExportRefM.current = {
        activeDate: activeDateFilter,
        dateRange,
        chDates: chDatesView,
        orderSourceVal,
        token,
        channel,
        isThirdParty,
        grossSales: grossSalesM, netSales: netSalesM, totalOrders: totalOrdersM, avgOrderValue: avgOrderM, spendPerGuest: spendPerGuestM,
        totalTax: totalTaxM, totalDiscounts: totalDiscountsM,
        refunds: liveRefundSummaryFailureM ? 0 : liveCancelledAmtM,
        tipsAndGratuity: tipsAndGratuityM, otherCharges: otherChargesM, outstandingAmount: outstandingAmountM,
    };

    // ── Hourly data ── (unified: both Today and Historical use the new /sales/live/v2/hourly-sales API via Redux)
    const hourlyHoursM = hourlySalesDataM?.hours ?? [];
    const opStartHourM = hourlySalesDataM?.operatingStartHour ?? 6;
    const opEndHourM = hourlySalesDataM?.operatingEndHour ?? 23;
    const opHourCountM = hourlySalesDataM?.operatingHourCount ?? 0;
    const hourlyDataM: { label: string; value: number }[] = hourlyHoursM.map(h => {
        const hv = h.hourValue % 24;
        const disp = hv % 12 || 12;
        const suffix = hv >= 12 ? 'p' : 'a';
        return { label: `${disp}${suffix}`, value: h.itemTotal };
    });
    const hourlyAvgM = opHourCountM > 0
        ? hourlyHoursM
            .filter(h => h.hourValue >= opStartHourM && h.hourValue <= opEndHourM)
            .reduce((s, h) => s + h.itemTotal, 0) / opHourCountM
        : 0;

    // ── Channel breakdown ──
    const channelRawM = listFromMobile(data.channel);

    // Same as Desktop: primary channels shown individually, everything else collapses into "3rd Party"
    const PRIMARY_CHANNELS_SALES_M = /^(dinein|dine-in|dine in|instore|in-store|in store|pickup|pick-up|pick up|delivery)$/i;
    const normalizedChannelArrM = (() => {
        let othersValue = 0;
        let othersCount = 0;
        const primary: any[] = [];
        channelRawM.forEach((item: any) => {
            const name = (item.channelName ?? item.name ?? item.label ?? item.channel ?? '').trim();
            const value = Math.round((Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0) * 100) / 100;
            const count = Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0;
            if (PRIMARY_CHANNELS_SALES_M.test(name)) {
                primary.push(item);
            } else if (value > 0) {
                othersValue += value;
                othersCount += count;
            }
        });
        if (othersValue > 0) {
            primary.push({ channelName: '3rd Party', totalSales: Math.round(othersValue * 100) / 100, orderCount: othersCount });
        }
        return primary;
    })();

    const channelDataM = normalizedChannelArrM.map((item: any, index: number) => ({
        name: item.channelName ?? item.name ?? item.label ?? item.channel ?? '—',
        value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
        count: Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0,
        color: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
    })).filter((item: any) => item.value > 0).sort((a: any, b: any) => b.value - a.value);
    const channelTotalM = channelDataM.reduce((s: number, c: any) => s + c.value, 0);
    const channelPctsM = distribute100(channelDataM.map((d: any) => d.value));
    const channelPctsExactM = distribute100At2dp(channelDataM.map((d: any) => d.value));
    const channelBreakdownM = channelDataM.map((item: any, i: number) => ({
        ...item,
        pct: channelPctsM[i],
        pctExact: channelPctsExactM[i],
    }));

    // ── Today Sales by Channel Mobile (ClickHouse Redux only — no fallbacks) ──
    // Source: GET /sales/live/v2/by-channel via fetchLiveByChannelRequest.
    // grandTotal drives the slice value; percentage is calculated on the frontend.
    const liveChFilteredM = liveByChannelM.filter((item) => item.grandTotal > 0);
    const liveChTotalM = liveChFilteredM.reduce((s, item) => s + item.grandTotal, 0);
    const liveChDoughnutDataM: DoughnutSlice[] = (() => {
        if (!liveChFilteredM.length) return [];
        const sorted = [...liveChFilteredM].sort((a, b) => b.grandTotal - a.grandTotal);
        const pcts = distribute100(sorted.map((item) => item.grandTotal));
        return sorted.map((item, i) => ({
            name: item.channel,
            value: item.grandTotal,
            color: getChannelColor(item.channel),
            pct: pcts[i],
        }));
    })();

    // ── Third-Party Marketplace — reads from raw channelRawM (before combining) so individual platforms still appear ──
    const thirdPartyDataM = channelRawM
        .map((item: any, index: number) => ({
            name: item.channelName ?? item.name ?? item.label ?? item.channel ?? `Channel ${index + 1}`,
            value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
            count: Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? 0) || 0,
            color: CHANNEL_COLORS[index % CHANNEL_COLORS.length],
        }))
        .filter((ch: any) => ch.value > 0 && /door.?dash|uber.?eats|grub.?hub|postmates|delivery.*maghil|third.?party|gloria.?food|swiggy|zomato|seamless/i.test(String(ch.name || '')));

    // ── Net Sales drill: Maghil vs 3rd-Party split (from API fields, fallback to channel data) ──
    const isThirdPartyChM = (name: any) => /third.?party|3p|uber|uber.?eats|door.?dash|grub.?hub|postmates|delivery.*maghil|gloria.?food|swiggy|zomato|seamless|deliveroo|just.?eat|talabat|foodpanda/i.test(String(name || ''));
    let maghilSalesM = Number(pickMobile(billedSrcM, 'magilNetSales', 'maghilNetSales', 'magilSales', 'maghilSales', 'directNetSales') ?? pickMobile(data.summary, 'magilNetSales', 'maghilNetSales', 'magilSales', 'maghilSales', 'directNetSales') ?? 0) || 0;
    let thirdPartySalesM = Number(pickMobile(billedSrcM, 'thirdPartyNetSales', 'thirdpartySales', 'thirdPartyTotalSales') ?? pickMobile(data.summary, 'thirdPartyNetSales', 'thirdpartySales', 'thirdPartyTotalSales') ?? 0) || 0;
    // Derive from channel breakdown when summary fields are absent (historical dates)
    if (maghilSalesM === 0 && thirdPartySalesM === 0 && channelRawM.length > 0) {
        channelRawM.forEach((item: any) => {
            const chName = item.channelName ?? item.name ?? item.channel ?? '';
            const total = Number(item.totalSales ?? item.sales ?? item.netSales ?? 0) || 0;
            const mRow = Number(item.maghilSales ?? item.magilSales ?? item.maghil ?? item.magil ?? item.directSales ?? 0) || 0;
            const tRow = Number(item.thirdPartySales ?? item.thirdpartySales ?? item.thirdParty ?? item.thirdparty ?? 0) || 0;
            if (mRow > 0 || tRow > 0) { maghilSalesM += mRow; thirdPartySalesM += tRow; }
            else if (total > 0) { if (isThirdPartyChM(chName)) thirdPartySalesM += total; else maghilSalesM += total; }
        });
    }
    const maghilThirdTotalM = maghilSalesM + thirdPartySalesM;
    const maghilPctM = maghilThirdTotalM > 0 ? Math.round((maghilSalesM / maghilThirdTotalM) * 100) : 0;
    const thirdPartyPctM = maghilThirdTotalM > 0 ? 100 - maghilPctM : 0;

    // ── Top Staff Seller (for net sales drill) ──
    console.log('[SalesScreen][Mobile] staffSales raw =>', data.staffSales);
    const staffSalesListM = listFromMobile(data.staffSales).map((item: any) => ({
        name: item.fullName ?? '—',
        sales: Number(item.total ?? 0) || 0,
        orders: Number(item.orders ?? 0) || 0,
    })).filter((s: any) => s.sales > 0).sort((a: any, b: any) => b.sales - a.sales);
    const topSellerM = staffSalesListM[0] ?? null;
    useEffect(() => { setStaffPageM(0); }, [data.staffSales]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setDiscountsStaffPageM(0); }, [liveStaffPerformanceM]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setRefundsStaffPageM(0); }, [liveRefundSummaryM]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Category data (historical) ──
    const categoryDataM = listFromMobile(data.category).map((item: any) => ({
        name: item.categoryName ?? item.name ?? item.label ?? '—',
        revenue: Number(item.totalPrice ?? item.totalSales ?? item.revenue ?? item.grossSales ?? item.amount ?? 0) || 0,
        qty: Number(item.totalQuantity ?? item.salesQty ?? item.qty ?? item.quantity ?? 0) || 0,
    })).filter((item: any) => item.revenue > 0).sort((a: any, b: any) => b.revenue - a.revenue);
    const maxCatRevenueM = categoryDataM[0]?.revenue || 1;

    // ── Voided reasons mobile ──
    const voidedSummaryListM = listFromMobile(data.voidedSummary);
    const refundReasonMapM: Record<string, { reason: string; amount: number; count: number }> = {};
    voidedSummaryListM.forEach((d: any) => {
        const reason = d.voidedReasons ?? d.reason ?? d.voidReason ?? d.refundReason ?? d.name ?? null;
        if (!reason) return;
        if (!refundReasonMapM[reason]) refundReasonMapM[reason] = { reason, amount: 0, count: 0 };
        refundReasonMapM[reason].amount += Number(d.voidedAmount ?? d.totalAmount ?? d.amount ?? 0) || 0;
        refundReasonMapM[reason].count += Number(d.orderCount ?? d.totalOrders ?? d.count ?? 1) || 0;
    });
    const refundReasonsM = Object.values(refundReasonMapM).sort((a, b) => b.amount - a.amount);
    const maxRefundM = refundReasonsM[0]?.amount || 1;

    // ── Payment Details Mobile ──
    const paymentListM = listFromMobile(data.payments);
    const tenderDataRawM = (() => {
        const grouped: Record<string, { type: string; amount: number; count: number }> = {};
        paymentListM.forEach((p: any) => {
            const type = p.paymentMode ?? p.paymentType ?? p.tenderType ?? p.type ?? p.name ?? '—';
            const amount = Number(p.totalSales ?? p.totalAmount ?? p.amount ?? p.total ?? p.netAmount ?? 0) || 0;
            const count = Number(p.totalOrders ?? p.transactionCount ?? p.count ?? p.orders ?? 0) || 0;
            if (!grouped[type]) grouped[type] = { type, amount: 0, count: 0 };
            grouped[type].amount += amount;
            grouped[type].count += count;
        });
        return Object.values(grouped).filter(p => p.amount > 0).sort((a, b) => b.amount - a.amount);
    })();
    const tenderPctsM = distribute100At2dp(tenderDataRawM.map(t => t.amount));
    const tenderDataM = tenderDataRawM.map((t, i) => ({ ...t, pct: tenderPctsM[i] }));

    // ── Live Tender Data Mobile (Today / ClickHouse) ──
    const liveTenderSortedM = livePaymentsM
        .filter(p => p.tenderAmount > 0)
        .sort((a, b) => b.tenderAmount - a.tenderAmount);
    const liveTenderPctsM = distribute100At2dp(liveTenderSortedM.map(p => p.tenderAmount));
    const liveTenderDataM = liveTenderSortedM.map((p, i) => ({
        type: p.tenderType,
        amount: p.tenderAmount,
        count: p.transactionCount,
        pct: liveTenderPctsM[i],
    }));

    const activeTenderDataM = liveTenderDataM;
    const activeTenderLoadingM = livePaymentsLoadingM;
    const activeTenderErrorM = livePaymentsFailureM;
    const activeTenderRetryM = () => dispatch(fetchLivePaymentsRequest({ locationId, ...chDatesView, ...orderSourceParam }));

    // ── Top Items Mobile ──
    const topItemsSourceM = liveItemSalesV2M.length > 0 ? liveItemSalesV2M : listFromMobile(data.topItems);
    const topItemsMobile = topItemsSourceM
        .map((item: any) => ({
            name: item.itemName ?? item.name ?? item.menuItemName ?? item.label ?? '—',
            qty: Number(item.salesQty ?? item.sales_qty ?? item.itemCount ?? item.qty ?? item.quantity ?? 0) || 0,
            rev: Number(item.totalItemPrice ?? item.price ?? item.grossSales ?? item.totalSales ?? item.amount ?? item.revenue ?? 0) || 0,
        }))
        .filter((item: any) => item.name !== '—' || item.rev > 0)
        .sort((a: any, b: any) => b.rev - a.rev)
        .slice(0, 3);

    const drillState = {
        locationId,
        token,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        isToday: data.isToday,
    };

    // ── Mobile Order Log helpers ──
    // Always use Redux state (ClickHouse order-log endpoint with date params) — Today + historical
    const activeMolOrders: OrderLogItem[] = orderLogOrdersM;
    const activeMolLoading = orderLogLoadingM;
    const activeMolTotal = orderLogTotalElementsM;
    const activeMolTotalPages = orderLogTotalPagesM;
    const activeMolError = orderLogFailureM;
    const molHasFilters = molDebouncedSearch || molOrderTypes.length || molChannelGroup || molMinAmt || molMaxAmt || molMinGuests;
    const molTotalPages = activeMolTotalPages;
    const molActiveFilterCount = activeDateFilter === 'Today'
        ? [molOrderTypes.length > 0, !!molChannelGroup, molMinAmt, molMaxAmt, molMinGuests].filter(Boolean).length
        : 0;
    const molAmtError = molMinAmt && molMaxAmt && Number(molMinAmt) >= Number(molMaxAmt)
        ? 'Min must be less than Max'
        : null;

    const fmtOrderTimeM = (raw: any, isToday: boolean) => {
        if (!raw) return '—';
        if (isToday && /^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(String(raw).trim())) return String(raw).trim();
        try {
            const d = new Date(raw);
            if (isNaN(d.getTime())) return String(raw);
            const h = d.getHours() % 12 || 12;
            const m = String(d.getMinutes()).padStart(2, '0');
            const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
            if (isToday) return `${h}:${m} ${ampm}`;
            const mo = d.toLocaleString('en-US', { month: 'short' });
            return `${mo} ${d.getDate()} ${h}:${m} ${ampm}`;
        } catch { return String(raw); }
    };

    const fmtPhoneM = (raw: any) => {
        if (!raw) return '—';
        const digits = String(raw).replace(/\D/g, '');
        if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        return String(raw);
    };

    const MOL_SORT_OPTIONS = ['Time (newest first)', 'Time (oldest first)', 'Amount (highest first)', 'Amount (lowest first)', 'Customer Name (A–Z)', 'Guests (most first)'];
    const molSortParams = (opt: string) => {
        if (opt === 'Time (newest first)') return { sortBy: 'orderTime', sortAsc: false };
        if (opt === 'Time (oldest first)') return { sortBy: 'orderTime', sortAsc: true };
        if (opt === 'Amount (highest first)') return { sortBy: 'amount', sortAsc: false };
        if (opt === 'Amount (lowest first)') return { sortBy: 'amount', sortAsc: true };
        if (opt === 'Customer Name (A–Z)') return { sortBy: 'customerName', sortAsc: true };
        if (opt === 'Guests (most first)') return { sortBy: 'guests', sortAsc: false };
        return { sortBy: 'time', sortAsc: false };
    };

    // Mobile Order Log: debounce search
    useEffect(() => {
        const t = setTimeout(() => setMolDebouncedSearch(molSearch), 300);
        return () => clearTimeout(t);
    }, [molSearch]);

    // Mobile Order Log: reset page on filter/date change
    useEffect(() => { setMolPage(0); setExpandedMolItems(new Set()); }, [molDebouncedSearch, molOrderTypes, molChannelGroup, molMinAmt, molMaxAmt, molMinGuests, dateRange.startDate, dateRange.endDate, channel]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setExpandedMolItems(new Set()); }, [molPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mobile Channel Names: fetch once per location
    useEffect(() => {
        if (locationId && channelNamesKeyRefM.current !== locationId) {
            channelNamesKeyRefM.current = locationId;
            dispatch(fetchChannelNamesRequest(locationId));
        }
    }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mobile Order Log: fetch
    // For Today → dispatch to Redux (ClickHouse live). For other dates → legacy reportsApi.
    useEffect(() => {
        if (!locationId) return;
        const sp = molSortParams(molSort);
        const molAmtInvalid = molMinAmt && molMaxAmt && Number(molMinAmt) >= Number(molMaxAmt);
        if (molAmtInvalid) return;
        const molIsToday = dateRange.startDate === today;
        const molDates = molIsToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        const molKey = `${locationId}|${dateRange.startDate}|${dateRange.endDate}|${molIsToday}|${molPage}|${molSort}|${molDebouncedSearch}|${molOrderTypes.join(',')}|${molChannelGroup ?? ''}|${molMinAmt}|${molMaxAmt}|${molMinGuests}|${channel}|${refreshNonce}`;
        if (orderLogKeyRefM.current === molKey) return;
        orderLogKeyRefM.current = molKey;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: molPage + 1,
            size: OL_PAGE_SIZE_M,
            sortBy: sp.sortBy,
            sortDir: sp.sortAsc ? 'asc' : 'desc',
            search: molDebouncedSearch || undefined,
            channelName: molOrderTypes.length ? molOrderTypes.join(',') : undefined,
            channelGroup: molChannelGroup ?? undefined,
            guestCount: molMinGuests ? Number(molMinGuests) : undefined,
            minAmount: molMinAmt ? Number(molMinAmt) : undefined,
            maxAmount: molMaxAmt ? Number(molMaxAmt) : undefined,
            ...molDates,
            ...orderSourceParam,
        }));
    }, [locationId, token, dateRange.startDate, dateRange.endDate, molPage, molSort, molDebouncedSearch, molOrderTypes, molChannelGroup, molMinAmt, molMaxAmt, molMinGuests, today, channel, refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mobile Order Detail: fetch when selected
    // Always use ClickHouse order-detail (orderId-based, no date params) for Today + historical.
    useEffect(() => {
        if (!molSelectedOrder || !locationId) { dispatch(clearOrderDetail()); return; }
        const orderId = (molSelectedOrder as OrderLogItem).orderId ?? '';
        if (orderId) dispatch(fetchOrderDetailRequest({ locationId, orderId, ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }));
    }, [molSelectedOrder, locationId, activeDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Mobile stat card helper ──
    const MStatCard = ({ label, value, color, sub, loading: isLoading }: { label: string; value: string; color?: string; sub?: string; loading?: boolean }) => (
        <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: color ?? C.t1, letterSpacing: -0.5 }}>
                {isLoading ? <Bone w={70} h={20} r={4} /> : value}
            </div>
            {!isLoading && sub && <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{sub}</div>}
        </div>
    );

    if (drillView === 'net-sales') {
        const waterfallSource: WaterfallSource = orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall';
        // Mirrors desktop: Gross is summed in buildWaterfallData, `grossSalesM` is the
        // "Total Collected" stamp KPI value and `otherChargesM` the "Other" stamp KPI.
        const wfDataM = buildWaterfallData({
            voids: liveVoidSummaryM.reduce((s, i) => s + (Number(i.cancelledAmt) || 0), 0),
            discounts: totalDiscountsM,
            complimentary: liveCompOrderLogM.reduce((s, o) => s + (Number(o.amount) || 0), 0),
            refunds: liveCancelledAmtM,
            netSales: netSalesM,
            tax: totalTaxM,
            tips: tipsM,
            gratuity: gratuityM,
            otherCharges: otherChargesM,
            totalCollected: grossSalesM,
        });
        const isTpM = waterfallSource === 'thirdparty';
        const primaryLabelM = isTpM ? 'Item Total' : 'Net Sales';
        const primaryValueM = isTpM ? wfDataM.itemTotal : wfDataM.netSales;
        const peakIdxM = hourlyDataM.length ? hourlyDataM.reduce((mi, d, i, arr) => d.value > arr[mi].value ? i : mi, 0) : -1;
        const hasPeakM = peakIdxM >= 0 && hourlyDataM[peakIdxM].value > 0;
        const peakLblM = hasPeakM ? expandHourLabel(hourlyDataM[peakIdxM].label) : '--';
        // Revenue booked in the peak hour — the hour alone doesn't say how big it was.
        const peakSubM = !hasPeakM || hourlySalesFailureM ? undefined : fmtCurrencyMobile(hourlyDataM[peakIdxM].value);
        // SW 10: the waterfall's Voids/Complimentary/Refunds rows come from three separate
        // async fetches on top of snapshot-details — snapshotDetailsLoadingM alone drops before
        // they land, so the bars would render stale/zero values then jump once each resolves.
        const waterfallLoadingM = snapshotDetailsLoadingM || liveVoidSummaryLoadingM || liveCompOrderLogLoadingM || liveRefundSummaryLoadingM;
        const nsKpis = [
            { l: primaryLabelM, v: fmtCurrencyMobile(primaryValueM), accent: true },
            { l: getTotalPaidLabel(waterfallSource), v: fmtCurrencyMobile(getTotalPaidValue(waterfallSource, wfDataM)), sub: getTotalPaidSub(waterfallSource) },
            { l: 'Peak Hour', v: hourlySalesFailureM ? '--' : peakLblM, sub: peakSubM },
            { l: 'Avg/Hour', v: fmtCurrencyMobile(hourlyAvgM) },
        ];
        return (
            <Phone>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
                <TopBar variant="mobile" />
                <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                    {error && (
                        <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 10, marginBottom: 10, fontSize: 11, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span>{error}</span>
                            <button type="button" onClick={() => setRefreshNonce(n => n + 1)} style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Try again</button>
                        </div>
                    )}
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 10 }}>{snapshotDetailsLoadingM ? <Bone w={160} h={11} r={3} /> : `${fmtCurrencyMobile(primaryValueM)} ${isTpM ? 'item total' : 'total revenue'} · ${getFilterLabel(activeDateFilter, dateRange.startDate, dateRange.endDate)}`}</div>
                    <div ref={netSalesKpiRowRefM} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                        {nsKpis.map((k, i) => (
                            <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '10px 12px', boxShadow: C.shadow, textAlign: 'center' }}>
                                <div style={{ fontSize: 8, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.l}</div>
                                <div style={{ fontSize: 17, fontWeight: 700, color: k.accent ? C.accent : C.t1, marginTop: 3 }}>{snapshotDetailsLoadingM ? <Bone w={60} h={18} r={4} style={{ margin: '0 auto' }} /> : k.v}</div>
                                {!snapshotDetailsLoadingM && k.sub && <div style={{ fontSize: 8, color: C.t3, marginTop: 2 }}>{k.sub}</div>}
                            </div>
                        ))}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <SalesWaterfall
                            source={waterfallSource}
                            data={wfDataM}
                            fmt={fmtCurrencyMobile}
                            loading={waterfallLoadingM}
                            // SW3 — same split as desktop: Discounts/Refunds stay on this screen's
                            // own drills, Voids/Complimentary route to Snapshot's drilldown.
                            onDeductionClick={(key) => {
                                if (key === 'discounts' || key === 'refunds') setDrillView(key);
                                else navigate(`/reports/snapshot?drill=${key}`, { state: { returnTo: '/reports/sales?drill=net-sales' } });
                            }}
                        />
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Hourly Revenue</div>
                        <div style={{ fontSize: 10, color: C.t3, marginBottom: 8 }}>Revenue distribution across service hours</div>
                        <HourlyBarChart
                            data={hourlyDataM}
                            loading={hourlySalesLoadingM}
                            error={hourlySalesFailureM}
                            onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                            kpiName="hourly revenue"
                            barColor={C.accent}
                            chartHeight={90}
                            showCurrency={true}
                            minHeight={120}
                            avgOverride={hourlyAvgM}
                        />
                    </div>
                </div>
                <BottomNav />
            </Phone>
        );
    }

    if (drillView === 'discounts') {
        // KPI row + Discounts-by-Staff from ClickHouse staff-performance; the
        // by-source card and Discount Log come from the loyalty
        // discount-performance / discount-log APIs inside
        // DiscountBreakdownAndLog. Identical wiring to the Snapshot report.
        const totalDiscountsPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.discount || 0), 0);
        const discOrdersPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.orderCount || 0), 0);
        const totalGrandTotalPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.grandTotal || 0), 0);
        const sortedByDiscountM = [...liveStaffPerformanceM].filter(s => s.discount > 0).sort((a, b) => b.discount - a.discount);
        const DISC_STAFF_PAGE_SIZE_M = 5;
        const discTotalPagesM = data.isToday ? 1 : Math.ceil(sortedByDiscountM.length / DISC_STAFF_PAGE_SIZE_M);
        const discSafePageM = data.isToday ? 0 : Math.min(discountsStaffPageM, Math.max(0, discTotalPagesM - 1));
        const discPageSliceM = data.isToday
            ? sortedByDiscountM.slice(0, DISC_STAFF_PAGE_SIZE_M)
            : sortedByDiscountM.slice(discSafePageM * DISC_STAFF_PAGE_SIZE_M, discSafePageM * DISC_STAFF_PAGE_SIZE_M + DISC_STAFF_PAGE_SIZE_M);
        return (
            <Phone>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
                <TopBar variant="mobile" />
                <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                    {error && (
                        <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 10, marginBottom: 10, fontSize: 11, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span>{error}</span>
                            <button type="button" onClick={() => setRefreshNonce(n => n + 1)} style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Try again</button>
                        </div>
                    )}
                    {liveStaffPerformanceLoadingM ? (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            {[1, 2].map(i => (
                                <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                                    <Bone w={60} h={9} mb={8} />
                                    <Bone w={90} h={22} mb={6} />
                                    <Bone w={50} h={9} />
                                </div>
                            ))}
                        </div>
                    ) : liveStaffPerformanceFailureM ? (
                        <SectionError kpiName="discounts" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} size="small" minHeight={120} />
                    ) : (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            <MStatCard label="Total Discounts" value={fmtCurrencyMobile(totalDiscountsPfM)} color={C.orange} sub={totalGrandTotalPfM > 0 ? `${((totalDiscountsPfM / totalGrandTotalPfM) * 100).toFixed(1)}% of Gross` : undefined} />
                            <MStatCard label="Disc. Orders" value={fmtNumMobile(discOrdersPfM)} sub={totalOrdersM > 0 ? `${((discOrdersPfM / totalOrdersM) * 100).toFixed(1)}% of all` : undefined} />
                        </div>
                    )}
                    <DiscountBreakdownAndLog
                        sym={getCurrencySymbol()}
                        startISO={data.isToday ? today : (dateRange.startDate || today)}
                        endISO={data.isToday ? today : (dateRange.endDate || today)}
                        outletId={null}
                        locationId={locationId}
                        isToday={data.isToday}
                        orderSource={orderSourceVal || undefined}
                        token={token}
                        onOpenOrder={(r) => setMolSelectedOrder({ orderId: r.orderId, orderNo: r.orderNo })}
                    >
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Discounts by Staff</div>
                        {liveStaffPerformanceLoadingM ? (
                            <div>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${C.divider}` : 'none' }}>
                                        <Bone w={22} h={22} r={6} />
                                        <div style={{ flex: 1 }}><Bone w={110} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                        <div style={{ textAlign: 'right' }}><Bone w={56} h={13} mb={4} /><Bone w={80} h={9} /></div>
                                    </div>
                                ))}
                            </div>
                        ) : liveStaffPerformanceFailureM ? (
                            <SectionError kpiName="discounts by staff" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} size="small" minHeight={80} />
                        ) : sortedByDiscountM.length ? (
                            <>
                                {discPageSliceM.map((s: LiveStaffPerformanceItem, i: number) => {
                                    const globalRankM = discSafePageM * DISC_STAFF_PAGE_SIZE_M + i;
                                    const pct = totalDiscountsPfM > 0 ? ((s.discount / totalDiscountsPfM) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < discPageSliceM.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                            <div style={{ width: 22, height: 22, borderRadius: 6, background: globalRankM === 0 ? C.orangeSoft : C.hover, color: globalRankM === 0 ? C.orange : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{globalRankM + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, color: C.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                <div style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>{s.orderCount} orders · {pct}%</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: C.orange }}>{fmtCurrencyMobile(s.discount)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {!data.isToday && discTotalPagesM > 1 && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 4 }}>
                                        <button onClick={() => setDiscountsStaffPageM((p) => Math.max(0, p - 1))} disabled={discSafePageM === 0} style={{ fontSize: 11, color: discSafePageM === 0 ? C.t3 : C.accent, background: 'none', border: `1px solid ${discSafePageM === 0 ? C.divider : C.accent}`, borderRadius: 4, cursor: discSafePageM === 0 ? 'default' : 'pointer', padding: '2px 8px' }}>← Prev</button>
                                        <span style={{ fontSize: 10, color: C.t3 }}>{discSafePageM + 1} / {discTotalPagesM}</span>
                                        <button onClick={() => setDiscountsStaffPageM((p) => Math.min(discTotalPagesM - 1, p + 1))} disabled={discSafePageM === discTotalPagesM - 1} style={{ fontSize: 11, color: discSafePageM === discTotalPagesM - 1 ? C.t3 : C.accent, background: 'none', border: `1px solid ${discSafePageM === discTotalPagesM - 1 ? C.divider : C.accent}`, borderRadius: 4, cursor: discSafePageM === discTotalPagesM - 1 ? 'default' : 'pointer', padding: '2px 8px' }}>Next →</button>
                                    </div>
                                )}
                                {sortedByDiscountM.length > 0 && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.t3 }}>
                                        <span>{sortedByDiscountM.length} staff</span>
                                        <span>Total: <span style={{ color: C.orange, fontWeight: 600 }}>{fmtCurrencyMobile(totalDiscountsPfM)}</span></span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <SectionEmpty kpiName="staff discounts" size="small" minHeight={80} />
                        )}
                    </div>
                    </DiscountBreakdownAndLog>
                </div>
                <OrderDetailSlider
                    visible={!!molSelectedOrder}
                    onClose={() => { setMolSelectedOrder(null); setMolOrderDetail(null); dispatch(clearOrderDetail()); }}
                    orderNo={(molSelectedOrder as OrderLogItem)?.orderNo}
                    orderType={(molSelectedOrder as OrderLogItem)?.orderType}
                    loading={orderDetailLoadingM}
                    error={orderDetailErrorM}
                    onRetry={() => dispatch(fetchOrderDetailRequest({ locationId, orderId: (molSelectedOrder as OrderLogItem).orderId }))}
                    detail={orderDetailDataM}
                    legacyDetail={undefined}
                />
                <BottomNav />
            </Phone>
        );
    }

    if (drillView === 'refunds') {
        const maxRefundCancelledAmtM = liveRefundSummaryM.reduce((m, item) => Math.max(m, item.cancelledAmt || 0), 1);
        const liveAvgRefundM = liveRefundOrderCountM > 0 ? liveCancelledAmtM / liveRefundOrderCountM : 0;
        const liveTopRefundReasonM: LiveRefundSummaryItem | null = liveRefundSummaryM.reduce<LiveRefundSummaryItem | null>(
            (top, item) => ((item.cancelledAmt || 0) > (top?.cancelledAmt || 0) ? item : top),
            null,
        );
        const liveTopRefundPctM = liveTopRefundReasonM && liveCancelledAmtM > 0
            ? ((liveTopRefundReasonM.cancelledAmt || 0) / liveCancelledAmtM) * 100
            : 0;
        const sortedStaffRefundsM = [...liveStaffRefundsM].sort((a, b) => b.refund - a.refund);
        const topRefunderM: LiveStaffRefundsItem | null = sortedStaffRefundsM[0] ?? null;
        const totalStaffRefundAmtM = liveStaffRefundsM.reduce((s, item) => s + (item.refund || 0), 0);
        const abbreviateNameM = (name: string) => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            return `${parts[0]} ${parts[parts.length - 1][0]}.`;
        };
        return (
            <Phone>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
                <TopBar variant="mobile" />
                <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                    {error && (
                        <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 10, marginBottom: 10, fontSize: 11, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span>{error}</span>
                            <button type="button" onClick={() => setRefreshNonce(n => n + 1)} style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Try again</button>
                        </div>
                    )}
                    {/* Stat row */}
                    <>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                <MStatCard label="Total Refunds" value={liveRefundSummaryFailureM ? fmtCurrencyMobile(0) : fmtCurrencyMobile(liveCancelledAmtM)} color={C.red} loading={liveRefundSummaryLoadingM} />
                                <MStatCard label="Count" value={liveRefundSummaryFailureM ? '0' : fmtNumMobile(liveRefundOrderCountM)} loading={liveRefundSummaryLoadingM} />
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                <MStatCard label="Avg Refund" value={liveRefundSummaryFailureM ? fmtCurrencyMobile(0) : liveRefundOrderCountM > 0 ? fmtCurrencyMobile(liveAvgRefundM) : fmtCurrencyMobile(0)} loading={liveRefundSummaryLoadingM} />
                                <MStatCard
                                    label="#1 Reason"
                                    value={liveRefundSummaryFailureM ? '--' : liveTopRefundReasonM ? liveTopRefundReasonM.cancelReason : '--'}
                                    color={liveTopRefundReasonM ? C.red : undefined}
                                    sub={liveTopRefundReasonM ? `${fmtCurrencyMobile(liveTopRefundReasonM.cancelledAmt)} · ${liveTopRefundPctM.toFixed(1)}%` : undefined}
                                    loading={liveRefundSummaryLoadingM}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                <MStatCard
                                    label="Top Refunder"
                                    value={liveStaffRefundsFailureM ? '--' : topRefunderM ? abbreviateNameM(topRefunderM.staff_name) : '--'}
                                    color={topRefunderM ? C.red : undefined}
                                    sub={topRefunderM ? `${fmtCurrencyMobile(topRefunderM.refund)} · ${topRefunderM.orderCount} refunds` : undefined}
                                    loading={liveStaffRefundsLoadingM}
                                />
                            </div>
                    </>
                    {/* Refunds by Reason from ClickHouse Redux (Today + historical) */}
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, boxShadow: C.shadow }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Refunds by Reason</div>
                        {liveRefundSummaryLoadingM ? (
                            <div>
                                {[1, 2, 3].map(i => (
                                    <div key={i} style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Bone w={100} h={12} />
                                            <Bone w={28} h={12} />
                                        </div>
                                        <Bone h={4} r={2} />
                                    </div>
                                ))}
                            </div>
                        ) : liveRefundSummaryFailureM ? (
                            <SectionError kpiName="refunds by reason" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} size="small" minHeight={80} />
                        ) : liveRefundSummaryM.length ? (() => {
                            const totalRefundCountM = liveRefundSummaryM.reduce((sum, item) => sum + item.orderCount, 0);
                            const totalCancelledAmtM = liveRefundSummaryM.reduce((sum, item) => sum + (item.cancelledAmt || 0), 0);
                            const totalSalesM = Number(snapshotDetails?.grandTotal ?? 0);
                            const refundRateM = totalSalesM > 0 ? (totalCancelledAmtM / totalSalesM) * 100 : null;
                            return (
                                <>
                                    {liveRefundSummaryM.map((item: LiveRefundSummaryItem, i: number) => (
                                        <div key={i} style={{ marginBottom: i < liveRefundSummaryM.length - 1 ? 10 : 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <span style={{ fontSize: 12, fontWeight: 500, color: C.t1 }}>{item.cancelReason}</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(item.cancelledAmt)}</span>
                                            </div>
                                            <div style={{ height: 4, background: C.hover, borderRadius: 2 }}>
                                                <div style={{ height: 4, borderRadius: 2, width: `${Math.min(((item.cancelledAmt || 0) / maxRefundCancelledAmtM) * 100, 100)}%`, background: C.red, opacity: 0.65 }} />
                                            </div>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: C.t3 }}>
                                        <span>{fmtNumMobile(totalRefundCountM)} refunds</span>
                                        <span>Refund Rate: <span style={{ color: C.red, fontWeight: 600 }}>{refundRateM != null ? `${refundRateM.toFixed(1)}%` : '--'}</span></span>
                                    </div>
                                </>
                            );
                        })() : (
                            <SectionEmpty kpiName="cancel reasons" size="small" minHeight={80} />
                        )}
                    </div>
                    {/* Refunds by Staff */}
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, boxShadow: C.shadow }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Refunds by Staff</div>
                        {liveStaffRefundsLoadingM ? (
                                <div>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                                            <Bone w={20} h={20} r={6} />
                                            <div style={{ flex: 1 }}><Bone w={90} h={12} mb={4} /><Bone w={60} h={10} /></div>
                                            <Bone w={36} h={14} />
                                        </div>
                                    ))}
                                </div>
                            ) : liveStaffRefundsFailureM ? (
                                <SectionError kpiName="refunds by staff" onRetry={() => dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} size="small" minHeight={80} />
                            ) : sortedStaffRefundsM.length ? (
                                <>
                                    {sortedStaffRefundsM.slice(0, 5).map((s: LiveStaffRefundsItem, i: number) => {
                                        const refundRateM2 = totalStaffRefundAmtM > 0 ? (s.refund / totalStaffRefundAmtM) * 100 : 0;
                                        return (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: i < Math.min(sortedStaffRefundsM.length, 5) - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                                <div style={{ width: 20, height: 20, borderRadius: 6, background: i === 0 ? `${C.red}20` : C.hover, color: i === 0 ? C.red : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                    <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>{s.orderCount} refunds · {refundRateM2.toFixed(1)}% rate</div>
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: C.red, flexShrink: 0 }}>{fmtCurrencyMobile(s.refund)}</div>
                                            </div>
                                        );
                                    })}
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.t3 }}>
                                        <span>{liveStaffRefundsM.length} staff</span>
                                        <span>Total: <span style={{ color: C.red, fontWeight: 600 }}>{fmtCurrencyMobile(totalStaffRefundAmtM)}</span></span>
                                    </div>
                                </>
                            ) : (
                                <SectionEmpty kpiName="refunds by staff" size="small" minHeight={80} />
                        )}
                    </div>
                </div>
                <BottomNav />
            </Phone>
        );
    }

    // ZAR-1762 / TG-2 — mobile "Tips & Gratuity" drill-in. Same shared two-section
    // component as desktop, stacked (Tips first, Gratuity second) with no view toggle.
    if (drillView === 'tips') {
        return (
            <Phone>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
                <TopBar variant="mobile" />
                <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                    {error && (
                        <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 10, marginBottom: 10, fontSize: 11, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span>{error}</span>
                            <button type="button" onClick={() => setRefreshNonce(n => n + 1)} style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Try again</button>
                        </div>
                    )}
                    <TipsGratuityDrill
                        data={tipsGratuityDrillDataM}
                        fmt={fmtCurrencyMobile}
                        isMobile
                        loading={snapshotDetailsLoadingM || liveStaffPerformanceLoadingM}
                        staffFailure={liveStaffPerformanceFailureM}
                        onRetryStaff={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                        channelLoading={liveByChannelLoadingM}
                        channelFailure={liveByChannelFailureM}
                        onRetryChannel={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                    />
                </div>
                <BottomNav />
            </Phone>
        );
    }

    const kpiLoadingM = snapshotDetailsLoadingM;
    const deltaLoadingM = kpiLoadingM || snapshotComparisonLoadingM;
    const outstandingKpiLoadingM = liveOutstandingValueLoadingM;

    // Outstanding stamp KPI — sits in row 3 alongside Tips & Grat and Other. Since ZAR-1762
    // merged the Tips and Gratuity cards into one, row 3 has a free slot under every source
    // filter, so Outstanding no longer needs a row of its own.
    const outstandingKpiM = (
        <KPI label="Outstanding" value={outstandingKpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(outstandingAmountM)} delta={showDeltaM && outstandingPctM != null ? `${outstandingPctM >= 0 ? '+' : ''}${outstandingPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={outstandingPctM == null ? true : outstandingPctM >= 0} drill={false} tooltip="Amount customers owes you (ie., Difference between order total and paid amount)" isMobile />
    );
    // SPG-1: Spend/Guest — last stamp KPI in the grid. Shows "—" (not $0.00) when there are no
    // guests in the active period/source.
    const spendGuestKpiM = (
        <KPI label="Spend / Guest" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : spendPerGuestM == null ? '—' : fmtCurrencyMobile(spendPerGuestM)} delta={showDeltaM && spendPerGuestPctM != null ? `${spendPerGuestPctM >= 0 ? '+' : ''}${spendPerGuestPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={spendPerGuestPctM == null ? true : spendPerGuestPctM >= 0} drill={false} />
    );

    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                {error && (
                    <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 10, padding: 10, marginBottom: 10, fontSize: 11, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => setRefreshNonce(n => n + 1)}
                            style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Try again
                        </button>
                    </div>
                )}

                {/* Net Sales hero */}
                <div onClick={() => setDrillView('net-sales')} style={{
                    background: `linear-gradient(135deg, ${C.accent}0D, ${C.purple ?? '#7c3aed'}0A)`,
                    border: `1px solid ${C.accent}22`,
                    borderRadius: 16,
                    padding: '16px 18px',
                    marginBottom: 10,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: C.shadow,
                    cursor: 'pointer',
                }}>
                    <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: C.accentSoft, filter: 'blur(30px)' }} />
                    <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>Net Sales<KpiTooltip text="From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions." isMobile /><span style={{ marginLeft: 'auto' }}><DrillArrow /></span></div>
                    {kpiLoadingM
                        ? <Bone w={180} h={30} mb={4} />
                        : <div style={{ fontSize: netSalesM >= 1000000 ? 24 : 30, fontWeight: 700, color: C.t1, letterSpacing: -1, marginBottom: 4 }}>{fmtCurrencyMobile(netSalesM)}</div>
                    }
                    {showDeltaM && (deltaLoadingM
                        ? <div style={{ width: 80, height: 14, borderRadius: 4, background: 'linear-gradient(90deg, #e8eaf0 25%, #f3f4f8 37%, #e8eaf0 63%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s ease infinite', marginTop: 4 }} />
                        : netSalesPctM != null && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: netSalesPctM === 0 ? C.t2 : netSalesPctM >= 0 ? C.green : C.red, display: 'flex', alignItems: 'center', gap: 3 }}>
                                {netSalesPctM !== 0 && <span>{netSalesPctM >= 0 ? '↑' : '↓'}</span>}
                                {Math.abs(netSalesPctM).toFixed(2)}%
                            </div>
                        )
                    )}
                </div>

                {/* KPI row 1: Total Collected, Orders, Avg Order */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <KPI label="Total Collected" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(grossSalesM)} delta={showDeltaM && grossSalesPctM != null ? `${grossSalesPctM >= 0 ? '+' : ''}${grossSalesPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={grossSalesPctM == null ? true : grossSalesPctM >= 0} drill={false} tooltip="Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees" isMobile />
                    <KPI label="Orders" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtNumMobile(totalOrdersM)} delta={showDeltaM && orderPctM != null ? `${orderPctM >= 0 ? '+' : ''}${orderPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={orderPctM == null ? true : orderPctM >= 0} drill={false} />
                    <KPI label="Avg Order" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(avgOrderM)} delta={showDeltaM && avgOrderPctM != null ? `${avgOrderPctM >= 0 ? '+' : ''}${avgOrderPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={avgOrderPctM == null ? true : avgOrderPctM >= 0} drill={false} />
                </div>

                {/* KPI row 2: Tax, Discounts, Refunds */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <KPI label="Tax" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(totalTaxM)} delta={showDeltaM && taxPctM != null ? `${taxPctM >= 0 ? '+' : ''}${taxPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={taxPctM == null ? true : taxPctM >= 0} drill={false} />
                    <KPI label="Discounts" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(totalDiscountsM)} delta={showDeltaM && discountsPctM != null ? `${discountsPctM >= 0 ? '+' : ''}${discountsPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={discountsPctM == null ? true : discountsPctM >= 0} drill={true} onClick={() => setDrillView('discounts')} />
                    <KPI label="Refunds" value={liveRefundSummaryLoadingM ? <Bone w={60} h={18} r={4} /> : liveRefundSummaryFailureM ? '--' : fmtCurrencyMobile(liveCancelledAmtM)} delta={showDeltaM && refundsPctM != null ? `${refundsPctM >= 0 ? '+' : ''}${refundsPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={refundsPctM == null ? false : refundsPctM < 0} drill={true} onClick={() => setDrillView('refunds')} />
                </div>

                {/* KPI row 3: Tips & Grat, Other, Outstanding.
                    ZAR-1762 / TG-1 — the unified card replaces the separate Tips and Gratuity cards,
                    which frees the slot Outstanding used to need a row of its own for. The row is now
                    the same three cells under every source filter, so no reflow special-casing. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                    <KPI label={TG_CARD_LABEL} value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(tipsAndGratuityM)} delta={showDeltaM && tipsGratuityPctM != null ? `${tipsGratuityPctM >= 0 ? '+' : ''}${tipsGratuityPctM.toFixed(2)}%` : null} deltaLoading={showDeltaM && deltaLoadingM} up={tipsGratuityPctM == null ? true : tipsGratuityPctM >= 0} drill={true} onClick={() => setDrillView('tips')} />
                    <KPI label="Other" value={kpiLoadingM ? <Bone w={60} h={18} r={4} /> : fmtCurrencyMobile(otherChargesM)} drill={false} />
                    {outstandingKpiM}
                    {spendGuestKpiM}
                </div>

                {/* Sales by Hour — Today: ClickHouse Redux; Historical: getByDuration */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Sales by Hour</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Revenue distribution across service hours</div>
                    <HourlyBarChart
                        data={hourlyDataM}
                        loading={hourlySalesLoadingM}
                        error={hourlySalesFailureM}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                        kpiName="sales by hour"
                        barColor={C.accent}
                        chartHeight={160}
                        formatValue={(v) => fmtCurrencyMobile(v)}
                        showCurrency={true}
                        currencySymbol={getCurrencySymbol()}
                        minHeight={200}
                        avgOverride={hourlyAvgM}
                    />
                </div>

                {/* Sales by Channel — Today: ClickHouse Redux; Historical: by-channel API */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Sales by Channel</div>
                            <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Revenue share across order channels</div>
                        </div>
                        {liveChFilteredM.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Sales by Channel"
                                filename="sales-by-channel"
                                buildExport={async (format, opts) => {
                                    const cc = branch?.country ?? branch?.countryCode ?? branch?.countryCd;
                                    const money = (n: any) => formatMoneyForExport(n);
                                    const count = (n: any) => format === 'pdf' ? formatGroupedNumber(n, cc, { maximumFractionDigits: 0 }) : n;
                                    let chSource: LiveByChannelItem[] = liveChFilteredM;
                                    if (opts?.locationId) {
                                        const res: any = await getLiveByChannelApi({ locationId: opts.locationId, ...chDatesView, orderSource: orderSourceVal, token });
                                        const arr: LiveByChannelItem[] = Array.isArray(res) ? res : (res?.data ?? []);
                                        chSource = arr.filter((item) => item.grandTotal > 0);
                                    }
                                    const sortedCh = [...chSource].sort((a, b) => b.grandTotal - a.grandTotal);
                                    const chTotal = sortedCh.reduce((s, item) => s + item.grandTotal, 0);
                                    const totalOrders = sortedCh.reduce((s, item) => s + (Number(item.orderCount) || 0), 0);
                                    const pcts = computeDisplayPcts(sortedCh.map((item) => item.grandTotal));
                                    return {
                                        totalRow: true,
                                        headers: ['Channel', 'Orders', 'Revenue', '%'],
                                        rows: [
                                            ...sortedCh.map((item: any, i: number) => [item.channel, count(item.orderCount ?? '—'), money(item.grandTotal), chTotal > 0 ? `${pcts[i]}%` : '—']),
                                            ['Total', count(totalOrders), money(chTotal), chTotal > 0 ? '100.00%' : '—'],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    <DoughnutChart
                        data={liveChDoughnutDataM}
                        loading={liveByChannelLoadingM}
                        error={liveByChannelFailureM}
                        onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                        kpiName="sales by channel"
                        centerLabel="Total"
                        centerValue={fmtCurrencyMobile(liveChTotalM)}
                        formatValue={(v) => fmtCurrencyMobile(v)}
                        chartDiameter={150}
                        minHeight={220}
                    />
                </div>

                {/* Sales by Channel — Financial Breakdown (ZAR-1555, replaces Third-Party Marketplace Performance) */}
                <div style={{ marginTop: 10 }}>
                    <SalesByChannelBreakdown
                        data={salesByChannelBreakdown.groups}
                        totals={salesByChannelBreakdown.totals}
                        loading={salesByChannelBreakdown.loading}
                        error={salesByChannelBreakdown.error}
                        onRetry={salesByChannelBreakdown.retry}
                        outletName={(branch as any)?.name}
                        dateRange={{ startDate: dateRange.startDate, endDate: dateRange.endDate, isToday: dateRange.startDate === today, orderSource: orderSourceVal }}
                        dateRangeLabel={exportDateRangeLabel}
                        onChannelDrill={(ch: SbcChannelRow) => {
                            // Filter the Order Log by the row's sales channel (`channelGroup`).
                            // Any Order Type (`channelName`) selection is cleared so the two
                            // filters can never contradict each other.
                            setMolOrderTypes([]);
                            setMolChannelGroup(ch.channelGroup ?? null);
                            requestAnimationFrame(() => document.getElementById('sales-order-log-m')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                        }}
                    />
                </div>

                {/* Sales by Category — Today: ClickHouse Redux; Historical: category sales API */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Sales by Category</div>
                            <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Top menu categories by revenue</div>
                        </div>
                        {liveCategorySalesTotalElementsM > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Sales by Category"
                                filename="sales-by-category"
                                buildExport={async (format, opts) => {
                                    // Fetch the whole set (all pages) at export time, like other sections.
                                    const loc = opts?.locationId ?? locationId;
                                    let size = liveCategorySalesTotalElementsM || 1;
                                    if (opts?.locationId) {
                                        // Current outlet's known total doesn't apply to the other outlet — probe first.
                                        const probe: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size: 1, startDate: chDatesView.startDate, endDate: chDatesView.endDate, orderSource: orderSourceVal, token });
                                        size = (Array.isArray(probe) ? probe.length : (probe?.totalElements ?? probe?.total ?? 1)) || 1;
                                    }
                                    const res: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size, startDate: chDatesView.startDate, endDate: chDatesView.endDate, orderSource: orderSourceVal, token });
                                    const cats: LiveCategorySalesItem[] = Array.isArray(res) ? res : (res?.categories ?? res?.data ?? []);
                                    // Every cell carries its own currency symbol now (formatMoneyForExport),
                                    // so this column is text in every format — no more raw-number CSV branch.
                                    const money = (n: number) => formatMoneyForExport(n);
                                    const totalQty = cats.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
                                    const totalValue = cats.reduce((s, c) => s + (Number(c.price) || 0), 0);
                                    return {
                                        totalRow: true,
                                        headers: ['Serial No.', 'Category Name', 'Total Sales Quantity', 'Total Sales Value'],
                                        rows: [
                                            ...cats.map((item, i) => [i + 1, item.categoryName, fmtExportQty(item.quantity), money(item.price)]),
                                            ['', 'Total', fmtExportQty(totalQty), money(totalValue)],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    {liveCategorySalesLoadingM ? (
                        <div>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={100} h={11} />
                                        <Bone w={48} h={11} />
                                    </div>
                                    <Bone h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : liveCategorySalesFailureM ? (
                        <SectionError kpiName="sales by category" onRetry={() => dispatch(fetchLiveCategorySalesRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal, catPageM + 1, catReqSizeM))} size="small" minHeight={120} />
                    ) : liveCategorySalesM.length === 0 ? (
                        <SectionEmpty kpiName="sales by category" minHeight={120} />
                    ) : (() => {
                        const maxCatPriceM = Math.max(...(catAllItemsM.length ? catAllItemsM : liveCategorySalesM).map(item => item.price), 1);
                        return (
                            <>
                                {liveCategorySalesM.map((item: LiveCategorySalesItem, i: number) => (
                                    <div key={i} style={{ marginBottom: i < liveCategorySalesM.length - 1 ? 9 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: C.t1 }}>{item.categoryName}</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(item.price)}</span>
                                        </div>
                                        <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: 5, borderRadius: 3, width: `${(item.price / maxCatPriceM) * 100}%`, background: '#db7172' }} />
                                        </div>
                                    </div>
                                ))}
                                {/* Running total reflects ALL categories, not just this page. */}
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.t3 }}>
                                    <span>{liveCategorySalesTotalElementsM} categories</span>
                                    <span>Total: <span style={{ color: C.t1, fontWeight: 600 }}>{fmtCurrencyMobile(catGrandTotalM)}</span></span>
                                </div>
                                {/* Records-per-page + pagination */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}`, gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <span style={{ fontSize: 10, color: C.t3 }}>Per page:</span>
                                        <select
                                            className="rpt-pager-select-sm"
                                            value={String(catPageSizeM)}
                                            onChange={e => setCatPageSizeM(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                            style={{ height: 24, padding: '0 4px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 11, color: C.t1, background: C.card, cursor: 'pointer' }}
                                        >
                                            {CAT_PAGE_SIZE_OPTIONS.map(n => <option key={String(n)} value={String(n)}>{n}</option>)}
                                        </select>
                                    </div>
                                    {catPageSizeM !== 'All' && liveCategorySalesTotalPagesM > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <button onClick={() => setCatPageM(p => Math.max(0, p - 1))} disabled={catPageM === 0} style={{ height: 24, padding: '0 8px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: catPageM === 0 ? C.hover : C.card, color: catPageM === 0 ? C.t3 : C.t1, cursor: catPageM === 0 ? 'default' : 'pointer', fontSize: 11 }}>‹</button>
                                            <span style={{ fontSize: 10, color: C.t3 }}>{catPageM + 1} / {liveCategorySalesTotalPagesM}</span>
                                            <button onClick={() => setCatPageM(p => Math.min(liveCategorySalesTotalPagesM - 1, p + 1))} disabled={catPageM >= liveCategorySalesTotalPagesM - 1} style={{ height: 24, padding: '0 8px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: catPageM >= liveCategorySalesTotalPagesM - 1 ? C.hover : C.card, color: catPageM >= liveCategorySalesTotalPagesM - 1 ? C.t3 : C.t1, cursor: catPageM >= liveCategorySalesTotalPagesM - 1 ? 'default' : 'pointer', fontSize: 11 }}>›</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </div>

                {/* Sales by Tender Type */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Sales by Tender Type</div>
                            <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Payment method breakdown</div>
                        </div>
                        {activeTenderDataM.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Sales by Tender Type"
                                filename="sales-by-tender-type"
                                buildExport={async (_format, opts) => {
                                    let tenderRows = activeTenderDataM;
                                    if (opts?.locationId) {
                                        const res: any = await getLivePaymentsApi({ locationId: opts.locationId, ...chDatesView, orderSource: orderSourceVal, token });
                                        const items: LivePaymentsItem[] = Array.isArray(res) ? res : (res?.data ?? []);
                                        const sorted = items.filter(p => p.tenderAmount > 0).sort((a, b) => b.tenderAmount - a.tenderAmount);
                                        const pcts = distribute100At2dp(sorted.map(p => p.tenderAmount));
                                        tenderRows = sorted.map((p, i) => ({ type: p.tenderType, amount: p.tenderAmount, count: p.transactionCount, pct: pcts[i] }));
                                    }
                                    return {
                                        headers: ['Type', 'Transactions', 'Amount', 'Share'],
                                        rows: tenderRows.map((t: any) => [t.type, t.count ?? '—', formatMoneyForExport(t.amount), `${t.pct}%`]),
                                    };
                                }}
                            />
                        )}
                    </div>
                    {activeTenderErrorM ? (
                        <SectionError kpiName="sales by tender type" onRetry={activeTenderRetryM} minHeight={120} />
                    ) : activeTenderLoadingM ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                                    <Bone w={80} h={12} />
                                    <Bone w={48} h={12} />
                                </div>
                            ))}
                        </div>
                    ) : activeTenderDataM.length === 0 ? (
                        <SectionEmpty kpiName="sales by tender type" minHeight={120} />
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                                {(['TYPE', 'TXN', 'AMOUNT', 'SHARE'] as const).map((h, ci) => (
                                    <span key={h} style={{ ...(ci === 0 ? { flex: 1 } : { width: [36, 72, 52][ci - 1], flexShrink: 0 }), fontSize: 8, fontWeight: 600, color: C.t3, textAlign: ci === 0 ? 'left' : 'right', letterSpacing: 0.4 }}>{h}</span>
                                ))}
                            </div>
                            {activeTenderDataM.map((t: any, i: number) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${C.divider}` }}>
                                    <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 600, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6 }}>{t.type}</span>
                                    <span style={{ width: 36, flexShrink: 0, fontSize: 11, color: C.t2, textAlign: 'right' }}>{t.count ?? '—'}</span>
                                    <span style={{ width: 72, flexShrink: 0, fontSize: 11, fontWeight: 600, color: C.t1, textAlign: 'right' }}>{fmtCurrencyMobile(t.amount)}</span>
                                    <span style={{ width: 52, flexShrink: 0, fontSize: 11, color: C.t3, textAlign: 'right' }}>{t.pct}%</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Refund Analysis — parity with desktop main view (live for Today, historical otherwise) */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Refund Analysis</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Top reasons by amount</div>
                    {liveRefundSummaryLoadingM ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={100} h={12} />
                                        <Bone w={28} h={12} />
                                    </div>
                                    <Bone h={4} r={2} />
                                </div>
                            ))}
                        </div>
                    ) : liveRefundSummaryFailureM ? (
                        <SectionError kpiName="refund analysis" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} size="small" minHeight={80} />
                    ) : liveRefundSummaryM.length ? (() => {
                        const maxCancelledAmtM = liveRefundSummaryM.reduce((m, item) => Math.max(m, item.cancelledAmt || 0), 1);
                        const totalRefundCountM = liveRefundSummaryM.reduce((sum, item) => sum + item.orderCount, 0);
                        const totalCancelledAmtM = liveRefundSummaryM.reduce((sum, item) => sum + (item.cancelledAmt || 0), 0);
                        const totalSalesM = Number(snapshotDetails?.grandTotal ?? 0);
                        const refundRateM = totalSalesM > 0 ? (totalCancelledAmtM / totalSalesM) * 100 : null;
                        return (
                            <>
                                {liveRefundSummaryM.map((item: LiveRefundSummaryItem, i: number) => (
                                    <div key={i} style={{ marginBottom: i < liveRefundSummaryM.length - 1 ? 10 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: C.t1 }}>{item.cancelReason}</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(item.cancelledAmt)}</span>
                                        </div>
                                        <div style={{ height: 4, background: C.hover, borderRadius: 2 }}>
                                            <div style={{ height: 4, borderRadius: 2, width: `${Math.min(((item.cancelledAmt || 0) / maxCancelledAmtM) * 100, 100)}%`, background: C.red, opacity: 0.65 }} />
                                        </div>
                                    </div>
                                ))}
                                <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: C.t3 }}>
                                    <span>{fmtNumMobile(totalRefundCountM)} refunds</span>
                                    <span>Refund Rate: <span style={{ color: C.red, fontWeight: 600 }}>{refundRateM != null ? `${refundRateM.toFixed(1)}%` : '--'}</span></span>
                                </div>
                            </>
                        );
                    })() : (
                        <SectionEmpty kpiName="refund analysis" size="small" minHeight={80} />
                    )}
                </div>

                {/* Staff Sales Leaderboard */}
                {staffSalesListM.length > 0 && (() => {
                    const PAGE_SIZE = 5;
                    const totalPages = Math.ceil(staffSalesListM.length / PAGE_SIZE);
                    const safePage = Math.min(netSalesStaffPageM, totalPages - 1);
                    const pageSlice = staffSalesListM.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
                    return (
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Staff Sales Leaderboard</div>
                            {pageSlice.map((s: any, i: number) => {
                                const globalRank = safePage * PAGE_SIZE + i;
                                return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < pageSlice.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                        <div style={{ width: 22, height: 22, borderRadius: 6, background: globalRank === 0 ? C.accentSoft : C.hover, color: globalRank === 0 ? C.accent : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{globalRank + 1}</div>
                                        <span style={{ flex: 1, fontSize: 13, color: C.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{fmtCurrencyMobile(s.sales)}</div>
                                            <div style={{ fontSize: 10, color: C.t3 }}>{fmtNumMobile(s.orders)} orders</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                    <button onClick={() => setNetSalesStaffPageM((p) => Math.max(0, p - 1))} disabled={safePage === 0} style={{ fontSize: 11, color: safePage === 0 ? C.t3 : C.accent, background: 'none', border: `1px solid ${safePage === 0 ? C.divider : C.accent}`, borderRadius: 4, cursor: safePage === 0 ? 'default' : 'pointer', padding: '2px 8px' }}>← Prev</button>
                                    <span style={{ fontSize: 10, color: C.t3 }}>{safePage + 1} / {totalPages}</span>
                                    <button onClick={() => setNetSalesStaffPageM((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1} style={{ fontSize: 11, color: safePage === totalPages - 1 ? C.t3 : C.accent, background: 'none', border: `1px solid ${safePage === totalPages - 1 ? C.divider : C.accent}`, borderRadius: 4, cursor: safePage === totalPages - 1 ? 'default' : 'pointer', padding: '2px 8px' }}>Next →</button>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* ─── Order Log (mobile — SALES-7/8) ─── */}
                <div id="sales-order-log-m" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginTop: 10 }}>
                    {/* Section header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Order Log (Paid Orders)</div>
                            {activeMolTotal > 0 && !activeMolLoading && (
                                <div style={{ fontSize: 10, color: C.t3 }}>{activeMolTotal} orders</div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {activeMolOrders.length > 0 && (
                                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                    variant="mobile"
                                    title="Order Log (Paid Orders)"
                                    filename={`order-log-paid-orders-${dateRange.startDate}`}
                                    buildExport={async (format, opts) => {
                                        const loc = opts?.locationId ?? locationId;
                                        const sp = molSortParams(molSort);
                                        const dateParams = dateRange.startDate !== today ? { startDate: dateRange.startDate, endDate: dateRange.endDate } : {};
                                        let size = orderLogTotalElementsM || 1;
                                        if (opts?.locationId) {
                                            const probe: any = await getOrderLogExportApi({ locationId: loc, page: 1, size: 1, sortBy: sp.sortBy, sortDir: sp.sortAsc ? 'asc' : 'desc', search: molDebouncedSearch || undefined, channelName: molOrderTypes.length ? molOrderTypes.join(',') : undefined, channelGroup: molChannelGroup ?? undefined, guestCount: molMinGuests ? Number(molMinGuests) : undefined, minAmount: molMinAmt ? Number(molMinAmt) : undefined, maxAmount: molMaxAmt ? Number(molMaxAmt) : undefined, ...dateParams, ...orderSourceParam });
                                            size = Number(probe?.totalElements) || 1;
                                        }
                                        const res: any = await getOrderLogExportApi({ locationId: loc, page: 1, size, sortBy: sp.sortBy, sortDir: sp.sortAsc ? 'asc' : 'desc', search: molDebouncedSearch || undefined, channelName: molOrderTypes.length ? molOrderTypes.join(',') : undefined, channelGroup: molChannelGroup ?? undefined, guestCount: molMinGuests ? Number(molMinGuests) : undefined, minAmount: molMinAmt ? Number(molMinAmt) : undefined, maxAmount: molMaxAmt ? Number(molMaxAmt) : undefined, ...dateParams, ...orderSourceParam });
                                        const orders = res?.orders ?? (Array.isArray(res) ? res : []);
                                        return orderLogToExportTable(orders, getCurrencySymbol(), format);
                                    }}
                                />
                            )}
                            <button
                                onClick={() => setMolFiltersOpen(true)}
                                style={{ height: 28, padding: '0 10px', border: `1px solid ${molActiveFilterCount > 0 ? C.accent : C.cardBorder}`, borderRadius: 6, fontSize: 11, color: molActiveFilterCount > 0 ? C.accent : C.t2, background: molActiveFilterCount > 0 ? `${C.accent}10` : C.card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: 14 }}>filter_list</span>
                                {molActiveFilterCount > 0 ? `Filters (${molActiveFilterCount})` : 'Filters'}
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div style={{ position: 'relative', width: '100%', marginBottom: 8 }}>
                        <input
                            value={molSearch}
                            onChange={e => setMolSearch(e.target.value)}
                            placeholder="Search order ID, customer, phone…"
                            className="order-log-search"
                            style={{ width: '100%', height: 32, padding: molSearch ? '0 28px 0 10px' : '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 12, color: C.t1, background: C.surface, outline: 'none', boxSizing: 'border-box' }}
                        />
                        {molSearch && (
                            <button
                                onClick={() => setMolSearch('')}
                                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: C.t3 }}
                                aria-label="Clear search"
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                            </button>
                        )}
                    </div>

                    {/* Active sales-channel chip (set by the Sales by Channel drill-down) */}
                    {molChannelGroup && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${C.accent}12`, borderRadius: 12, fontSize: 11, color: C.accent, fontWeight: 500 }}>
                                Channel: {channelGroupLabel(molChannelGroup)}
                                <span onClick={() => setMolChannelGroup(null)} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                            </span>
                        </div>
                    )}

                    {/* Cards */}
                    {activeMolLoading ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.divider}` }}>
                                <Bone w={null} h={12} mb={6} /><Bone w={'60%' as any} h={10} mb={4} /><Bone w={'40%' as any} h={10} />
                            </div>
                        ))
                    ) : activeMolError ? (
                        <SectionError kpiName="Order Log" onRetry={() => { const sp = molSortParams(molSort); dispatch(fetchOrderLogRequest({ locationId, page: molPage, size: OL_PAGE_SIZE_M, sortBy: sp.sortBy, sortDir: sp.sortAsc ? 'asc' : 'desc', search: molDebouncedSearch || undefined, ...orderSourceParam })); }} minHeight={120} />
                    ) : activeMolOrders.length === 0 ? (
                        <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 12, color: C.t3 }}>
                            {molHasFilters ? 'No orders found for the selected filters.' : 'No orders found for the selected period.'}
                        </div>
                    ) : (
                        <>
                            {activeMolOrders.map((order: OrderLogItem, i: number) => {
                                const orderId = order.orderNo ?? '—';
                                const orderDate = order.orderDate ?? '—';
                                const orderTime = fmtOrderTimeM(order.orderTime, activeDateFilter === 'Today');
                                const orderType = order.orderType ?? '—';
                                const customerName = order.customerName || null;
                                const phone = fmtPhoneM(order.customerPhone ?? null);
                                const isOffPremise = /delivery|doordash|uber|pickup|catering/i.test(String(orderType));
                                const guestsDisplay = order.guests ? String(order.guests) : '—';
                                const amount = Number(order.amount ?? 0);

                                return (
                                    <div
                                        key={`${orderId}-${i}`}
                                        onClick={() => setMolSelectedOrder(order)}
                                        style={{ padding: '10px 0', borderBottom: i < activeMolOrders.length - 1 ? `1px solid ${C.divider}` : 'none', cursor: 'pointer' }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>#{orderId}</span>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{fmtCurrencyCellMobile(amount)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                            <span style={{ fontSize: 11, color: C.t2 }}>{customerName ?? 'Walk-in'} · {phone}</span>
                                            <span style={{ fontSize: 11, color: C.t3 }}>{orderDate} · {orderTime}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 1 }}>
                                            <span style={{ fontSize: 11, color: C.t3 }}>{orderType}</span>
                                        </div>
                                        {order.itemPreview && (() => {
                                            const itemText = order.itemPreview;
                                            const ITEM_LIMIT = 35;
                                            const isTruncatable = itemText.length > ITEM_LIMIT;
                                            const isExpanded = expandedMolItems.has(i);
                                            return (
                                                <div
                                                    style={{ fontSize: 10, color: C.t3, marginTop: 1, wordBreak: 'break-all', overflowWrap: 'break-word', cursor: isTruncatable ? 'pointer' : 'default' }}
                                                    onClick={isTruncatable ? (e) => { e.stopPropagation(); setExpandedMolItems(prev => { const s = new Set(prev); s.has(i) ? s.delete(i) : s.add(i); return s; }); } : undefined}
                                                >
                                                    {isExpanded || !isTruncatable ? itemText : itemText.slice(0, ITEM_LIMIT) + '…'}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}

                            {/* Mobile Pagination — always shown when there are orders */}
                            {activeMolOrders.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, marginTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                    <button onClick={() => setMolPage(p => Math.max(0, p - 1))} disabled={molPage === 0} style={{ height: 28, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 12, color: molPage === 0 ? C.t3 : C.t1, background: molPage === 0 ? C.hover : C.surface, cursor: molPage === 0 ? 'default' : 'pointer' }}>‹</button>
                                    <span style={{ fontSize: 11, color: C.t3 }}>{molPage + 1} / {molTotalPages || 1}</span>
                                    <button onClick={() => setMolPage(p => Math.min((molTotalPages || 1) - 1, p + 1))} disabled={molPage >= (molTotalPages || 1) - 1} style={{ height: 28, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 12, color: molPage >= (molTotalPages || 1) - 1 ? C.t3 : C.t1, background: molPage >= (molTotalPages || 1) - 1 ? C.hover : C.surface, cursor: molPage >= (molTotalPages || 1) - 1 ? 'default' : 'pointer' }}>›</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ─── Mobile Filters Panel ─── */}
            {molFiltersOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: C.surface, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${C.divider}` }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: C.t1 }}>Filters</span>
                        <button onClick={() => setMolFiltersOpen(false)} style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: C.hover, color: C.t2, cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                        <>
                            {/* Order Type — from API */}
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Order Type</div>
                                {(channelNamesM.length > 0 ? channelNamesM : []).map(ot => (
                                    <div key={ot} onClick={() => setMolOrderTypes(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.divider}`, cursor: 'pointer' }}>
                                        <span style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${molOrderTypes.includes(ot) ? C.accent : C.t3}`, background: molOrderTypes.includes(ot) ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {molOrderTypes.includes(ot) && <span style={{ color: '#fff', fontSize: 10 }}>✓</span>}
                                        </span>
                                        <span style={{ fontSize: 13, color: C.t1 }}>{ot}</span>
                                    </div>
                                ))}
                            </div>
                            {/* Amount — no negatives, min < max */}
                            <div style={{ marginBottom: molAmtError ? 4 : 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Amount Range</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input
                                        value={molMinAmt}
                                        onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setMolMinAmt(v); }}
                                        placeholder="Min $"
                                        type="number"
                                        min="0"
                                        style={{ flex: 1, height: 36, padding: '0 10px', border: `1px solid ${molAmtError ? C.red : C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface }}
                                    />
                                    <input
                                        value={molMaxAmt}
                                        onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setMolMaxAmt(v); }}
                                        placeholder="Max $"
                                        type="number"
                                        min="0"
                                        style={{ flex: 1, height: 36, padding: '0 10px', border: `1px solid ${molAmtError ? C.red : C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface }}
                                    />
                                </div>
                            </div>
                            {molAmtError && (
                                <div style={{ fontSize: 11, color: C.red, marginBottom: 12 }}>{molAmtError}</div>
                            )}
                            {/* Min Guests — no negatives */}
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Guests ≥</div>
                                <input
                                    value={molMinGuests}
                                    onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 1) setMolMinGuests(v); }}
                                    placeholder="Min party size"
                                    type="number"
                                    min="1"
                                    style={{ width: '100%', height: 36, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 13, color: C.t1, background: C.surface, boxSizing: 'border-box' }}
                                />
                            </div>
                            {molHasFilters && (
                                <button onClick={() => { setMolOrderTypes([]); setMolChannelGroup(null); setMolMinAmt(''); setMolMaxAmt(''); setMolMinGuests(''); }} style={{ width: '100%', height: 36, border: 'none', borderRadius: 6, fontSize: 13, color: C.accent, background: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Clear all filters
                                </button>
                            )}
                        </>
                    </div>
                    <div style={{ padding: 16, borderTop: `1px solid ${C.divider}` }}>
                        <button onClick={() => setMolFiltersOpen(false)} style={{ width: '100%', height: 44, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: C.accent, cursor: 'pointer' }}>Apply Filters</button>
                    </div>
                </div>
            )}

            {/* ─── Mobile Order Detail Slider (SALES-9) ─── */}
            <OrderDetailSlider
                visible={!!molSelectedOrder}
                onClose={() => { setMolSelectedOrder(null); setMolOrderDetail(null); dispatch(clearOrderDetail()); }}
                orderNo={(molSelectedOrder as OrderLogItem)?.orderNo}
                orderType={(molSelectedOrder as OrderLogItem)?.orderType}
                loading={orderDetailLoadingM}
                error={orderDetailErrorM}
                onRetry={() => dispatch(fetchOrderDetailRequest({ locationId, orderId: (molSelectedOrder as OrderLogItem).orderId, ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }))}
                detail={orderDetailDataM}
                legacyDetail={undefined}
            />
            <BottomNav />
        </Phone>
    );
};

export default function SalesScreen() {
    const isDesktop = useIsDesktop();
    return isDesktop ? <DesktopLayout><DesktopSalesScreen /></DesktopLayout> : <MobileSalesScreen />;
}
